import React, { useEffect, useState, useRef, useCallback, useMemo, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { 
  Menu, X, ChevronRight, ChevronDown, ChevronUp, Phone, Mail, MapPin, 
  Clock, Award, Globe, Users, Wrench, Zap, Settings, Cog, 
  Sun, Shield, Check, ArrowRight, Linkedin, Facebook, MessageCircle,
  ArrowLeft, Home, Target, Star, Gauge, Search
} from "lucide-react";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { HelmetProvider } from "react-helmet-async";
import { IndustryTemplate, ALL_INDUSTRIES } from "@/IndustryPage";
import ProjectPage from "@/ProjectPage";
import ProductsPage from "@/ProductsPage";
import ProductDepartmentPage from "@/ProductDepartmentPage";
import SearchOverlay from "@/SearchOverlay";
import SEO from "@/SEO";

// Optimized Image component with lazy loading and CLS prevention
const OptimizedImage = ({ src, alt, className, style, width, height, priority = false, sizes, ...props }) => {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    if (imgRef.current?.complete) setLoaded(true);
  }, []);

  // Generate responsive srcSet for Unsplash images
  const isUnsplash = src && src.includes('images.unsplash.com');
  const srcSet = useMemo(() => {
    if (!isUnsplash) return undefined;
    const base = src.split('&w=')[0].split('&q=')[0];
    return [640, 960, 1280, 1920].map(w => `${base}&q=75&w=${w} ${w}w`).join(', ');
  }, [src, isUnsplash]);

  const optimizedSrc = useMemo(() => {
    if (isUnsplash && !src.includes('&w=')) return `${src.split('&q=')[0]}&q=80&w=1920`;
    return src;
  }, [src, isUnsplash]);

  return (
    <img
      ref={imgRef}
      src={optimizedSrc}
      srcSet={srcSet}
      sizes={sizes || (srcSet ? '100vw' : undefined)}
      alt={alt || ""}
      className={`${className || ""} transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"}`}
      style={style}
      width={width}
      height={height}
      loading={priority ? "eager" : "lazy"}
      decoding={priority ? "sync" : "async"}
      fetchPriority={priority ? "high" : undefined}
      onLoad={() => setLoaded(true)}
      {...props}
    />
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_industrial-mastery/artifacts/9580l49d_Macsteel%20express%20Facebook%20Posts%20November%202025%20Start%20%281%29.png";

// Image URLs from design guidelines
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1758750518277-1a8a182033e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxtaW5pbmclMjBlcXVpcG1lbnQlMjBoZWF2eSUyMG1hY2hpbmVyeSUyMGluZHVzdHJpYWx8ZW58MHx8fHwxNzc0MTg5NzMyfDA&ixlib=rb-4.1.0&q=85",
  about: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/iuci02bw_Welkom%20Winder%20Service.webp",
  mining: "/images/mining-winding-sector.jpg",
  industrial: "https://images.unsplash.com/photo-1767739791246-9f832345f8f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxoZWF2eSUyMG1hY2hpbmVyeSUyMG1lY2hhbmljYWx8ZW58MHx8fHwxNzc0MTg1NTk1fDA&ixlib=rb-4.1.0&q=85",
  agricultural: "/images/agricultural-sector.jpg",
  residential: "https://images.pexels.com/photos/13558357/pexels-photo-13558357.jpeg",
  team: "https://images.pexels.com/photos/7108269/pexels-photo-7108269.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  electrical: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/s6wkmi1u_Electrical%20Solutions1.webp",
  renewable: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/594oy2kc_Renewable%20Energy%20Solutions1.jpg",
  mechanical: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/y91p3un9_Mechanical%20SOlutions%20%281%29.jpg"
};

// =============================================
// PHASE X — PREMIUM DESIGN SYSTEM COMPONENTS
// Reusable across the entire WWS design language
// =============================================

// Premium Section Header — Architectural overline, headline, editorial description
const PremiumSectionHeader = ({ overline, title, titleAccent, description, align = 'center' }) => (
  <div className={`${align === 'center' ? 'text-center' : ''} mb-16 lg:mb-20 group/header cursor-default`}>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="mb-8"
    >
      <span className="inline-flex items-center gap-5">
        <motion.span
          className="h-[2px] bg-gradient-to-r from-transparent to-[#8B2332] transition-all duration-[350ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/header:to-[#a63d50]"
          initial={{ width: 0 }}
          whileInView={{ width: 45 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          style={{ transition: 'width 350ms cubic-bezier(0.22,0.61,0.36,1)' }}
        />
        <span className="text-[#8B2332] text-[13px] lg:text-[14px] font-bold uppercase tracking-[0.35em] transition-all duration-[300ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/header:text-[#a63d50] group-hover/header:tracking-[0.38em]">{overline}</span>
        <motion.span
          className="h-[2px] bg-gradient-to-l from-transparent to-[#8B2332] transition-all duration-[350ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/header:to-[#a63d50]"
          initial={{ width: 0 }}
          whileInView={{ width: 45 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          style={{ transition: 'width 350ms cubic-bezier(0.22,0.61,0.36,1)' }}
        />
      </span>
    </motion.div>
    <motion.h2
      className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold uppercase text-white mb-6 transition-all duration-[350ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/header:text-white"
      style={{ letterSpacing: '-0.01em', lineHeight: '0.95' }}
      initial={{ opacity: 0, y: 25 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
    >
      {title}{titleAccent && <>{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B2332] to-[#c44b5e] transition-all duration-[350ms] group-hover/header:from-[#a63d50] group-hover/header:to-[#d46b7c]">{titleAccent}</span></>}
    </motion.h2>
    {description && (
      <motion.p
        className="text-white/55 text-base lg:text-lg max-w-2xl mx-auto leading-[1.9]"
        style={{ letterSpacing: '0.01em' }}
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3, duration: 0.6 }}
      >
        {description}
      </motion.p>
    )}
  </div>
);

// Glass Editorial Panel — Premium content container with internal edge lighting
const GlassEditorialPanel = ({ children, className = '', accent = true }) => (
  <div className={`relative overflow-hidden ${className}`}>
    <div className="absolute inset-0" style={{
      background: 'linear-gradient(160deg, rgba(18,19,24,0.92) 0%, rgba(12,13,17,0.96) 100%)',
      border: '1px solid rgba(255,255,255,0.07)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05), inset 0 0 0 1px rgba(255,255,255,0.025), 0 20px 60px rgba(0,0,0,0.35), 0 4px 16px rgba(0,0,0,0.2)',
    }} />
    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
    <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.03] to-transparent" />
    {accent && (
      <>
        <div className="absolute top-6 bottom-6 left-0 w-[2px] bg-gradient-to-b from-[#8B2332] via-[#8B2332]/40 to-transparent" />
        <div className="absolute top-0 left-0 w-10 h-full bg-gradient-to-r from-[#8B2332]/[0.04] to-transparent pointer-events-none" />
      </>
    )}
    <div className="relative z-10 p-8 lg:p-10">{children}</div>
  </div>
);

// 9-Layer Industry Showcase Card with mouse-tracking parallax
const IndustryShowcaseCard = ({ industry, index, onClick }) => {
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springCfg = { stiffness: 150, damping: 20 };
  const imgX = useSpring(useTransform(mouseX, [-0.5, 0.5], [12, -12]), springCfg);
  const imgY = useSpring(useTransform(mouseY, [-0.5, 0.5], [10, -10]), springCfg);

  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  }, [mouseX, mouseY]);

  const handleMouseLeave = useCallback(() => {
    mouseX.set(0);
    mouseY.set(0);
    setIsHovered(false);
  }, [mouseX, mouseY]);

  return (
    <motion.div
      ref={cardRef}
      className="group relative cursor-pointer"
      initial={{ opacity: 0, y: 80 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 1, delay: index * 0.15, ease: [0.16, 1, 0.3, 1] }}
      onClick={onClick}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      data-testid={`industry-${industry.slug}`}
    >
      <div
        className="relative overflow-hidden"
        style={{
          aspectRatio: '3/4',
          boxShadow: isHovered
            ? '0 40px 80px rgba(0,0,0,0.6), 0 15px 40px rgba(0,0,0,0.4), 0 0 60px rgba(139,35,50,0.08)'
            : '0 10px 40px rgba(0,0,0,0.5), 0 4px 20px rgba(0,0,0,0.3)',
          transition: 'box-shadow 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Layer 1-2: Surface + Shadow Foundation */}
        <div className="absolute inset-0 bg-[#0C0D10]" />

        {/* Layer 3: Inner Glass Highlight — top edge luminance */}
        <div className="absolute inset-0 pointer-events-none z-[2]" style={{
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), inset 0 0 0 1px rgba(255,255,255,0.03)',
        }} />

        {/* Layer 4: Parallax Image with cinematic color grading */}
        <motion.div className="absolute -inset-3 z-[1]" style={{ x: imgX, y: imgY }}>
          <img
            src={industry.image}
            alt={`${industry.title} sector`}
            className="w-full h-full object-cover"
            loading="lazy"
            style={{
              filter: isHovered ? 'contrast(1.15) saturate(0.8) brightness(0.88)' : 'contrast(1.1) saturate(0.65) brightness(0.8)',
              transform: isHovered ? 'scale(1.08)' : 'scale(1.15)',
              transition: 'filter 1.4s cubic-bezier(0.16, 1, 0.3, 1), transform 1.8s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          />
        </motion.div>

        {/* Layer 5: Multi-stop gradient overlay + cinematic vignette */}
        <div className="absolute inset-0 z-[3]" style={{
          background: 'linear-gradient(to top, rgba(10,11,13,0.98) 0%, rgba(10,11,13,0.88) 22%, rgba(10,11,13,0.45) 50%, rgba(10,11,13,0.15) 75%, rgba(10,11,13,0.3) 100%)',
        }} />
        <div className="absolute inset-0 z-[3]" style={{
          background: 'radial-gradient(ellipse 120% 90% at 50% 50%, transparent 25%, rgba(10,11,13,0.5) 100%)',
        }} />

        {/* Layer 6: Ambient maroon atmosphere (hover-activated) */}
        <div className="absolute inset-0 z-[4]" style={{
          background: 'linear-gradient(to top, rgba(139,35,50,0.3) 0%, rgba(139,35,50,0.06) 30%, transparent 60%)',
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
        }} />

        {/* Layer 7: Content — editorial text, CTA, index */}
        <div className="absolute inset-0 flex flex-col justify-between p-6 lg:p-8 z-[5]">
          <span className="font-['Barlow_Condensed'] text-[4.5rem] lg:text-[5.5rem] font-black leading-none select-none" style={{
            color: isHovered ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.045)',
            transition: 'color 0.7s ease',
          }}>
            0{index + 1}
          </span>

          <div>
            <div className="overflow-hidden mb-3">
              <span className="inline-block text-[10px] font-bold uppercase" style={{
                color: isHovered ? '#d45567' : '#8B2332',
                letterSpacing: '0.35em',
                transform: isHovered ? 'translateY(0)' : 'translateY(110%)',
                opacity: isHovered ? 1 : 0,
                transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.5s ease, color 0.5s ease',
              }}>
                {industry.subtitle}
              </span>
            </div>

            <h3 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl lg:text-[1.75rem] xl:text-[2rem] font-bold uppercase text-white mb-3" style={{
              textShadow: '0 4px 20px rgba(0,0,0,0.7)',
              letterSpacing: '0.02em',
              lineHeight: '1.1',
              transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
              transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
            }}>
              {industry.title}
            </h3>

            <div className="relative h-[2px] mb-4 overflow-hidden">
              <div style={{
                width: isHovered ? '75%' : '40px',
                height: '100%',
                background: isHovered ? 'linear-gradient(to right, #8B2332, rgba(139,35,50,0.3), transparent)' : 'rgba(139,35,50,0.5)',
                transition: 'width 0.8s cubic-bezier(0.16, 1, 0.3, 1), background 0.5s ease',
              }} />
            </div>

            <p style={{
              color: isHovered ? 'rgba(255,255,255,0.72)' : 'rgba(255,255,255,0.38)',
              fontSize: '13px', lineHeight: '1.75', letterSpacing: '0.015em',
              transition: 'color 0.5s ease',
              maxWidth: '250px', marginBottom: '1.25rem',
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {industry.cardDesc || industry.heroStatement}
            </p>

            <div className="flex items-center gap-3" style={{
              color: isHovered ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.25)',
              transition: 'color 0.5s ease',
            }}>
              <span className="text-[10px] font-bold uppercase" style={{ letterSpacing: '0.25em' }}>Explore Solutions</span>
              <div className="flex items-center">
                <div style={{ width: isHovered ? '40px' : '20px', height: '1px', background: 'currentColor', transition: 'width 0.5s cubic-bezier(0.16, 1, 0.3, 1)' }} />
                <ArrowRight size={11} strokeWidth={2.5} style={{ transform: isHovered ? 'translateX(4px)' : 'translateX(0)', transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Layer 8: Engineered Frame — borders, accent lines, corner brackets */}
        <div className="absolute inset-0 z-[6] pointer-events-none" style={{
          border: isHovered ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(255,255,255,0.06)',
          transition: 'border-color 0.7s ease',
        }} />
        <div className="absolute top-0 left-0 right-0 h-[2px] z-[6] overflow-hidden pointer-events-none">
          <div style={{ height: '100%', width: isHovered ? '100%' : '15%', background: isHovered ? 'linear-gradient(to right, #8B2332, rgba(212,85,103,0.5), transparent)' : 'rgba(139,35,50,0.4)', transition: 'width 0.8s cubic-bezier(0.16, 1, 0.3, 1), background 0.5s ease' }} />
        </div>
        <div className="absolute top-0 left-0 w-[2px] z-[6] overflow-hidden pointer-events-none">
          <div style={{ width: '100%', height: isHovered ? '80px' : '0px', background: 'linear-gradient(to bottom, #8B2332, transparent)', transition: 'height 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.1s' }} />
        </div>
        <div className="absolute top-4 right-4 z-[6] pointer-events-none" style={{
          width: isHovered ? '48px' : '32px', height: isHovered ? '48px' : '32px',
          borderTop: isHovered ? '1.5px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.07)',
          borderRight: isHovered ? '1.5px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.07)',
          transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
        }} />
        <div className="absolute bottom-4 left-4 z-[6] pointer-events-none" style={{
          width: isHovered ? '40px' : '24px', height: isHovered ? '40px' : '24px',
          borderBottom: isHovered ? '1.5px solid rgba(139,35,50,0.35)' : '1px solid rgba(255,255,255,0.06)',
          borderLeft: isHovered ? '1.5px solid rgba(139,35,50,0.35)' : '1px solid rgba(255,255,255,0.06)',
          transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
        }} />

        {/* Layer 9: Interactive layer (pointer events handled by parent) */}
      </div>
    </motion.div>
  );
};

// =============================================
// ABOUT PAGE — EXECUTIVE DESIGN SYSTEM COMPONENTS
// Reusable across the entire WWS design language
// =============================================

// Engineered Image Frame — Premium floating image presentation with parallax, scanning light, corner brackets
const EngineeredImageFrame = ({ src, alt, aspectRatio = '4/3', className = '' }) => {
  const frameRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springCfg = { stiffness: 120, damping: 18 };
  const imgX = useSpring(useTransform(mouseX, [-0.5, 0.5], [10, -10]), springCfg);
  const imgY = useSpring(useTransform(mouseY, [-0.5, 0.5], [8, -8]), springCfg);

  const handleMouseMove = useCallback((e) => {
    if (!frameRef.current) return;
    const rect = frameRef.current.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  }, [mouseX, mouseY]);

  const handleMouseLeave = useCallback(() => {
    mouseX.set(0);
    mouseY.set(0);
    setIsHovered(false);
  }, [mouseX, mouseY]);

  return (
    <motion.div
      ref={frameRef}
      className={`group relative ${className}`}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      animate={{ y: [0, -3, 0] }}
      transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      data-testid="engineered-image-frame"
    >
      {/* Multi-layer glass backing panel */}
      <div className="absolute -inset-3 z-0" style={{
        background: 'linear-gradient(145deg, rgba(18,19,24,0.85) 0%, rgba(12,13,17,0.92) 100%)',
        border: '1px solid rgba(255,255,255,0.05)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 30px 80px rgba(0,0,0,0.5), 0 8px 30px rgba(0,0,0,0.3)',
      }} />
      {/* Metallic top/bottom trim on backing */}
      <div className="absolute -inset-3 z-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.04] to-transparent" />
      </div>

      {/* Image container */}
      <div className="relative overflow-hidden z-10" style={{ aspectRatio }}>
        <div className="absolute inset-0 bg-[#0C0D10]" />

        {/* Parallax image with cinematic color grading */}
        <motion.div className="absolute -inset-4" style={{ x: imgX, y: imgY }}>
          <img
            src={src}
            alt={alt}
            className="w-full h-full object-cover"
            loading="lazy"
            style={{
              filter: isHovered ? 'contrast(1.12) saturate(0.85) brightness(0.92)' : 'contrast(1.08) saturate(0.7) brightness(0.85)',
              transform: isHovered ? 'scale(1.05)' : 'scale(1.12)',
              transition: 'filter 1.4s cubic-bezier(0.16, 1, 0.3, 1), transform 1.8s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          />
        </motion.div>

        {/* Cinematic vignette */}
        <div className="absolute inset-0 z-[1] pointer-events-none" style={{
          background: 'radial-gradient(ellipse 110% 90% at 50% 50%, transparent 30%, rgba(10,11,13,0.45) 100%)',
        }} />
        <div className="absolute inset-0 z-[1] pointer-events-none" style={{
          background: 'linear-gradient(to top, rgba(10,11,13,0.35) 0%, transparent 25%)',
        }} />

        {/* Inner glass highlight */}
        <div className="absolute inset-0 z-[2] pointer-events-none" style={{
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.07), inset 0 0 0 1px rgba(255,255,255,0.04)',
        }} />

        {/* Scanning light — sweeps every ~18s */}
        <div className="absolute inset-0 z-[2] pointer-events-none overflow-hidden">
          <div className="scan-light-sweep" />
        </div>

        {/* Border frame */}
        <div className="absolute inset-0 z-[3] pointer-events-none" style={{
          border: isHovered ? '1px solid rgba(255,255,255,0.14)' : '1px solid rgba(255,255,255,0.06)',
          transition: 'border-color 0.7s ease',
        }} />

        {/* Top accent line */}
        <div className="absolute top-0 left-0 right-0 h-[2px] z-[3] overflow-hidden pointer-events-none">
          <div style={{
            height: '100%',
            width: isHovered ? '100%' : '20%',
            background: isHovered ? 'linear-gradient(to right, #8B2332, rgba(212,85,103,0.4), transparent)' : 'rgba(139,35,50,0.4)',
            transition: 'width 0.8s cubic-bezier(0.16, 1, 0.3, 1), background 0.5s ease',
          }} />
        </div>

        {/* 4 Engineering corner brackets */}
        <div className="absolute top-3 right-3 z-[3] pointer-events-none" style={{
          width: isHovered ? '48px' : '28px', height: isHovered ? '48px' : '28px',
          borderTop: isHovered ? '1.5px solid rgba(255,255,255,0.22)' : '1px solid rgba(255,255,255,0.08)',
          borderRight: isHovered ? '1.5px solid rgba(255,255,255,0.22)' : '1px solid rgba(255,255,255,0.08)',
          transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
        }} />
        <div className="absolute top-3 left-3 z-[3] pointer-events-none" style={{
          width: isHovered ? '28px' : '16px', height: isHovered ? '28px' : '16px',
          borderTop: isHovered ? '1px solid rgba(139,35,50,0.3)' : '1px solid rgba(255,255,255,0.04)',
          borderLeft: isHovered ? '1px solid rgba(139,35,50,0.3)' : '1px solid rgba(255,255,255,0.04)',
          transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
        }} />
        <div className="absolute bottom-3 left-3 z-[3] pointer-events-none" style={{
          width: isHovered ? '48px' : '28px', height: isHovered ? '48px' : '28px',
          borderBottom: isHovered ? '1.5px solid rgba(139,35,50,0.35)' : '1px solid rgba(255,255,255,0.06)',
          borderLeft: isHovered ? '1.5px solid rgba(139,35,50,0.35)' : '1px solid rgba(255,255,255,0.06)',
          transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
        }} />
        <div className="absolute bottom-3 right-3 z-[3] pointer-events-none" style={{
          width: isHovered ? '28px' : '16px', height: isHovered ? '28px' : '16px',
          borderBottom: isHovered ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.04)',
          borderRight: isHovered ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.04)',
          transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
        }} />

        {/* Ambient hover glow */}
        <div className="absolute inset-0 z-[1]" style={{
          background: 'linear-gradient(to top, rgba(139,35,50,0.15) 0%, transparent 35%)',
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.7s ease',
        }} />
      </div>

      {/* External ambient shadow */}
      <div className="absolute -inset-8 -z-10 pointer-events-none" style={{
        background: isHovered ? 'radial-gradient(ellipse 80% 60% at 50% 70%, rgba(139,35,50,0.06) 0%, transparent 70%)' : 'none',
        transition: 'background 0.7s ease',
      }} />
    </motion.div>
  );
};

// Executive KPI Card — Premium animated statistic display
const ExecutiveKPICard = ({ value, suffix = '', label, index, icon: Icon }) => {
  const { count, startCounting } = useCountUp(typeof value === 'number' ? value : 0, 2500);
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !isVisible) { setIsVisible(true); startCounting(); } },
      { threshold: 0.5 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [startCounting, isVisible]);

  const displayValue = typeof value === 'number' ? count : value;

  return (
    <motion.div
      ref={cardRef}
      className="group relative overflow-hidden"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: index * 0.15, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -6, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } }}
      data-testid={`kpi-card-${index}`}
    >
      {/* Glass surface */}
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(150deg, rgba(18,19,24,0.95) 0%, rgba(10,11,13,0.98) 100%)',
        border: '1px solid rgba(255,255,255,0.06)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), inset 0 0 0 1px rgba(255,255,255,0.02), 0 12px 40px rgba(0,0,0,0.35)',
        transition: 'border-color 0.6s ease, box-shadow 0.6s ease',
      }} />
      {/* Top edge light */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
      {/* Moving accent line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden">
        <div className="h-full w-1/4 group-hover:w-full bg-gradient-to-r from-[#8B2332] via-[#d45567]/40 to-transparent transition-all duration-700 ease-out" />
      </div>
      {/* Corner brackets */}
      <div className="absolute top-2.5 right-2.5 w-6 h-6 border-t border-r border-white/[0.06] group-hover:border-[#8B2332]/30 group-hover:w-9 group-hover:h-9 transition-all duration-500 pointer-events-none" />
      <div className="absolute bottom-2.5 left-2.5 w-5 h-5 border-b border-l border-white/[0.04] group-hover:border-[#8B2332]/20 group-hover:w-7 group-hover:h-7 transition-all duration-500 pointer-events-none" />
      {/* Ambient glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#8B2332]/[0.04] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
      {/* Scanning reflection */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 scan-light-sweep-fast" />
      </div>

      {/* Content */}
      <div className="relative z-10 p-7 lg:p-8">
        {Icon && (
          <div className="relative w-10 h-10 mb-5 flex items-center justify-center">
            <div className="absolute inset-0 bg-[#8B2332]/[0.08] border border-[#8B2332]/15 group-hover:border-[#8B2332]/30 transition-all duration-500" />
            <Icon className="w-[18px] h-[18px] text-[#8B2332] group-hover:text-[#d45567] transition-colors duration-500 relative z-10" strokeWidth={1.5} />
          </div>
        )}
        <span className="font-['Barlow_Condensed'] text-5xl lg:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#8B2332] to-[#c44b5e] block mb-2">
          {displayValue}{suffix}
        </span>
        <p className="text-white/55 text-[13px] font-bold uppercase tracking-[0.2em] group-hover:text-white/70 transition-colors duration-500">{label}</p>
      </div>
    </motion.div>
  );
};

// Engineering Section Divider — Architectural transition element
const EngineeringDivider = () => (
  <div className="relative py-3 overflow-hidden" data-testid="engineering-divider">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 border border-[#8B2332]/30 rotate-45" />
          <div className="w-2 h-2 border border-[#8B2332]/40 rotate-45" />
          <div className="w-1.5 h-1.5 border border-[#8B2332]/30 rotate-45" />
        </div>
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
      </div>
    </div>
  </div>
);

// =============================================
// EXECUTIVE ENGINEERING ACHIEVEMENT DASHBOARD
// Premium trust-building section — reusable design system component
// =============================================

// Executive Achievement Card — Premium KPI widget with animated counter
const ExecutiveAchievementCard = ({ value, suffix = '', label, sublabel, icon: Icon, index, isText = false }) => {
  const { count, startCounting } = useCountUp(isText ? 0 : (typeof value === 'number' ? value : parseInt(value) || 0), 2200);
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springCfg = { stiffness: 200, damping: 25 };
  const tiltX = useSpring(useTransform(mouseY, [-0.5, 0.5], [2, -2]), springCfg);
  const tiltY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-2, 2]), springCfg);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !isVisible) { setIsVisible(true); if (!isText) startCounting(); } },
      { threshold: 0.4 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [startCounting, isVisible, isText]);

  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  }, [mouseX, mouseY]);

  const handleMouseLeave = useCallback(() => {
    mouseX.set(0);
    mouseY.set(0);
    setIsHovered(false);
  }, [mouseX, mouseY]);

  const displayValue = isText ? value : count;
  const isLongValue = String(displayValue).length > 5;

  return (
    <motion.div
      ref={cardRef}
      className="group relative h-full"
      style={{ perspective: 800 }}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.9, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      data-testid={`achievement-card-${index}`}
    >
      <motion.div
        className="relative overflow-hidden h-full"
        style={{
          rotateX: tiltX,
          rotateY: tiltY,
          transformStyle: 'preserve-3d',
        }}
      >
        {/* Multi-layer glass architecture */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(155deg, rgba(18,19,24,0.94) 0%, rgba(10,11,13,0.98) 100%)',
          border: isHovered ? '1px solid rgba(139,35,50,0.22)' : '1px solid rgba(255,255,255,0.06)',
          boxShadow: isHovered
            ? 'inset 0 1px 0 rgba(255,255,255,0.06), inset 0 0 0 1px rgba(139,35,50,0.06), 0 35px 70px rgba(0,0,0,0.5), 0 0 50px rgba(139,35,50,0.06)'
            : 'inset 0 1px 0 rgba(255,255,255,0.04), inset 0 0 0 1px rgba(255,255,255,0.02), 0 10px 40px rgba(0,0,0,0.35)',
          transition: 'border-color 0.6s ease, box-shadow 0.6s ease',
        }} />

        {/* Blueprint texture */}
        <div className="absolute inset-0 blueprint-texture opacity-30" />

        {/* Top edge metallic trim */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />

        {/* Animated top accent line */}
        <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden">
          <div style={{
            height: '100%',
            width: isHovered ? '100%' : '25%',
            background: isHovered ? 'linear-gradient(to right, #8B2332, rgba(212,85,103,0.5), transparent)' : 'rgba(139,35,50,0.35)',
            transition: 'width 0.8s cubic-bezier(0.16, 1, 0.3, 1), background 0.5s ease',
          }} />
        </div>

        {/* Engineering corner brackets — all 4 */}
        <div className="absolute top-3 left-3 pointer-events-none" style={{
          width: isHovered ? '28px' : '16px', height: isHovered ? '28px' : '16px',
          borderTop: isHovered ? '1.5px solid rgba(139,35,50,0.35)' : '1px solid rgba(255,255,255,0.06)',
          borderLeft: isHovered ? '1.5px solid rgba(139,35,50,0.35)' : '1px solid rgba(255,255,255,0.06)',
          transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
        }} />
        <div className="absolute top-3 right-3 pointer-events-none" style={{
          width: isHovered ? '28px' : '16px', height: isHovered ? '28px' : '16px',
          borderTop: isHovered ? '1.5px solid rgba(255,255,255,0.15)' : '1px solid rgba(255,255,255,0.05)',
          borderRight: isHovered ? '1.5px solid rgba(255,255,255,0.15)' : '1px solid rgba(255,255,255,0.05)',
          transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
        }} />
        <div className="absolute bottom-3 left-3 pointer-events-none" style={{
          width: isHovered ? '24px' : '14px', height: isHovered ? '24px' : '14px',
          borderBottom: isHovered ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.04)',
          borderLeft: isHovered ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.04)',
          transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
        }} />
        <div className="absolute bottom-3 right-3 pointer-events-none" style={{
          width: isHovered ? '24px' : '14px', height: isHovered ? '24px' : '14px',
          borderBottom: isHovered ? '1px solid rgba(139,35,50,0.25)' : '1px solid rgba(255,255,255,0.04)',
          borderRight: isHovered ? '1px solid rgba(139,35,50,0.25)' : '1px solid rgba(255,255,255,0.04)',
          transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
        }} />

        {/* Ambient hover glow */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(135deg, rgba(139,35,50,0.05) 0%, transparent 50%)',
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.7s ease',
        }} />

        {/* Scanning light on hover */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div style={{
            position: 'absolute', top: 0, left: 0, width: '50%', height: '100%',
            background: 'linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.03) 45%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.03) 55%, transparent 70%)',
            transform: 'translateX(-150%)',
            animation: isHovered ? 'scanLightSweep 3s ease-in-out infinite' : 'none',
          }} />
        </div>

        {/* Content */}
        <div className="relative z-10 flex h-full flex-col p-5 sm:p-8 lg:p-10 text-center">
          {/* Premium icon badge — fixed zone */}
          <div className="relative w-16 h-16 shrink-0 mx-auto mb-6 lg:mb-7 flex items-center justify-center">
            {/* Outer ring */}
            <div className="absolute inset-0 rounded-full border border-[#8B2332]/15" style={{
              boxShadow: isHovered ? '0 0 20px rgba(139,35,50,0.12)' : 'none',
              borderColor: isHovered ? 'rgba(139,35,50,0.3)' : 'rgba(139,35,50,0.15)',
              transition: 'all 0.6s ease',
            }} />
            {/* Inner glass surface */}
            <div className="absolute inset-1.5 rounded-full" style={{
              background: 'linear-gradient(145deg, rgba(139,35,50,0.1) 0%, rgba(139,35,50,0.04) 100%)',
              border: '1px solid rgba(139,35,50,0.18)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
            }} />
            {/* Ambient glow behind icon */}
            <div className="absolute inset-0 rounded-full" style={{
              boxShadow: isHovered ? '0 0 30px rgba(139,35,50,0.2)' : 'none',
              transition: 'box-shadow 0.6s ease',
            }} />
            <Icon
              className="relative z-10 transition-all duration-500"
              size={22}
              strokeWidth={1.5}
              style={{
                color: isHovered ? '#d45567' : '#8B2332',
                transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                transition: 'color 0.5s ease, transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            />
          </div>

          {/* Large number / value — fixed zone (supports up to 2 lines) */}
          <motion.div
            className="shrink-0 mb-3 flex h-[3.75rem] sm:h-[4.5rem] lg:h-[5rem] items-center justify-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isVisible ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: index * 0.1 + 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <span
              className={`font-['Barlow_Condensed'] font-bold text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-white/70 block ${isLongValue ? 'text-[1.75rem] sm:text-[2.25rem] lg:text-[2.6rem]' : 'text-4xl sm:text-5xl lg:text-[3.25rem]'}`}
              style={{
                textShadow: isHovered ? '0 0 40px rgba(139,35,50,0.15)' : 'none',
                filter: isHovered ? 'drop-shadow(0 0 8px rgba(139,35,50,0.15))' : 'none',
                lineHeight: '1.05',
              }}
            >
              {displayValue}{suffix}
            </span>
          </motion.div>

          {/* Label — fixed zone (supports up to 2 lines) */}
          <div className="shrink-0 flex h-[2.5rem] items-start justify-center">
            <p className="text-white/60 text-[11px] sm:text-xs font-bold uppercase tracking-[0.22em] leading-[1.5]" style={{
              color: isHovered ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.6)',
              transition: 'color 0.5s ease',
            }}>
              {label}
            </p>
          </div>

          {/* Supporting text — fixed zone */}
          <div className="shrink-0 flex h-[2rem] items-start justify-center">
            {sublabel && (
              <p className="text-white/30 text-[10px] tracking-[0.15em] uppercase leading-[1.5]" style={{
                color: isHovered ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.3)',
                transition: 'color 0.5s ease',
              }}>
                {sublabel}
              </p>
            )}
          </div>

          {/* Bottom accent line */}
          <div className="mt-auto pt-6 shrink-0">
            <div className="mx-auto h-[1px] overflow-hidden" style={{
              width: isHovered ? '60%' : '30%',
              background: isHovered ? 'linear-gradient(to right, transparent, rgba(139,35,50,0.5), transparent)' : 'linear-gradient(to right, transparent, rgba(255,255,255,0.06), transparent)',
              transition: 'width 0.6s cubic-bezier(0.16, 1, 0.3, 1), background 0.5s ease',
            }} />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Executive Achievement Dashboard — Full section component
const ExecutiveAchievementDashboard = () => {
  const achievements = [
    { value: 1999, suffix: '', label: 'Established', sublabel: 'Year Founded', icon: Clock, isText: true },
    { value: 200, suffix: '+', label: 'Projects Completed', sublabel: 'Engineering Track Record', icon: Target },
    { value: 'OEM', suffix: '', label: 'Standard Workmanship', sublabel: 'Specification-Led', icon: Shield, isText: true },
    { value: 'Multi-Sector', suffix: '', label: 'Engineering Capability', sublabel: 'Mining · Industrial · Energy', icon: Globe, isText: true },
  ];

  return (
    <section className="relative py-20 lg:py-24 overflow-hidden" data-testid="achievement-dashboard">
      {/* Background layers */}
      <div className="absolute inset-0 bg-[#08090B]" />
      <div className="absolute inset-0 blueprint-texture" />

      {/* Engineering grid overlay */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.008) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.008) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
      }} />

      {/* Atmospheric lighting */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none" style={{
        background: 'radial-gradient(ellipse 45% 40% at 50% 30%, rgba(139,35,50,0.05) 0%, transparent 60%)'
      }} />
      <div className="absolute bottom-0 left-0 w-full h-full pointer-events-none" style={{
        background: 'radial-gradient(ellipse 40% 40% at 20% 80%, rgba(51,51,163,0.025) 0%, transparent 60%)'
      }} />
      <div className="absolute bottom-0 right-0 w-full h-full pointer-events-none" style={{
        background: 'radial-gradient(ellipse 40% 40% at 80% 80%, rgba(139,35,50,0.03) 0%, transparent 60%)'
      }} />

      {/* Vertical engineering lines */}
      <div className="absolute top-0 left-[10%] w-px h-full bg-gradient-to-b from-transparent via-white/[0.015] to-transparent pointer-events-none" />
      <div className="absolute top-0 left-[30%] w-px h-full bg-gradient-to-b from-transparent via-white/[0.015] to-transparent pointer-events-none" />
      <div className="absolute top-0 left-[50%] w-px h-full bg-gradient-to-b from-transparent via-white/[0.02] to-transparent pointer-events-none" />
      <div className="absolute top-0 left-[70%] w-px h-full bg-gradient-to-b from-transparent via-white/[0.015] to-transparent pointer-events-none" />
      <div className="absolute top-0 left-[90%] w-px h-full bg-gradient-to-b from-transparent via-white/[0.015] to-transparent pointer-events-none" />

      {/* Top transition from CTA */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#050506] to-transparent pointer-events-none z-10" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20 lg:mb-24">
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mb-7"
          >
            <span className="inline-flex items-center gap-5">
              <motion.span className="h-[2px] bg-gradient-to-r from-transparent to-[#8B2332]" initial={{ width: 0 }} whileInView={{ width: 50 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }} />
              <span className="text-[#8B2332] text-[13px] lg:text-[14px] font-bold uppercase tracking-[0.35em]">Our Engineering Legacy</span>
              <motion.span className="h-[2px] bg-gradient-to-l from-transparent to-[#8B2332]" initial={{ width: 0 }} whileInView={{ width: 50 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }} />
            </span>
          </motion.div>

          <motion.h2
            className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold uppercase text-white mb-6"
            style={{ letterSpacing: '-0.01em', lineHeight: '0.95' }}
            initial={{ opacity: 0, y: 35 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            Engineering Experience{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B2332] to-[#c44b5e]">Since</span>{' '}
            1999
          </motion.h2>

          <motion.p
            className="text-white/50 text-base lg:text-lg max-w-2xl mx-auto leading-[1.9]"
            style={{ letterSpacing: '0.01em' }}
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.25, duration: 0.7 }}
          >
            Since 1999, Welkom Winder Services has delivered specialist mine-winder, electrical and mechanical engineering with OEM-standard workmanship and hundreds of successful projects across southern Africa.
          </motion.p>
        </div>

        {/* Achievement Cards Grid — equal-height system */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 lg:gap-6 items-stretch">
          {achievements.map((item, index) => (
            <ExecutiveAchievementCard key={item.label} {...item} index={index} />
          ))}
        </div>
      </div>

      {/* Bottom transition into footer */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#050506] via-[#050506]/60 to-transparent pointer-events-none" />
    </section>
  );
};

// Navigation Component - Premium Engineering-Grade Header
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const navLinks = useMemo(() => [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Products', path: '/products' },
    { name: 'Industries', path: '/industries/mining' },
    { name: 'Contact', path: '/contact' }
  ], []);

  const isActive = useCallback((path) => {
    if (path === '/') return location.pathname === '/';
    if (path.startsWith('/industries')) return location.pathname.startsWith('/industries') || location.pathname.startsWith('/projects');
    if (path.startsWith('/products')) return location.pathname.startsWith('/products');
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  return (
    <>
      <motion.nav
        data-testid="main-navigation"
        role="navigation"
        aria-label="Main navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-out ${
          isScrolled ? 'glass py-3' : 'bg-transparent py-4 lg:py-5'
        }`}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Subtle ambient glow on scrolled state */}
        {isScrolled && (
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-64 h-[1px] pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(139,35,50,0.15) 0%, transparent 70%)' }} />
        )}

        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between">
          
          {/* ─── Logo ─── */}
          <Link to="/" className="group/logo flex items-center flex-shrink-0 mr-10 lg:mr-14" data-testid="logo-link">
            <motion.div
              className="relative transition-all duration-500 ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/logo:-translate-y-[1px]"
              initial={{ opacity: 0, x: -15 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
              <img 
                src={LOGO_URL} 
                alt="Welkom Winder Services" 
                className={`w-auto transition-all duration-700 ease-out ${isScrolled ? 'h-[4.5rem] lg:h-20' : 'h-20 lg:h-[6.5rem]'}`}
              />
              {/* Subtle border frame for white-bg logo */}
              <div className="absolute inset-0 border border-white/[0.04] group-hover/logo:border-[#8B2332]/15 transition-colors duration-500 pointer-events-none" />
            </motion.div>
          </Link>

          {/* ─── Desktop Nav Links ─── */}
          <div className="hidden lg:flex items-center flex-1 justify-center">
            <div className="flex items-center gap-7 xl:gap-10">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.15 + index * 0.05, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={link.path}
                    className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <span className="nav-bracket-tl" />
                    <span className="nav-bracket-br" />
                    {link.name}
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>

          {/* ─── Search + CTA ─── */}
          <motion.div 
            className="hidden lg:flex items-center gap-4 flex-shrink-0"
            initial={{ opacity: 0, x: 15 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Search button */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="group flex items-center gap-2.5 px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] text-white/35 text-xs rounded-sm hover:text-white/60 hover:border-white/12 hover:bg-white/[0.05] transition-all duration-500"
              data-testid="search-button"
              aria-label="Search"
            >
              <Search size={13} className="group-hover:text-[#8B2332] transition-colors duration-500" />
              <span className="font-['Manrope'] tracking-wider text-[11px]">Search</span>
              <span className="text-white/12 text-[9px] border border-white/8 px-1.5 py-0.5 ml-0.5 font-mono">⌘K</span>
            </button>

            {/* Premium CTA button */}
            <Link
              to="/contact"
              className="group relative inline-flex items-center gap-3 bg-gradient-to-b from-[#8B2332] to-[#721c29] text-white/95 text-xs font-semibold px-7 py-3 overflow-hidden transition-all duration-500 hover:-translate-y-0.5"
              style={{ 
                letterSpacing: '0.14em', 
                textTransform: 'uppercase',
                boxShadow: '0 4px 16px rgba(139,35,50,0.2), inset 0 1px 0 rgba(255,255,255,0.08)',
              }}
              data-testid="nav-cta-button"
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 12px 30px rgba(139,35,50,0.4), inset 0 1px 0 rgba(255,255,255,0.12)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(139,35,50,0.2), inset 0 1px 0 rgba(255,255,255,0.08)'; }}
            >
              <span className="relative z-10">Get in Touch</span>
              <ArrowRight size={13} className="relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
              {/* Hover gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#a03040] via-[#8B2332] to-[#6e1c28] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              {/* Light sweep */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[800ms] ease-out bg-gradient-to-r from-transparent via-white/12 to-transparent" />
            </Link>
          </motion.div>

          {/* ─── Mobile Controls ─── */}
          <div className="lg:hidden flex items-center gap-2">
            <button
              onClick={() => setIsSearchOpen(true)}
              className="text-white/40 p-2.5 hover:text-white/70 transition-colors duration-300"
              aria-label="Search"
            >
              <Search size={18} />
            </button>
            <button
              className="text-white/70 p-2.5 hover:text-white transition-colors duration-300"
              onClick={() => setIsMobileMenuOpen(true)}
              data-testid="mobile-menu-button"
              aria-label="Open navigation menu"
              aria-expanded={isMobileMenuOpen}
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </motion.nav>

      {/* ─── Mobile Menu ─── */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-[60] bg-[#0A0B0D]/98 backdrop-blur-xl flex flex-col items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-menu"
          >
            {/* Ambient glow */}
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(139,35,50,0.08) 0%, transparent 70%)' }} />
            
            <button
              className="absolute top-6 right-6 text-white/50 p-3 hover:text-white transition-colors duration-300"
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid="close-mobile-menu"
              aria-label="Close navigation menu"
            >
              <X size={28} />
            </button>
            
            {/* Logo at top */}
            <motion.img 
              src={LOGO_URL} 
              alt="WWS" 
              className="h-10 mb-12 opacity-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.4 }}
              transition={{ delay: 0.1 }}
            />
            
            <nav className="flex flex-col items-center gap-1">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.06, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={link.path}
                    className={`block px-8 py-3.5 font-['Barlow_Condensed'] text-2xl font-semibold uppercase tracking-[0.12em] transition-colors duration-300 ${
                      isActive(link.path) ? 'text-[#8B2332]' : 'text-white/50 hover:text-white/80'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
            </nav>
            
            {/* Mobile CTA */}
            <motion.div
              className="mt-10"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.4 }}
            >
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 bg-gradient-to-b from-[#8B2332] to-[#721c29] text-white/90 text-sm font-semibold px-10 py-4 uppercase tracking-[0.15em]"
                style={{ boxShadow: '0 4px 20px rgba(139,35,50,0.3)' }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Get in Touch <ArrowRight size={14} />
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <SearchOverlay isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
};

// Hero Section - World-Class Cinematic Luxury Industrial Experience
const HeroSection = () => {
  const { scrollY } = useScroll();
  
  // Background parallax - moves slower
  const bgY = useTransform(scrollY, [0, 1000], [0, 400]);
  const bgScale = useTransform(scrollY, [0, 1000], [1, 1.25]);
  
  // Content fade - headline and overline fade gradually
  const contentOpacity = useTransform(scrollY, [0, 300, 550], [1, 1, 0]);
  const contentY = useTransform(scrollY, [0, 600], [0, 60]);
  
  // Subheading - fades slightly later
  const subheadingOpacity = useTransform(scrollY, [0, 400, 600], [1, 1, 0]);
  const subheadingY = useTransform(scrollY, [0, 600], [0, 35]);
  
  // Buttons - stay visible longest, fade out gracefully
  const buttonsOpacity = useTransform(scrollY, [0, 300, 500], [1, 1, 0]);
  const buttonsY = useTransform(scrollY, [0, 500], [0, 25]);
  const buttonsScale = useTransform(scrollY, [0, 500], [1, 0.98]);

  // Premium animation variants with luxury timing
  const headlineVariants = {
    hidden: { 
      opacity: 0, 
      y: 120,
      rotateX: -50,
      transformOrigin: "bottom center",
    },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: {
        duration: 1.2,
        delay: 0.3 + (i * 0.1),
        ease: [0.16, 1, 0.3, 1]
      }
    })
  };

  const subheadingVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1,
        delay: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.92 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        delay: 0.9 + (i * 0.12),
        ease: [0.16, 1, 0.3, 1]
      }
    })
  };

  return (
    <section className="relative overflow-hidden bg-[#030304]" style={{ minHeight: 'min(100vh, 900px)' }} data-testid="hero-section">
      
      {/* ═══════════════════════════════════════════════════════════════
          LAYER 1: BACKGROUND - Mine Winder Scene with Parallax
      ═══════════════════════════════════════════════════════════════ */}
      <motion.div 
        style={{ y: bgY, scale: bgScale }} 
        className="absolute inset-0 will-change-transform"
      >
        {/* Background Image - Immediate fade in */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="absolute inset-0"
        >
          <OptimizedImage
            src={IMAGES.hero}
            alt="Mine winder headframe - industrial heavy machinery"
            className="w-full h-full object-cover object-center"
            priority={true}
            style={{ 
              filter: 'brightness(0.5) contrast(1.25) saturate(0.8)',
              transform: 'scale(1.05)'
            }}
          />
        </motion.div>
        
        {/* SIGNATURE EFFECT A: Dynamic Lighting Shift */}
        <motion.div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse 100% 80% at 30% 20%, rgba(255,220,180,0.08) 0%, transparent 50%)'
          }}
          animate={{ 
            opacity: [0.3, 0.6, 0.3],
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 8, 
            repeat: Infinity, 
            ease: "easeInOut"
          }}
        />
      </motion.div>

      {/* ═══════════════════════════════════════════════════════════════
          LAYER 2: OVERLAYS - Gradients, Vignette, Atmosphere
      ═══════════════════════════════════════════════════════════════ */}
      
      {/* SIGNATURE EFFECT B: Cinematic Light Sweep */}
      <motion.div 
        className="absolute inset-0 pointer-events-none z-[1]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
      >
        <motion.div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(110deg, transparent 0%, transparent 35%, rgba(255,255,255,0.04) 40%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 60%, transparent 65%, transparent 100%)',
            backgroundSize: '300% 100%'
          }}
          animate={{ 
            backgroundPosition: ['-150% 0%', '150% 0%']
          }}
          transition={{ 
            duration: 6,
            repeat: Infinity,
            repeatDelay: 5,
            ease: "easeInOut"
          }}
        />
      </motion.div>
      
      {/* Cinematic Color Grade - Warm highlight top-left */}
      <div 
        className="absolute inset-0 pointer-events-none z-[2]"
        style={{
          background: 'radial-gradient(ellipse 60% 50% at 0% 10%, rgba(139, 35, 50, 0.2) 0%, transparent 60%)'
        }}
      />
      
      {/* Cool accent light - bottom-right */}
      <div 
        className="absolute inset-0 pointer-events-none z-[2]"
        style={{
          background: 'radial-gradient(ellipse 50% 60% at 100% 80%, rgba(51, 51, 163, 0.08) 0%, transparent 50%)'
        }}
      />
      
      {/* Primary Gradient Overlay - Heavy bottom */}
      <div className="absolute inset-0 z-[3] bg-gradient-to-t from-[#030304] via-[#030304]/65 to-[#030304]/20" />
      
      {/* Side Depth Gradients */}
      <div className="absolute inset-0 z-[3] bg-gradient-to-r from-[#030304]/90 via-transparent to-[#030304]/50" />
      
      {/* Strong Radial Vignette */}
      <div 
        className="absolute inset-0 pointer-events-none z-[4]"
        style={{
          background: 'radial-gradient(ellipse 60% 50% at 50% 50%, transparent 0%, rgba(3, 3, 4, 0.6) 55%, rgba(3, 3, 4, 0.95) 100%)'
        }}
      />
      
      {/* Atmospheric Haze Layer */}
      <motion.div 
        className="absolute inset-0 pointer-events-none z-[5]"
        style={{
          background: 'linear-gradient(180deg, rgba(3,3,4,0.4) 0%, rgba(3,3,4,0.05) 40%, rgba(3,3,4,0.1) 60%, rgba(3,3,4,0.8) 100%)'
        }}
        animate={{ opacity: [0.9, 1, 0.9] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />
      
      {/* Top Cinematic Letterbox */}
      <div className="absolute top-0 left-0 right-0 h-24 z-[6] bg-gradient-to-b from-[#030304] via-[#030304]/60 to-transparent" />
      
      {/* SIGNATURE EFFECT C: Atmospheric Dust Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-[7]">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: `${1.5 + Math.random() * 2}px`,
              height: `${1.5 + Math.random() * 2}px`,
              left: `${10 + Math.random() * 80}%`,
              top: `${10 + Math.random() * 80}%`,
              background: `rgba(255, 255, 255, ${0.15 + Math.random() * 0.2})`,
              boxShadow: '0 0 4px rgba(255,255,255,0.3)'
            }}
            animate={{
              y: [0, -60 - Math.random() * 40],
              x: [(Math.random() - 0.5) * 20, (Math.random() - 0.5) * 30],
              opacity: [0, 0.8, 0],
              scale: [0.5, 1, 0.5]
            }}
            transition={{
              duration: 12 + Math.random() * 8,
              repeat: Infinity,
              delay: Math.random() * 10,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>
      
      {/* Film Grain Texture */}
      <div 
        className="absolute inset-0 z-[8] opacity-[0.04] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`
        }}
      />

      {/* ═══════════════════════════════════════════════════════════════
          LAYER 3: FOREGROUND - Content & UI
      ═══════════════════════════════════════════════════════════════ */}
      <div className="relative z-[10] flex items-center" style={{ minHeight: 'min(100vh, 900px)' }}>
        <div className="w-full max-w-[110rem] mx-auto px-5 sm:px-6 lg:px-20 xl:px-28" style={{ paddingTop: 'clamp(5rem, 12vh, 9rem)', paddingBottom: 'clamp(5rem, 12vh, 9rem)' }}>
          
          {/* Overline with animated line - fades with content */}
          <motion.div
            style={{ opacity: contentOpacity, y: contentY }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="mb-8 lg:mb-10 group/brand cursor-default"
            >
              {/* Red technical marker line — extends on hover */}
              <motion.div
                className="h-[2px] bg-gradient-to-r from-[#8B2332] via-[#8B2332] to-[#8B2332]/40 mb-5 transition-all duration-[350ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/brand:to-[#8B2332]/70"
                initial={{ width: 0 }}
                animate={{ width: 80 }}
                transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                style={{ transition: 'width 350ms cubic-bezier(0.22,0.61,0.36,1)' }}
              />
              {/* Prominent company name — subtle activation on hover */}
              <motion.h2
                className="font-['Barlow_Condensed'] text-[20px] sm:text-[26px] md:text-[32px] lg:text-[38px] font-bold uppercase text-white/95 tracking-[0.06em] leading-[1.15] transition-all duration-[350ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/brand:text-white group-hover/brand:tracking-[0.07em] group-hover/brand:-translate-y-[1px]"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                style={{ textShadow: '0 4px 30px rgba(0,0,0,0.6)', transition: 'color 350ms cubic-bezier(0.22,0.61,0.36,1), letter-spacing 350ms cubic-bezier(0.22,0.61,0.36,1), transform 350ms cubic-bezier(0.22,0.61,0.36,1), text-shadow 350ms cubic-bezier(0.22,0.61,0.36,1)' }}
                data-testid="hero-company-name"
                onMouseEnter={(e) => { e.currentTarget.style.textShadow = '0 4px 30px rgba(0,0,0,0.6), 0 0 40px rgba(139,35,50,0.12)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.textShadow = '0 4px 30px rgba(0,0,0,0.6)'; }}
              >
                Welkom Winder Services <span className="transition-colors duration-[350ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] text-white/60 group-hover/brand:text-white/75">(Pty) Ltd</span>
              </motion.h2>
              {/* Red underline — sweep effect on hover */}
              <div className="relative mt-4 h-[1px] overflow-hidden" style={{ width: '60%' }}>
                {/* Base line */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-[#8B2332]/60 via-[#8B2332]/20 to-transparent transition-opacity duration-[350ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/brand:opacity-40"
                  initial={{ scaleX: 0, transformOrigin: 'left' }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                />
                {/* Hover sweep — brighter red travelling segment */}
                <div className="absolute inset-0 -translate-x-full group-hover/brand:translate-x-[200%] transition-transform duration-[600ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] bg-gradient-to-r from-transparent via-[#8B2332] to-transparent" style={{ width: '40%' }} />
                {/* Hover state — full bright line */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#8B2332] via-[#8B2332]/70 to-[#8B2332]/25 opacity-0 group-hover/brand:opacity-100 transition-opacity duration-[350ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] delay-[200ms]" />
              </div>
            </motion.div>
          </motion.div>
          
          {/* HEADLINE - fades with content */}
          <motion.div
            style={{ opacity: contentOpacity, y: contentY }}
          >
            <div
              className="mb-6 lg:mb-8"
              style={{ perspective: "1500px" }}
              data-testid="hero-headline"
            >
              {/* Line 1 */}
              <div className="overflow-hidden mb-1 lg:mb-2">
                <motion.h1 
                  variants={headlineVariants}
                  initial="hidden"
                  animate="visible"
                  custom={0}
                  className="font-['Barlow_Condensed'] text-[2.8rem] sm:text-[5rem] md:text-[6rem] lg:text-[8rem] xl:text-[10rem] font-black uppercase text-white leading-[0.82] tracking-[-0.02em]"
                  style={{ textShadow: '0 8px 60px rgba(0,0,0,0.8), 0 2px 20px rgba(0,0,0,0.6)' }}
                >
                  Engineering
                </motion.h1>
              </div>
              
              {/* Line 2 - Accent */}
              <div className="overflow-hidden">
                <motion.h1 
                  variants={headlineVariants}
                  initial="hidden"
                  animate="visible"
                  custom={1}
                  className="font-['Barlow_Condensed'] text-[2.8rem] sm:text-[5rem] md:text-[6rem] lg:text-[8rem] xl:text-[10rem] font-black uppercase leading-[0.82] tracking-[-0.02em] relative inline-block"
                >
                  <motion.span 
                    className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B2332] via-[#d45567] to-[#8B2332] bg-[length:200%_100%]"
                    animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    style={{ filter: 'drop-shadow(0 8px 60px rgba(139,35,50,0.4))' }}
                  >
                    Reliability.
                  </motion.span>
                  <span className="absolute inset-0 blur-[50px] opacity-40 text-[#8B2332] -z-10" aria-hidden="true">Reliability.</span>
                  
                  <motion.span 
                    className="absolute -bottom-1 left-0 h-[4px] bg-gradient-to-r from-[#8B2332] via-[#d45567] to-transparent rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: "70%" }}
                    transition={{ duration: 0.9, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
                  />
                </motion.h1>
              </div>
              
              {/* Line 3 */}
              <div className="overflow-hidden mt-2 lg:mt-3">
                <motion.p 
                  variants={headlineVariants}
                  initial="hidden"
                  animate="visible"
                  custom={2}
                  className="font-['Barlow_Condensed'] text-[1.4rem] sm:text-[2rem] md:text-[2.8rem] lg:text-[3.5rem] xl:text-[4rem] font-bold uppercase text-white/60 leading-[0.9] tracking-[0.05em]"
                  style={{ textShadow: '0 4px 30px rgba(0,0,0,0.6)' }}
                >
                  Powering Industry.
                </motion.p>
              </div>
            </div>
          </motion.div>
          
          {/* Subheading - stays visible slightly longer */}
          <motion.div
            style={{ opacity: subheadingOpacity, y: subheadingY }}
          >
            <motion.p
              className="font-['Manrope'] text-lg sm:text-xl md:text-2xl lg:text-[1.75rem] text-white/55 max-w-2xl mb-8 lg:mb-10 leading-[1.9] tracking-[0.01em]"
              variants={subheadingVariants}
              initial="hidden"
              animate="visible"
              data-testid="hero-subheadline"
              style={{ textShadow: '0 3px 30px rgba(0,0,0,0.7)' }}
            >
              Specialist mine winder, electrical, mechanical and industrial engineering solutions built on hands-on experience, technical precision and dependable service since 1999.
            </motion.p>
          </motion.div>
          
          {/* Premium CTA Buttons - stay visible longest, fade gracefully */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-5 sm:gap-7"
            style={{ 
              opacity: buttonsOpacity, 
              y: buttonsY,
              scale: buttonsScale
            }}
          >
            {/* Primary Button */}
            <motion.div
              variants={buttonVariants}
              initial="hidden"
              animate="visible"
              custom={0}
            >
              <Link 
                to="/contact" 
                className="group relative inline-flex items-center gap-4 bg-gradient-to-b from-[#9a2a3b] to-[#7a1f2c] text-white font-bold px-12 sm:px-16 py-5 sm:py-7 overflow-hidden transition-all duration-600 ease-out hover:scale-[1.04] hover:shadow-[0_35px_80px_rgba(139,35,50,0.55),0_0_60px_rgba(139,35,50,0.2)] hover:-translate-y-1.5"
                style={{ boxShadow: '0 8px 30px rgba(139,35,50,0.3), inset 0 1px 0 rgba(255,255,255,0.1)' }}
                data-testid="hero-cta-quote"
              >
                <span className="relative z-10 tracking-[0.2em] uppercase text-[13px] sm:text-sm font-bold">
                  Request a Quotation
                </span>
                <motion.span
                  className="relative z-10"
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronRight size={22} strokeWidth={2.5} />
                </motion.span>
                
                {/* Hover gradient */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#a8485a] via-[#8B2332] to-[#6e1c28] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Shine sweep */}
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1100ms] ease-out bg-gradient-to-r from-transparent via-white/25 to-transparent" />
                
                {/* Inner highlight */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400" style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15), inset 0 0 30px rgba(255,255,255,0.1)' }} />
              </Link>
            </motion.div>
            
            {/* Secondary Button */}
            <motion.div
              variants={buttonVariants}
              initial="hidden"
              animate="visible"
              custom={1}
            >
              <Link 
                to="/services" 
                className="group relative inline-flex items-center gap-4 bg-white/[0.03] backdrop-blur-sm border-2 border-white/15 text-white font-bold px-12 sm:px-16 py-5 sm:py-7 overflow-hidden transition-all duration-600 ease-out hover:scale-[1.04] hover:border-white/35 hover:bg-white/[0.07] hover:shadow-[0_30px_70px_rgba(255,255,255,0.08)] hover:-translate-y-1.5"
                style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.06)' }}
                data-testid="hero-cta-services"
              >
                <span className="relative z-10 tracking-[0.2em] uppercase text-[13px] sm:text-sm font-bold">
                  Explore Our Services
                </span>
                <motion.span
                  className="relative z-10"
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <ArrowRight size={22} strokeWidth={2.5} />
                </motion.span>
                
                {/* Inner glow on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400" style={{ boxShadow: 'inset 0 0 40px rgba(255,255,255,0.08)' }} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
      
      {/* Bottom Cinematic Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-48 z-[9] bg-gradient-to-t from-[#030304] via-[#030304]/80 to-transparent pointer-events-none" />
      
      {/* Premium Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-4 z-[11]"
        initial={{ opacity: 0, y: 25 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      >
        <motion.span 
          className="text-white/15 text-[9px] uppercase tracking-[0.5em] font-semibold"
          animate={{ opacity: [0.15, 0.35, 0.15] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
        >
          Scroll
        </motion.span>
        <div className="relative w-[1px] h-12">
          <div className="absolute inset-0 bg-gradient-to-b from-[#8B2332]/50 via-white/15 to-transparent" />
          <motion.div
            className="absolute top-0 left-0 w-full h-2.5 bg-gradient-to-b from-[#8B2332] to-transparent rounded-full"
            animate={{ y: [0, 38, 0], opacity: [1, 0.3, 1] }}
            transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
      
      {/* Corner Design Accents */}
      <motion.div 
        className="absolute bottom-24 left-12 lg:left-24 z-[11]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
      >
        <div className="w-[1px] h-20 bg-gradient-to-t from-[#8B2332]/40 to-transparent" />
        <div className="w-20 h-[1px] bg-gradient-to-r from-[#8B2332]/40 to-transparent" />
      </motion.div>
      
      {/* Right vertical accent */}
      <motion.div 
        className="absolute top-1/2 right-12 lg:right-24 -translate-y-1/2 hidden lg:block z-[11]"
        initial={{ opacity: 0, scaleY: 0 }}
        animate={{ opacity: 1, scaleY: 1 }}
        transition={{ delay: 1.6, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        style={{ transformOrigin: "center" }}
      >
        <div className="w-[1px] h-32 bg-gradient-to-b from-transparent via-white/6 to-transparent" />
      </motion.div>
    </section>
  );
};

// Animated Counter Hook - optimized with cleanup
const useCountUp = (end, duration = 2000) => {
  const [count, setCount] = useState(0);
  const hasStarted = useRef(false);
  const rafRef = useRef(null);
  
  const startCounting = useCallback(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    
    const startTime = Date.now();
    const endValue = parseInt(end) || 0;
    
    const animate = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(progress >= 1 ? endValue : Math.floor(easeOut * endValue));
      
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    
    rafRef.current = requestAnimationFrame(animate);
  }, [end, duration]);
  
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);
  
  return { count, startCounting };
};

// Trust Strip Component - Elite Cinematic with Animated Counters
const TrustStrip = () => {
  const stats = [
    { value: 1999, suffix: "", label: "Engineering Experience", icon: Clock, isText: true },
    { value: "OEM", suffix: "", label: "Standard Workmanship", icon: Award, isText: true },
    { value: 200, suffix: "+", label: "Projects Completed", icon: Settings },
    { value: "Senior", suffix: "", label: "Winder Expertise", icon: Globe, isText: true }
  ];

  return (
    <section className="relative py-20 lg:py-24 overflow-hidden" data-testid="trust-strip">
      {/* Deep cinematic gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050506] via-[#0A0B0D] to-[#0e0f11]" />
      
      {/* Atmospheric glow behind stats */}
      <motion.div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[200%] rounded-full"
        style={{ background: 'radial-gradient(ellipse, rgba(139,35,50,0.06) 0%, transparent 70%)' }}
        animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      
      {/* Grid lines for industrial feel */}
      <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
        backgroundSize: '80px 80px'
      }} />
      
      {/* Top cinematic border line with glow */}
      <div className="absolute top-0 left-0 right-0">
        <div className="h-[1px] bg-gradient-to-r from-transparent via-[#8B2332]/50 to-transparent" />
        <div className="h-[6px] bg-gradient-to-r from-transparent via-[#8B2332]/10 to-transparent blur-sm" />
      </div>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-12 lg:gap-20">
          {stats.map((stat, index) => (
            <StatCounter key={stat.label} stat={stat} index={index} totalStats={stats.length} />
          ))}
        </div>
      </div>
      
      {/* Bottom cinematic border */}
      <div className="absolute bottom-0 left-0 right-0">
        <div className="h-[6px] bg-gradient-to-r from-transparent via-white/[0.03] to-transparent blur-sm" />
        <div className="h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>
    </section>
  );
};

// Individual Stat Counter with Cinematic Animation
const StatCounter = ({ stat, index, totalStats }) => {
  const { count, startCounting } = useCountUp(stat.value, 2500);
  const ref = useRef(null);
  const hasAnimated = useRef(false);
  
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          if (!stat.isText) {
            startCounting();
          }
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    
    observer.observe(el);
    return () => observer.disconnect();
  }, [startCounting, stat.isText]);
  
  return (
    <motion.div
      ref={ref}
      className="relative text-center group"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.7, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Vertical divider between stats (not on last) */}
      {index < totalStats - 1 && (
        <div className="absolute top-1/2 -right-6 lg:-right-10 -translate-y-1/2 h-20 w-[1px] bg-gradient-to-b from-transparent via-white/[0.08] to-transparent hidden md:block" />
      )}
      
      {/* Icon container with rotating diamond */}
      <div className="relative inline-flex items-center justify-center w-16 h-16 lg:w-18 lg:h-18 mb-6 mx-auto">
        <motion.div 
          className="absolute inset-0 bg-gradient-to-br from-[#8B2332]/20 to-[#8B2332]/5 border border-[#8B2332]/20 group-hover:border-[#8B2332]/40 transition-colors duration-500"
          initial={{ rotate: 0, borderRadius: '12px' }}
          whileInView={{ rotate: 45, borderRadius: '12px' }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
        />
        <div className="absolute inset-[2px] bg-[#0A0B0D]/80 transform rotate-45" style={{ borderRadius: '10px' }} />
        <stat.icon className="relative z-10 w-6 h-6 text-[#8B2332] group-hover:text-[#d45567] transition-colors duration-500" strokeWidth={1.5} />
      </div>
      
      {/* Counter value - always visible once scrolled into view */}
      <motion.div 
        className="font-['Barlow_Condensed'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-3 relative"
        initial={{ opacity: 0, y: 15 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-30px" }}
        transition={{ duration: 0.6, delay: index * 0.1 + 0.2, ease: [0.16, 1, 0.3, 1] }}
      >
        {stat.isText ? stat.value : count}{stat.suffix}
      </motion.div>
      
      {/* Label */}
      <motion.p 
        className="text-[#8B2332]/70 text-[10px] sm:text-xs font-bold uppercase tracking-[0.35em] group-hover:text-[#8B2332] transition-colors duration-500"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: index * 0.1 + 0.4 }}
      >
        {stat.label}
      </motion.p>
    </motion.div>
  );
};

// About Preview Section - Elite Cinematic
const AboutPreview = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 35 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
    }
  };

  return (
    <section className="relative py-20 lg:py-24 overflow-hidden" data-testid="about-preview">
      {/* Deep background */}
      <div className="absolute inset-0 bg-[#0A0B0D]" />
      
      {/* Cinematic accent lighting - right side warm glow */}
      <div className="absolute top-0 right-0 w-1/2 h-full pointer-events-none" style={{
        background: 'radial-gradient(ellipse 80% 60% at 100% 40%, rgba(139,35,50,0.08) 0%, transparent 60%)'
      }} />
      
      {/* Left cool accent */}
      <div className="absolute bottom-0 left-0 w-1/3 h-1/2 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 80% 60% at 0% 100%, rgba(51,51,163,0.04) 0%, transparent 60%)'
      }} />
      
      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-[0.012]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} />
      
      {/* Floating dust particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={`about-dust-${i}`}
            className="absolute rounded-full"
            style={{
              width: `${1 + Math.random() * 2}px`,
              height: `${1 + Math.random() * 2}px`,
              left: `${20 + Math.random() * 60}%`,
              top: `${20 + Math.random() * 60}%`,
              background: `rgba(255, 255, 255, ${0.1 + Math.random() * 0.15})`,
            }}
            animate={{
              y: [0, -40 - Math.random() * 30],
              opacity: [0, 0.6, 0],
            }}
            transition={{
              duration: 10 + Math.random() * 8,
              repeat: Infinity,
              delay: Math.random() * 8,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-28 items-center">
          {/* Text Content */}
          <motion.div
            className="group/about cursor-default"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {/* Overline */}
            <motion.div variants={itemVariants} className="mb-8">
              <span className="inline-flex items-center gap-5 group-hover/about:gap-6 transition-all duration-[350ms]">
                <motion.span 
                  className="h-[2px] bg-gradient-to-r from-[#8B2332] via-[#8B2332] to-transparent transition-all duration-[350ms] group-hover/about:to-[#a63d50]"
                  initial={{ width: 0 }}
                  whileInView={{ width: 60 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                  style={{ transition: 'width 350ms cubic-bezier(0.22,0.61,0.36,1)' }}
                />
                <span className="text-[#8B2332] text-[13px] lg:text-[14px] font-bold uppercase tracking-[0.35em] transition-all duration-[300ms] group-hover/about:text-[#a63d50] group-hover/about:tracking-[0.38em]">Who We Are</span>
              </span>
            </motion.div>
            
            {/* Headline */}
            <motion.h2 
              variants={itemVariants}
              className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold uppercase text-white mb-4 leading-[0.92]"
            >
              Welkom Winder{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B2332] to-[#c44b5e] transition-all duration-[350ms] group-hover/about:from-[#a63d50] group-hover/about:to-[#d46b7c]" style={{ filter: 'drop-shadow(0 0 20px rgba(139,35,50,0.3))' }}>Services</span>
            </motion.h2>
            <motion.p variants={itemVariants} className="font-['Barlow_Condensed'] text-xl md:text-2xl lg:text-3xl font-semibold uppercase text-white/50 mb-12 tracking-[0.04em]">
              Built on Experience. Engineered for Reliability.
            </motion.p>
            
            {/* Text blocks with hover interaction, corner brackets, and accent animation */}
            <div className="space-y-8 mb-14">
              <motion.div variants={itemVariants} className="group/p relative pl-6 border-l-2 border-[#8B2332]/30 hover:border-[#8B2332]/60 cursor-default overflow-hidden transition-all duration-[450ms]" style={{ transitionTimingFunction: 'cubic-bezier(.22,.61,.36,1)' }} whileHover={{ y: -3, transition: { duration: 0.45 } }}>
                <div className="absolute top-1 right-1 w-3.5 h-3.5 border-t border-r border-white/[0.04] group-hover/p:border-white/[0.12] group-hover/p:w-5 group-hover/p:h-5 transition-all duration-[450ms] pointer-events-none" />
                <div className="absolute bottom-1 left-1 w-3 h-3 border-b border-l border-white/[0.03] group-hover/p:border-[#8B2332]/20 group-hover/p:w-4 group-hover/p:h-4 transition-all duration-[450ms] pointer-events-none" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#8B2332]/[0.02] to-transparent opacity-0 group-hover/p:opacity-100 transition-opacity duration-500 pointer-events-none" />
                <p className="text-white/60 text-base lg:text-[17px] leading-[2] max-w-xl group-hover/p:text-white/75 transition-colors duration-[450ms]">
                  Welkom Winder Services (Pty) Ltd is a trusted leader in the electrical, mechanical, and mining industries, proudly serving clients across{' '}
                  <span className="text-white/90 font-semibold group-hover/p:text-white transition-colors duration-[450ms]">Africa since 1999</span>.
                </p>
              </motion.div>
              
              <motion.div variants={itemVariants} className="group/p relative pl-6 border-l-2 border-white/10 hover:border-[#8B2332]/40 cursor-default overflow-hidden transition-all duration-[450ms]" style={{ transitionTimingFunction: 'cubic-bezier(.22,.61,.36,1)' }} whileHover={{ y: -3, transition: { duration: 0.45 } }}>
                <div className="absolute top-1 right-1 w-3.5 h-3.5 border-t border-r border-white/[0.04] group-hover/p:border-white/[0.1] group-hover/p:w-5 group-hover/p:h-5 transition-all duration-[450ms] pointer-events-none" />
                <div className="absolute bottom-1 left-1 w-3 h-3 border-b border-l border-white/[0.03] group-hover/p:border-[#8B2332]/15 group-hover/p:w-4 group-hover/p:h-4 transition-all duration-[450ms] pointer-events-none" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#8B2332]/[0.02] to-transparent opacity-0 group-hover/p:opacity-100 transition-opacity duration-500 pointer-events-none" />
                <p className="text-white/55 text-base lg:text-[17px] leading-[2] max-w-xl group-hover/p:text-white/72 transition-colors duration-[450ms]">
                  We specialize in the supply, maintenance, repair, and upgrading of all types of mine winders, ensuring{' '}
                  <span className="text-white/90 font-semibold group-hover/p:text-white transition-colors duration-[450ms]">safe and efficient operations</span>{' '}
                  through strict{' '}
                  <span className="text-[#d45567] font-bold">OEM standards</span>.
                </p>
              </motion.div>
              
              <motion.div variants={itemVariants} className="group/p relative pl-6 border-l-2 border-white/5 hover:border-[#8B2332]/30 cursor-default overflow-hidden transition-all duration-[450ms]" style={{ transitionTimingFunction: 'cubic-bezier(.22,.61,.36,1)' }} whileHover={{ y: -3, transition: { duration: 0.45 } }}>
                <div className="absolute top-1 right-1 w-3.5 h-3.5 border-t border-r border-white/[0.04] group-hover/p:border-white/[0.1] group-hover/p:w-5 group-hover/p:h-5 transition-all duration-[450ms] pointer-events-none" />
                <div className="absolute bottom-1 left-1 w-3 h-3 border-b border-l border-white/[0.03] group-hover/p:border-[#8B2332]/15 group-hover/p:w-4 group-hover/p:h-4 transition-all duration-[450ms] pointer-events-none" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#8B2332]/[0.02] to-transparent opacity-0 group-hover/p:opacity-100 transition-opacity duration-500 pointer-events-none" />
                <p className="text-white/50 text-base lg:text-[17px] leading-[2] max-w-xl group-hover/p:text-white/68 transition-colors duration-[450ms]">
                  From heavy engineering and precision machining to{' '}
                  <span className="text-white/80 font-medium group-hover/p:text-white/95 transition-colors duration-[450ms]">electrical systems</span> and{' '}
                  <span className="text-white/80 font-medium group-hover/p:text-white/95 transition-colors duration-[450ms]">renewable energy</span> — we deliver end-to-end excellence.
                </p>
              </motion.div>
            </div>
            
            {/* CTA Link with premium feel */}
            <motion.div variants={itemVariants}>
              <Link 
                to="/about" 
                className="group inline-flex items-center gap-5" 
                data-testid="about-preview-link"
              >
                <span className="relative">
                  <span className="font-bold uppercase text-sm tracking-[0.2em] text-white group-hover:text-[#8B2332] transition-colors duration-500">
                    Learn More About Us
                  </span>
                  <span className="absolute -bottom-2 left-0 w-full h-[1px] bg-white/20 group-hover:bg-[#8B2332]/50 transition-colors duration-500" />
                  <motion.span 
                    className="absolute -bottom-2 left-0 h-[1px] bg-[#8B2332]"
                    initial={{ width: 0 }}
                    whileInView={{ width: "40%" }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                  />
                </span>
                <motion.div
                  className="w-10 h-10 border border-white/20 group-hover:border-[#8B2332]/60 group-hover:bg-[#8B2332]/10 flex items-center justify-center transition-all duration-500"
                  whileHover={{ x: 5 }}
                >
                  <ArrowRight size={16} className="text-white/60 group-hover:text-[#8B2332] transition-colors duration-500" />
                </motion.div>
              </Link>
            </motion.div>
          </motion.div>
          
          {/* Image side — Premium Engineered Frame */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 80 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <EngineeredImageFrame
              src={IMAGES.about}
              alt="Industrial facility - Welkom Winder Services operations"
              aspectRatio="4/3"
            />
            
            {/* Stats badge with premium glow */}
            <motion.div 
              className="absolute -bottom-10 -left-6 lg:-left-10 bg-gradient-to-br from-[#8B2332] to-[#6e1c28] p-10 lg:p-12 z-20 group/stat cursor-default overflow-hidden"
              initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
              whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ scale: 1.05, rotate: 1, boxShadow: "0 35px 70px rgba(139,35,50,0.45)" }}
              style={{ boxShadow: '0 20px 50px rgba(139,35,50,0.3)' }}
            >
              <div className="absolute top-2 right-2 w-6 h-6 border-t border-r border-white/10 group-hover/stat:border-white/25 group-hover/stat:w-8 group-hover/stat:h-8 transition-all duration-500 pointer-events-none" />
              <div className="absolute inset-0 -translate-x-full group-hover/stat:translate-x-full transition-transform duration-[800ms] ease-out bg-gradient-to-r from-transparent via-white/[0.08] to-transparent pointer-events-none" />
              <p className="text-[#8B2332]/90 text-[10px] uppercase tracking-[0.25em] font-bold relative z-10 mb-1">Since</p>
              <p className="font-['Barlow_Condensed'] text-5xl lg:text-6xl font-bold text-white relative z-10" style={{ textShadow: '0 0 30px rgba(255,255,255,0.15)' }}>1999</p>
              <p className="text-white/80 text-xs uppercase tracking-[0.2em] mt-2 font-semibold relative z-10">Engineering Experience</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
      
      {/* Section transition fade - longer, smoother */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#121318] via-[#121318]/50 to-transparent pointer-events-none" />
    </section>
  );
};

// Services Preview Section - Premium High-Converting
const ServicesPreview = () => {
  const navigate = useNavigate();
  const services = [
    { 
      title: "Mine Winder Services", 
      slug: "mine-winder-services", 
      desc: "Complete maintenance, repair, and upgrading of all mine winder types to strict OEM specifications.", 
      benefit: "Maximize uptime. Minimize risk.", 
      highlights: ["Level 1 & 2 Audits", "OEM Refurbishments", "Emergency Response"],
      icon: Cog, 
      image: IMAGES.mining 
    },
    { 
      title: "Electrical Solutions", 
      slug: "electrical-solutions", 
      desc: "Comprehensive electrical distribution, automation, and power management for demanding environments.", 
      benefit: "Powering operations safely and efficiently.", 
      highlights: ["Switchgear & MCC", "Automation Systems", "Power Distribution"],
      icon: Zap, 
      image: IMAGES.electrical 
    },
    { 
      title: "Mechanical Services", 
      slug: "mechanical-services", 
      desc: "Heavy engineering, hydraulic systems, precision machining, and industrial component fabrication.", 
      benefit: "Precision-built for extreme conditions.", 
      highlights: ["Precision Machining", "Hydraulic Repairs", "Structural Steel"],
      icon: Wrench, 
      image: IMAGES.mechanical 
    },
    { 
      title: "Engineering & Refurbishments", 
      slug: "engineering-refurbishments", 
      desc: "Full-scale equipment refurbishment, modernization, design engineering, and optimization.", 
      benefit: "Extend asset life. Boost performance.", 
      highlights: ["Equipment Modernization", "Project Management", "Technical Consulting"],
      icon: Settings, 
      image: IMAGES.hero 
    },
    { 
      title: "Renewable Energy", 
      slug: "renewable-energy", 
      desc: "Solar installations, hybrid power systems, and sustainable energy solutions for industry.", 
      benefit: "Sustainable power for the future.", 
      highlights: ["Solar PV Systems", "Energy Audits", "Battery Storage"],
      icon: Sun, 
      image: IMAGES.renewable 
    }
  ];

  return (
    <section className="relative py-20 lg:py-24 overflow-hidden" data-testid="services-preview">
      {/* Deep background */}
      <div className="absolute inset-0 bg-[#121318]" />
      
      {/* Top transition overlap */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#121318] to-transparent" />
      
      {/* Cinematic accent lighting */}
      <div className="absolute top-1/4 left-0 w-96 h-[600px] pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 0% 50%, rgba(139,35,50,0.05) 0%, transparent 70%)'
      }} />
      <div className="absolute bottom-1/4 right-0 w-96 h-[600px] pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 100% 50%, rgba(139,35,50,0.05) 0%, transparent 70%)'
      }} />
      
      {/* Industrial pattern */}
      <div className="absolute inset-0 opacity-[0.012]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section header */}
        <div className="text-center mb-16 lg:mb-20 group/services cursor-default">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mb-6"
          >
            <span className="inline-flex items-center gap-5">
              <motion.span 
                className="h-[2px] bg-gradient-to-r from-transparent to-[#8B2332] transition-all duration-[350ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/services:to-[#a63d50]"
                initial={{ width: 0 }}
                whileInView={{ width: 45 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                style={{ transition: 'width 350ms cubic-bezier(0.22,0.61,0.36,1)' }}
              />
              <span className="text-[#8B2332] text-[13px] lg:text-[14px] font-bold uppercase tracking-[0.35em] transition-all duration-[300ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/services:text-[#a63d50] group-hover/services:tracking-[0.38em]">What We Offer</span>
              <motion.span 
                className="h-[2px] bg-gradient-to-l from-transparent to-[#8B2332] transition-all duration-[350ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/services:to-[#a63d50]"
                initial={{ width: 0 }}
                whileInView={{ width: 45 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                style={{ transition: 'width 350ms cubic-bezier(0.22,0.61,0.36,1)' }}
              />
            </span>
          </motion.div>
          
          <motion.h2
            className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold uppercase text-white transition-all duration-[350ms] group-hover/services:text-white"
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            Our Services & Solutions
          </motion.h2>
          
          <motion.p
            className="text-white/55 text-base lg:text-lg mt-6 max-w-2xl mx-auto leading-[1.9]"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.25, duration: 0.7 }}
          >
            Comprehensive electrical, mechanical, and mining solutions tailored for performance, safety, and reliability.
          </motion.p>
        </div>
        
        {/* Services grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              className="group relative overflow-hidden cursor-pointer"
              style={{ boxShadow: '0 6px 30px rgba(0,0,0,0.35)' }}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ 
                y: -12, 
                boxShadow: '0 25px 60px rgba(139,35,50,0.2), 0 8px 30px rgba(0,0,0,0.5)',
                transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } 
              }}
              onClick={() => navigate(`/services#${service.slug}`)}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/services#${service.slug}`); }}
              data-testid={`service-card-${index}`}
            >
              {/* Background image */}
              <div className="absolute inset-0 overflow-hidden">
                <OptimizedImage
                  src={service.image} 
                  alt={service.title} 
                  className="w-full h-full object-cover"
                  style={{ opacity: 0.25, filter: 'saturate(0.5) brightness(0.9)' }}
                />
              </div>
              
              {/* Gradient overlays */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/90 to-[#0A0B0D]/50 group-hover:via-[#0A0B0D]/80 transition-all duration-700" />
              
              {/* Border frame */}
              <div className="absolute inset-0 border border-white/[0.07] group-hover:border-[#8B2332]/25 transition-colors duration-600" />
              
              {/* Top accent line */}
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#8B2332]/25 to-transparent group-hover:via-[#8B2332]/80 transition-all duration-600" />
              
              {/* Left accent on hover */}
              <div className="absolute top-0 left-0 w-[2px] h-0 group-hover:h-full bg-gradient-to-b from-[#8B2332] via-[#8B2332]/40 to-transparent transition-all duration-700 ease-out" />

              {/* Content */}
              <div className="relative z-10 p-7 lg:p-9 flex flex-col" style={{ minHeight: '400px' }}>
                {/* Icon */}
                <div className="w-14 h-14 bg-[#8B2332]/10 border border-[#8B2332]/25 flex items-center justify-center mb-7 relative group-hover:border-[#8B2332]/45 group-hover:bg-[#8B2332]/15 transition-all duration-500">
                  <service.icon className="w-6 h-6 text-[#8B2332] group-hover:text-[#d45567] transition-colors duration-500 relative z-10" strokeWidth={1.5} />
                  <div className="absolute -inset-1 opacity-0 group-hover:opacity-100 transition-opacity duration-600 rounded-sm" style={{ boxShadow: '0 0 18px rgba(139,35,50,0.15)' }} />
                </div>
                
                {/* Title */}
                <h3 className="font-['Barlow_Condensed'] text-[1.45rem] lg:text-[1.65rem] font-bold uppercase text-white mb-2.5 group-hover:text-[#d45567] transition-colors duration-500 leading-tight">
                  {service.title}
                </h3>
                
                {/* Benefit tagline */}
                <p className="text-[#8B2332]/70 text-[11px] font-bold uppercase tracking-[0.12em] mb-5 group-hover:text-[#8B2332] transition-colors duration-500">
                  {service.benefit}
                </p>
                
                {/* Description */}
                <p className="text-white/45 leading-[1.8] group-hover:text-white/65 transition-colors duration-500 text-[14px] mb-6">
                  {service.desc}
                </p>
                
                {/* Highlight points */}
                <div className="space-y-2.5 mb-auto">
                  {service.highlights.map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#8B2332]/40 group-hover:bg-[#8B2332] transition-colors duration-500 flex-shrink-0" />
                      <span className="text-white/40 text-[13px] group-hover:text-white/60 transition-colors duration-500">{item}</span>
                    </div>
                  ))}
                </div>
                
                {/* Learn More CTA button */}
                <div className="mt-8 pt-6 border-t border-white/[0.05] group-hover:border-[#8B2332]/25 transition-colors duration-500">
                  <span className="inline-flex items-center gap-3 text-[#8B2332]/70 group-hover:text-[#d45567] transition-all duration-500">
                    <span className="text-[13px] font-bold uppercase tracking-[0.15em]">Learn More</span>
                    <div className="w-5 h-[1px] bg-current group-hover:w-10 transition-all duration-500" />
                    <ArrowRight size={13} strokeWidth={2.5} className="transform group-hover:translate-x-1.5 transition-transform duration-500" />
                  </span>
                </div>
              </div>
              
              {/* Bottom ambient glow on hover */}
              <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#8B2332]/12 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            </motion.div>
          ))}
        </div>
        
        {/* CTA Button */}
        <motion.div
          className="text-center mt-16 lg:mt-20"
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.7 }}
        >
          <Link 
            to="/services" 
            className="group relative inline-flex items-center gap-4 bg-[#8B2332] text-white font-bold px-12 py-6 overflow-hidden transition-all duration-500 hover:shadow-[0_25px_60px_rgba(139,35,50,0.45)] hover:-translate-y-1"
            data-testid="services-view-all"
          >
            <span className="relative z-10 tracking-[0.2em] uppercase text-sm">View All Services</span>
            <ChevronRight size={18} className="relative z-10 transform group-hover:translate-x-1.5 transition-transform duration-300" />
            {/* Shine sweep */}
            <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[900ms] ease-out bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            {/* Hover gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#a8485a] via-[#8B2332] to-[#6e1c28] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </Link>
        </motion.div>
      </div>
      
      {/* Section transition */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/50 to-transparent pointer-events-none" />
    </section>
  );
};

// Sectors We Serve — Phase X Premium Showcase
const IndustriesSection = () => {
  const navigate = useNavigate();
  const featured = ALL_INDUSTRIES.slice(0, 4);
  const remaining = ALL_INDUSTRIES.slice(4);

  return (
    <section className="relative py-20 lg:py-24 overflow-hidden" data-testid="industries-section">
      {/* Background */}
      <div className="absolute inset-0 bg-[#0A0B0D]" />

      {/* Atmospheric lighting */}
      <div className="absolute top-0 left-0 w-full h-2/3 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 50% 40% at 50% 0%, rgba(139,35,50,0.03) 0%, transparent 70%)'
      }} />

      {/* Subtle engineering grid lines */}
      <div className="absolute top-0 left-[20%] w-px h-full bg-gradient-to-b from-transparent via-white/[0.02] to-transparent pointer-events-none" />
      <div className="absolute top-0 left-[80%] w-px h-full bg-gradient-to-b from-transparent via-white/[0.02] to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <PremiumSectionHeader
          overline="Industries"
          title="Sectors We"
          titleAccent="Serve"
          description={`Tailored engineering expertise across ${ALL_INDUSTRIES.length} industry sectors, delivering precision solutions from mining operations to residential infrastructure.`}
        />

        {/* 4 Featured Industry Showcase Cards — 9-layer architecture */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {featured.map((industry, index) => (
            <IndustryShowcaseCard
              key={industry.slug}
              industry={industry}
              index={index}
              onClick={() => navigate(`/industries/${industry.slug}`)}
            />
          ))}
        </div>

        {/* Remaining Industries — Premium Badge Row */}
        <motion.div
          className="mt-16 lg:mt-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Architectural divider */}
          <div className="flex items-center gap-6 mb-10">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
            <span className="text-white/30 text-[11px] font-semibold uppercase tracking-[0.3em]">Also Serving</span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {remaining.map((ind, i) => (
              <motion.div
                key={ind.slug}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 + i * 0.06, duration: 0.6 }}
              >
                <Link
                  to={`/industries/${ind.slug}`}
                  className="group/badge inline-flex items-center gap-2.5 px-6 py-3 relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.025) 0%, rgba(255,255,255,0.01) 100%)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.02)',
                    transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                  data-testid={`industry-link-${ind.slug}`}
                >
                  <span className="relative z-10 text-white/55 text-[13px] font-semibold uppercase tracking-[0.15em] group-hover/badge:text-white/90 transition-colors duration-500">{ind.title}</span>
                  <ArrowRight size={10} className="relative z-10 text-white/20 group-hover/badge:text-[#8B2332] group-hover/badge:translate-x-0.5 transition-all duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#8B2332]/10 to-transparent opacity-0 group-hover/badge:opacity-100 transition-opacity duration-500" />
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Section transition */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#121318] via-[#121318]/60 to-transparent pointer-events-none" />
    </section>
  );
};

// Why Choose Us — Executive Showcase with Engineered Feature Cards
const WhyChooseUs = () => {
  const reasons = [
    { title: "OEM Standard Workmanship", desc: "All work adheres to original equipment manufacturer specifications and technical standards", icon: Shield },
    { title: "Since 1999", desc: "Decades of specialist engineering experience across mine-winder, electrical and mechanical disciplines", icon: Award },
    { title: "Precision Engineering", desc: "Disciplined technical accuracy from initial inspection through to final testing and commissioning", icon: Settings },
    { title: "Full-Service Capability", desc: "End-to-end solutions from audit and diagnosis through repair, refurbishment and project completion", icon: Wrench },
    { title: "Africa-Wide Operations", desc: "Engineering project capabilities across South African mining regions and the African continent", icon: Globe },
    { title: "Emergency Support", desc: "Rapid deployment support for critical equipment failures and operational emergencies", icon: Clock }
  ];

  return (
    <section className="relative py-20 lg:py-24 overflow-hidden" data-testid="why-choose-us">
      {/* Background */}
      <div className="absolute inset-0 bg-[#121318]" />

      {/* Top transition */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#121318] to-transparent" />

      {/* Cinematic accent lighting */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none" style={{
        background: 'radial-gradient(ellipse 50% 40% at 10% 30%, rgba(139,35,50,0.05) 0%, transparent 60%)'
      }} />
      <div className="absolute top-0 right-0 w-full h-full pointer-events-none" style={{
        background: 'radial-gradient(ellipse 40% 50% at 90% 70%, rgba(51,51,163,0.025) 0%, transparent 60%)'
      }} />

      {/* Structural grid lines */}
      <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-white/[0.025] to-transparent pointer-events-none" />
      <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-white/[0.025] to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          {/* Left Column — Executive Content Panel */}
          <motion.div
            className="group/diff cursor-default"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Overline */}
            <div className="mb-10">
              <span className="inline-flex items-center gap-5">
                <motion.span
                  className="h-[2px] bg-gradient-to-r from-[#8B2332] to-transparent transition-all duration-[350ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/diff:to-[#a63d50]"
                  initial={{ width: 0 }}
                  whileInView={{ width: 60 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  style={{ transition: 'width 350ms cubic-bezier(0.22,0.61,0.36,1)' }}
                />
                <span className="text-[#8B2332] text-[13px] lg:text-[14px] font-bold uppercase tracking-[0.35em] transition-all duration-[300ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/diff:text-[#a63d50] group-hover/diff:tracking-[0.38em]">The WWS Difference</span>
              </span>
            </div>

            {/* Headline */}
            <motion.h2
              className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold uppercase text-white mb-14 transition-all duration-[350ms] group-hover/diff:text-white"
              style={{ lineHeight: '0.92', letterSpacing: '-0.01em' }}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              Excellence in{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B2332] to-[#c44b5e] transition-all duration-[350ms] group-hover/diff:from-[#a63d50] group-hover/diff:to-[#d46b7c]" style={{ filter: 'drop-shadow(0 0 20px rgba(139,35,50,0.2))' }}>
                Every
              </span>{' '}
              Project
            </motion.h2>

            {/* Editorial Description — Interactive Glass Panel */}
            <motion.div
              className="group/ep cursor-default"
              initial={{ opacity: 0, y: 20, scale: 0.99 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.35, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -8, transition: { duration: 0.45, ease: [0.22, 0.61, 0.36, 1] } }}
            >
              <div className="relative overflow-hidden mb-14">
                <div className="absolute inset-0 transition-all duration-[450ms]" style={{
                  background: 'linear-gradient(160deg, rgba(18,19,24,0.92) 0%, rgba(12,13,17,0.96) 100%)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05), inset 0 0 0 1px rgba(255,255,255,0.025), 0 20px 60px rgba(0,0,0,0.35), 0 4px 16px rgba(0,0,0,0.2)',
                }} />
                <div className="absolute inset-0 border border-transparent group-hover/ep:border-[#8B2332]/15 transition-colors duration-[450ms] pointer-events-none" />
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.03] to-transparent" />
                <div className="absolute top-6 bottom-6 left-0 w-[2px] bg-gradient-to-b from-[#8B2332] via-[#8B2332]/40 to-transparent group-hover/ep:from-[#8B2332] group-hover/ep:via-[#8B2332]/60 transition-all duration-[450ms]" />
                <div className="absolute top-0 left-0 w-10 h-full bg-gradient-to-r from-[#8B2332]/[0.04] to-transparent pointer-events-none group-hover/ep:from-[#8B2332]/[0.06] transition-all duration-[450ms]" />
                {/* Corner brackets */}
                <div className="absolute top-2.5 right-2.5 w-5 h-5 border-t border-r border-white/[0.05] group-hover/ep:border-white/[0.14] group-hover/ep:w-7 group-hover/ep:h-7 transition-all duration-[450ms] pointer-events-none" />
                <div className="absolute bottom-2.5 left-2.5 w-4 h-4 border-b border-l border-white/[0.03] group-hover/ep:border-[#8B2332]/20 group-hover/ep:w-6 group-hover/ep:h-6 transition-all duration-[450ms] pointer-events-none" />
                {/* Ambient glow beneath */}
                <div className="absolute -bottom-4 left-[10%] right-[10%] h-12 bg-gradient-to-t from-[#8B2332]/[0.06] to-transparent opacity-0 group-hover/ep:opacity-100 transition-opacity duration-[450ms] pointer-events-none" />
                {/* Light sweep */}
                <div className="absolute inset-0 -translate-x-full group-hover/ep:translate-x-full transition-transform duration-[1200ms] ease-out bg-gradient-to-r from-transparent via-white/[0.015] to-transparent pointer-events-none" />
                <div className="relative z-10 p-8 lg:p-10">
                  <p className="text-white/70 text-[15px] lg:text-[17px] leading-[2.15] group-hover/ep:text-white/80 transition-colors duration-[450ms]" style={{ letterSpacing: '0.015em' }}>
                    WWS combines hands-on{' '}
                    <span className="text-white/95 font-semibold group-hover/ep:text-white transition-colors duration-[450ms]">field experience</span>{' '}
                    with disciplined{' '}
                    <span className="text-white/95 font-semibold group-hover/ep:text-white transition-colors duration-[450ms]">technical standards</span>{' '}
                    across mine-winder, electrical and mechanical work. From{' '}
                    <span className="text-white/95 font-semibold group-hover/ep:text-white transition-colors duration-[450ms]">inspection and testing</span>{' '}
                    to refurbishment and repairs, every project is approached with{' '}
                    <span className="text-white/95 font-semibold group-hover/ep:text-white transition-colors duration-[450ms]">safety</span>,{' '}
                    accuracy and long-term operational reliability in mind.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Stat Highlight — Premium KPI Panel */}
            <motion.div
              className="group/kpi relative overflow-hidden cursor-default"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -10, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } }}
            >
              <div className="absolute inset-0 transition-all duration-500" style={{
                background: 'linear-gradient(135deg, rgba(10,11,13,0.9) 0%, rgba(18,19,24,0.85) 100%)',
                border: '1px solid rgba(255,255,255,0.06)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 20px 60px rgba(0,0,0,0.3)',
              }} />
              <div className="absolute inset-0 border border-transparent group-hover/kpi:border-[#8B2332]/20 transition-colors duration-500 pointer-events-none" />
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
              {/* Top accent line */}
              <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden pointer-events-none">
                <div className="h-full w-0 group-hover/kpi:w-full bg-gradient-to-r from-[#8B2332] via-[#d45567]/50 to-transparent transition-all duration-700 ease-out" />
              </div>
              {/* Corner brackets */}
              <div className="absolute top-3 right-3 w-10 h-10 border-t border-r border-[#8B2332]/20 group-hover/kpi:border-[#8B2332]/50 group-hover/kpi:w-14 group-hover/kpi:h-14 transition-all duration-500 pointer-events-none" />
              <div className="absolute bottom-3 left-3 w-8 h-8 border-b border-l border-white/[0.05] group-hover/kpi:border-[#8B2332]/25 group-hover/kpi:w-10 group-hover/kpi:h-10 transition-all duration-500 pointer-events-none" />
              {/* Ambient glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#8B2332]/[0.04] to-transparent opacity-0 group-hover/kpi:opacity-100 transition-opacity duration-700 pointer-events-none" />
              {/* Light sweep */}
              <div className="absolute inset-0 -translate-x-full group-hover/kpi:translate-x-full transition-transform duration-[1000ms] ease-out bg-gradient-to-r from-transparent via-white/[0.03] to-transparent pointer-events-none" />
              {/* Bottom red glow */}
              <div className="absolute -bottom-4 left-[10%] right-[10%] h-14 bg-gradient-to-t from-[#8B2332]/[0.08] to-transparent opacity-0 group-hover/kpi:opacity-100 transition-opacity duration-500 pointer-events-none" />

              <div className="relative z-10 p-10 lg:p-12 flex items-end gap-6">
                <span className="font-['Barlow_Condensed'] text-7xl lg:text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#8B2332] to-[#c44b5e] group-hover/kpi:scale-[1.02] transition-transform duration-500 origin-left" style={{ filter: 'drop-shadow(0 0 15px rgba(139,35,50,0.15))' }}>
                  200+
                </span>
                <span className="text-white/55 text-[15px] uppercase tracking-[0.15em] pb-3 leading-tight font-semibold group-hover/kpi:text-white/70 transition-colors duration-500">
                  Projects<br/>Completed
                </span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column — Engineered Feature Cards */}
          <div className="grid sm:grid-cols-2 gap-5 lg:gap-6">
            {reasons.map((reason, index) => (
              <motion.div
                key={reason.title}
                className="group relative overflow-hidden"
                style={{
                  background: 'linear-gradient(150deg, rgba(18,19,24,0.95) 0%, rgba(10,11,13,0.98) 100%)',
                  border: '1px solid rgba(255,255,255,0.05)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03), inset 0 0 0 1px rgba(255,255,255,0.02), 0 8px 32px rgba(0,0,0,0.3)',
                  transition: 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.6s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
                initial={{ opacity: 0, y: 50, x: 30 }}
                whileInView={{ opacity: 1, y: 0, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.8, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{
                  y: -6,
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), inset 0 0 0 1px rgba(139,35,50,0.06), 0 30px 60px rgba(0,0,0,0.45), 0 0 40px rgba(139,35,50,0.05)',
                  borderColor: 'rgba(139,35,50,0.2)',
                  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
                }}
                data-testid={`why-card-${index}`}
              >
                {/* Top accent line */}
                <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden">
                  <div className="h-full w-0 group-hover:w-full bg-gradient-to-r from-[#8B2332] via-[#d45567]/50 to-transparent transition-all duration-700 ease-out" />
                </div>

                {/* Left accent line */}
                <div className="absolute top-0 left-0 w-[2px] overflow-hidden">
                  <div className="w-full h-0 group-hover:h-16 bg-gradient-to-b from-[#8B2332] to-transparent transition-all duration-700 ease-out" />
                </div>

                {/* Card content */}
                <div className="relative p-7 lg:p-8 z-10">
                  {/* Icon containment with double-border effect */}
                  <div className="relative w-14 h-14 mb-7 flex items-center justify-center">
                    <div className="absolute inset-0 bg-[#8B2332]/[0.08] border border-[#8B2332]/20 group-hover:bg-[#8B2332]/[0.12] group-hover:border-[#8B2332]/35 transition-all duration-500" />
                    <div className="absolute -inset-1.5 border border-[#8B2332]/[0.06] opacity-0 group-hover:opacity-100 transition-all duration-500" />
                    <div className="absolute -inset-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{ boxShadow: '0 0 25px rgba(139,35,50,0.15)' }} />
                    <reason.icon className="w-6 h-6 text-[#8B2332] group-hover:text-[#d45567] group-hover:rotate-[4deg] group-hover:scale-110 transition-all duration-500 relative z-10" strokeWidth={1.5} />
                  </div>

                  {/* Title — premium typography */}
                  <h3 className="font-['Barlow_Condensed'] text-lg lg:text-xl font-bold uppercase text-white mb-3 group-hover:text-[#d45567] group-hover:-translate-y-0.5 transition-all duration-500" style={{ letterSpacing: '0.03em' }}>
                    {reason.title}
                  </h3>

                  {/* Description — elevated readability */}
                  <p className="text-white/55 text-[15px] leading-[2.1] group-hover:text-white/75 transition-colors duration-500" style={{ letterSpacing: '0.01em' }}>
                    {reason.desc}
                  </p>
                </div>

                {/* Bottom ambient glow */}
                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#8B2332]/[0.06] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                {/* Bottom-right corner bracket */}
                <div className="absolute bottom-3 right-3 w-6 h-6 border-b border-r border-white/[0.06] group-hover:border-[#8B2332]/25 group-hover:w-9 group-hover:h-9 transition-all duration-500 pointer-events-none" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Section transition */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/50 to-transparent pointer-events-none" />
    </section>
  );
};

// =============================================
// MINE WINDER FLAGSHIP EXPERTISE SECTION
// =============================================
const MineWinderExpertise = () => {
  const capabilities = [
    { label: "Inspections", icon: Shield },
    { label: "Maintenance", icon: Wrench },
    { label: "Repairs", icon: Settings },
    { label: "Refurbishments", icon: Cog },
    { label: "Testing", icon: Gauge },
    { label: "Audits", icon: Check },
  ];

  return (
    <section className="relative py-20 lg:py-24 overflow-hidden" data-testid="mine-winder-expertise">
      <div className="absolute inset-0 bg-[#08090B]" />
      <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} />
      <div className="absolute top-0 right-0 w-1/2 h-full pointer-events-none" style={{
        background: 'radial-gradient(ellipse 70% 60% at 100% 30%, rgba(139,35,50,0.06) 0%, transparent 60%)'
      }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          {/* Image side */}
          <motion.div
            className="relative order-2 lg:order-1"
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <EngineeredImageFrame
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/oo6gfwn5_Our%20Capabulities%20-%20Mine%20Winder%20Expertise.png"
              alt="Mine winder engineering and maintenance expertise - Welkom Winder Services"
              aspectRatio="4/3"
            />
          </motion.div>

          {/* Content side */}
          <motion.div
            className="order-1 lg:order-2 group/winder cursor-default"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="mb-6">
              <span className="inline-flex items-center gap-4">
                <motion.span className="h-[2px] bg-gradient-to-r from-[#8B2332] to-transparent transition-all duration-[350ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/winder:to-[#a63d50]" initial={{ width: 0 }} whileInView={{ width: 60 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} style={{ transition: 'width 350ms cubic-bezier(0.22,0.61,0.36,1)' }} />
                <span className="text-[#8B2332] text-[13px] lg:text-[14px] font-bold uppercase tracking-[0.35em] transition-all duration-[300ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/winder:text-[#a63d50] group-hover/winder:tracking-[0.38em]">Our Core Expertise</span>
              </span>
            </div>

            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-bold uppercase text-white mb-3 leading-[0.92] transition-all duration-[350ms] group-hover/winder:text-white">
              Mine Winders.
            </h2>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-bold uppercase leading-[0.92] mb-8">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B2332] to-[#c44b5e] transition-all duration-[350ms] group-hover/winder:from-[#a63d50] group-hover/winder:to-[#d46b7c]">Engineered</span>{' '}
              <span className="text-white">for Reliability.</span>
            </h2>

            <p className="text-white/60 text-base lg:text-[17px] leading-[2] max-w-xl mb-10">
              Mine winder systems are central to the identity and heritage of Welkom Winder Services. We provide specialist lifecycle support including inspections, maintenance, repairs, refurbishments, dynamic brake testing and OEM-standard technical work for mine winder equipment across South Africa.
            </p>

            {/* Technical competency grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-10">
              {capabilities.map((cap, i) => (
                <motion.div
                  key={cap.label}
                  className="group/cap relative overflow-hidden p-4 cursor-default"
                  style={{
                    background: 'linear-gradient(150deg, rgba(18,19,24,0.9) 0%, rgba(10,11,13,0.95) 100%)',
                    border: '1px solid rgba(255,255,255,0.05)',
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.5 }}
                  whileHover={{ y: -3, borderColor: 'rgba(139,35,50,0.25)', transition: { duration: 0.3 } }}
                  data-testid={`winder-cap-${i}`}
                >
                  <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-[#8B2332]/30 to-transparent opacity-0 group-hover/cap:opacity-100 transition-opacity duration-400" />
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#8B2332]/40 group-hover/cap:bg-[#8B2332] transition-colors duration-400 flex-shrink-0" />
                    <span className="text-white/60 text-[13px] font-semibold uppercase tracking-[0.1em] group-hover/cap:text-white/85 transition-colors duration-400">{cap.label}</span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <Link
              to="/services#mine-winder-services"
              className="group inline-flex items-center gap-4"
              data-testid="winder-cta-link"
            >
              <span className="relative">
                <span className="font-bold uppercase text-sm tracking-[0.2em] text-white group-hover:text-[#8B2332] transition-colors duration-500">Explore Mine Winder Services</span>
                <span className="absolute -bottom-2 left-0 w-full h-[1px] bg-white/15 group-hover:bg-[#8B2332]/50 transition-colors duration-500" />
              </span>
              <motion.div
                className="w-10 h-10 border border-white/15 group-hover:border-[#8B2332]/50 group-hover:bg-[#8B2332]/10 flex items-center justify-center transition-all duration-500"
                whileHover={{ x: 5 }}
              >
                <ArrowRight size={16} className="text-white/50 group-hover:text-[#8B2332] transition-colors duration-500" />
              </motion.div>
            </Link>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#121318] to-transparent pointer-events-none" />
    </section>
  );
};

// =============================================
// SELECTED PROJECTS SECTION — REAL PROJECTS ONLY
// =============================================
const SelectedProjects = () => {
  const projects = [
    {
      title: "Pump Starter Maintenance",
      client: "Harmony Central Plant",
      category: "Electrical Systems",
      industry: "Mining",
      location: "Free State, South Africa",
      duration: "2000 — Present",
      type: "Active Contract",
      desc: "Continuous maintenance, repair, replacement and engineering support for 5.5kW and 7.5kW pump starter systems. Uninterrupted service since 2000.",
      image: "https://customer-assets.emergentagent.com/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/v908p0xt_IMG-20260709-WA0002.jpg",
      slug: "mining/harmony-central-plant-pump-starters",
    },
    {
      title: "Bearing Temperature Monitoring",
      client: "Harmony Target 3 Shaft",
      category: "Condition Monitoring",
      industry: "Mining",
      location: "Free State, South Africa",
      duration: "May 2011",
      type: "Completed",
      desc: "Design, installation and commissioning of mine winder bearing temperature monitoring system with RTD/Pt100 sensors, alarm configuration and automatic shutdown logic.",
      image: "https://customer-assets.emergentagent.com/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/e22vz5ql_IMG-20260709-WA0004.jpg",
      slug: "mining/harmony-target3-temperature-monitoring",
    },
  ];

  return (
    <section className="relative py-20 lg:py-24 overflow-hidden" data-testid="selected-projects">
      <div className="absolute inset-0 bg-[#0A0B0D]" />
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none" style={{
        background: 'radial-gradient(ellipse 50% 40% at 50% 20%, rgba(139,35,50,0.04) 0%, transparent 60%)'
      }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <PremiumSectionHeader
          overline="Proven in the Field"
          title="Selected"
          titleAccent="Projects"
          description="Real engineering work delivered with precision, safety and OEM-standard workmanship."
        />

        <div className="grid md:grid-cols-2 gap-7 lg:gap-8 max-w-5xl mx-auto">
          {projects.map((project, index) => (
            <Link
              key={project.title}
              to={`/industries/mining#projects`}
              className="group relative overflow-hidden cursor-pointer block"
              style={{ boxShadow: '0 6px 30px rgba(0,0,0,0.35)', textDecoration: 'none' }}
              data-testid={`project-card-home-${index}`}
            >
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: index * 0.15, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -8, boxShadow: '0 25px 60px rgba(139,35,50,0.15), 0 8px 30px rgba(0,0,0,0.5)', transition: { duration: 0.4 } }}
              >
              {/* Project image */}
              <div className="relative h-56 overflow-hidden">
                <OptimizedImage
                  src={project.image}
                  alt={`${project.title} - ${project.client}`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.025]"
                  style={{ filter: 'brightness(0.8) contrast(1.1)' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/30 to-transparent" />
                {/* Type badge */}
                <div className="absolute top-4 left-4 z-10">
                  <span className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] ${project.type === 'Active Contract' ? 'bg-[#8B2332]/80 text-white' : 'bg-white/10 text-white/80 backdrop-blur-sm'}`}>
                    {project.type}
                  </span>
                </div>
              </div>

              {/* Card border */}
              <div className="absolute inset-0 border border-white/[0.06] group-hover:border-[#8B2332]/20 transition-colors duration-500" />
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#8B2332]/20 to-transparent group-hover:via-[#8B2332]/60 transition-all duration-500" />
              <div className="absolute top-0 left-0 w-[2px] h-0 group-hover:h-full bg-gradient-to-b from-[#8B2332] via-[#8B2332]/30 to-transparent transition-all duration-700" />

              {/* Content */}
              <div className="relative bg-[#0A0B0D] p-6 lg:p-7">
                <p className="text-[#8B2332] text-[10px] font-bold uppercase tracking-[0.2em] mb-2">{project.category} — {project.industry}</p>
                <h3 className="font-['Barlow_Condensed'] text-xl lg:text-2xl font-bold uppercase text-white mb-1 group-hover:text-[#d45567] transition-colors duration-500">{project.title}</h3>
                <p className="text-white/40 text-[12px] font-semibold uppercase tracking-[0.1em] mb-3">{project.client} · {project.location}</p>
                <p className="text-white/50 text-[14px] leading-[1.9] mb-4">{project.desc}</p>

                <div className="flex items-center justify-between pt-4 border-t border-white/[0.05] group-hover:border-[#8B2332]/20 transition-colors duration-500">
                  <span className="text-white/30 text-[11px] uppercase tracking-[0.1em]">{project.duration}</span>
                  <span className="inline-flex items-center gap-2 text-[#8B2332]/70 group-hover:text-[#d45567] transition-colors duration-500">
                    <span className="text-[12px] font-bold uppercase tracking-[0.15em]">View Project</span>
                    <ArrowRight size={12} className="transform group-hover:translate-x-1.5 transition-transform duration-500" />
                  </span>
                </div>
              </div>
            </motion.div>
            </Link>
          ))}
        </div>

        {/* View All Projects CTA */}
        <motion.div
          className="text-center mt-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <Link
            to="/industries/mining#projects"
            className="group relative inline-flex items-center gap-4 bg-white/[0.03] border border-white/10 text-white font-bold px-10 py-5 overflow-hidden transition-all duration-500 hover:border-white/25 hover:bg-white/[0.06] hover:-translate-y-1"
            data-testid="view-all-projects"
          >
            <span className="relative z-10 tracking-[0.2em] uppercase text-[13px]">View Industry Projects</span>
            <ArrowRight size={16} className="relative z-10 transform group-hover:translate-x-1.5 transition-transform duration-300" />
          </Link>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#121318] to-transparent pointer-events-none" />
    </section>
  );
};

// =============================================
// LEON SCHOEMAN LEADERSHIP TEASER — PREMIUM EXECUTIVE MODULE
// =============================================
const LeadershipTeaser = () => {
  return (
    <section className="relative py-20 lg:py-24 overflow-hidden" data-testid="leadership-teaser">
      <div className="absolute inset-0 bg-[#121318]" />
      <div className="absolute top-0 left-0 w-1/3 h-full pointer-events-none" style={{
        background: 'radial-gradient(ellipse 80% 60% at 0% 50%, rgba(139,35,50,0.05) 0%, transparent 60%)'
      }} />
      {/* Subtle engineering grid */}
      <div className="absolute inset-0 opacity-[0.008]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="group/leon grid lg:grid-cols-2 gap-8 lg:gap-0 items-stretch max-w-5xl mx-auto cursor-default">
          
          {/* Portrait — left side with premium executive frame */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="relative group/portrait h-full" style={{ minHeight: '420px' }}>
              {/* Outer technical frame */}
              <div className="absolute inset-0 border border-white/[0.06] group-hover/leon:border-white/[0.10] transition-colors duration-[400ms] z-10 pointer-events-none" />
              {/* Top red accent line */}
              <div className="absolute top-0 left-0 right-0 h-[2px] z-20 pointer-events-none overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#8B2332] via-[#8B2332]/60 to-transparent w-[40%] group-hover/leon:w-full transition-all duration-[500ms] ease-[cubic-bezier(0.22,0.61,0.36,1)]" />
              </div>
              {/* Left red accent */}
              <div className="absolute top-0 left-0 w-[2px] z-20 pointer-events-none overflow-hidden">
                <div className="w-full bg-gradient-to-b from-[#8B2332] to-transparent h-[60px] group-hover/leon:h-[100px] transition-all duration-[500ms]" />
              </div>
              {/* Inner subtle border */}
              <div className="absolute inset-[6px] border border-white/[0.03] group-hover/leon:border-white/[0.06] transition-colors duration-[400ms] z-10 pointer-events-none" />
              
              {/* Corner brackets */}
              <div className="absolute top-3 right-3 w-7 h-7 border-t border-r border-white/[0.06] group-hover/leon:border-white/[0.14] group-hover/leon:w-10 group-hover/leon:h-10 transition-all duration-[400ms] z-20 pointer-events-none" />
              <div className="absolute bottom-3 left-3 w-6 h-6 border-b border-l border-[#8B2332]/15 group-hover/leon:border-[#8B2332]/35 group-hover/leon:w-9 group-hover/leon:h-9 transition-all duration-[400ms] z-20 pointer-events-none" />
              <div className="absolute bottom-3 right-3 w-5 h-5 border-b border-r border-white/[0.04] group-hover/leon:border-white/[0.08] transition-all duration-[400ms] z-20 pointer-events-none" />
              
              {/* Portrait image */}
              <div className="absolute inset-0 overflow-hidden">
                <OptimizedImage
                  src="https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/x9lcwy5l_Welkom%20Winder%20Service.png"
                  alt="Leon Schoeman – Director and Owner of Welkom Winder Services (Pty) Ltd"
                  className="w-full h-full object-cover transition-transform duration-[600ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/portrait:scale-[1.012]"
                  style={{ objectPosition: 'center 20%' }}
                />
                {/* Subtle bottom vignette */}
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#121318]/40 to-transparent pointer-events-none" />
              </div>
              
              {/* Shadow / depth */}
              <div className="absolute -inset-1 -z-10 opacity-60 group-hover/leon:opacity-80 transition-opacity duration-[400ms]" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} />
            </div>
          </motion.div>

          {/* Panel left border acts as visual connector on desktop */}

          {/* Premium Director Content Panel — right side */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="group/panel relative overflow-hidden h-full transition-transform duration-[400ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/leon:-translate-y-[2px]" style={{
              background: 'linear-gradient(155deg, rgba(18,19,24,0.95) 0%, rgba(14,15,18,0.98) 100%)',
              border: '1px solid rgba(255,255,255,0.05)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03), 0 15px 50px rgba(0,0,0,0.35)',
            }}>
              {/* Top accent line */}
              <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden pointer-events-none">
                <div className="h-full bg-gradient-to-r from-transparent via-[#8B2332]/30 to-transparent w-0 group-hover/leon:w-full transition-all duration-[500ms] ease-[cubic-bezier(0.22,0.61,0.36,1)]" />
              </div>
              {/* Left red edge */}
              <div className="absolute top-8 bottom-8 left-0 w-[2px] bg-gradient-to-b from-[#8B2332]/40 via-[#8B2332]/15 to-transparent group-hover/leon:from-[#8B2332]/60 group-hover/leon:via-[#8B2332]/30 transition-all duration-[400ms]" />
              {/* Corner brackets */}
              <div className="absolute top-4 right-4 w-6 h-6 border-t border-r border-white/[0.04] group-hover/leon:border-white/[0.10] group-hover/leon:w-8 group-hover/leon:h-8 transition-all duration-[400ms] pointer-events-none" />
              <div className="absolute bottom-4 left-4 w-5 h-5 border-b border-l border-white/[0.03] group-hover/leon:border-[#8B2332]/15 transition-all duration-[400ms] pointer-events-none" />
              {/* Faint engineering grid */}
              <div className="absolute inset-0 opacity-[0.008] group-hover/leon:opacity-[0.015] transition-opacity duration-[500ms] pointer-events-none" style={{
                backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
                backgroundSize: '40px 40px'
              }} />

              <div className="relative z-10 p-8 lg:p-10 xl:p-12 flex flex-col justify-center h-full">
                {/* Eyebrow */}
                <div className="mb-6">
                  <span className="inline-flex items-center gap-4">
                    <motion.span 
                      className="h-[2px] bg-gradient-to-r from-[#8B2332] to-transparent transition-all duration-[350ms] group-hover/leon:to-[#a63d50]" 
                      initial={{ width: 0 }} 
                      whileInView={{ width: 45 }} 
                      viewport={{ once: true }} 
                      transition={{ duration: 0.5, delay: 0.3 }}
                      style={{ transition: 'width 350ms cubic-bezier(0.22,0.61,0.36,1)' }}
                    />
                    <span className="text-[#8B2332] text-[13px] lg:text-[14px] font-bold uppercase tracking-[0.35em] transition-all duration-[300ms] group-hover/leon:text-[#a63d50] group-hover/leon:tracking-[0.38em]">Led by Experience</span>
                  </span>
                </div>

                {/* Name */}
                <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-[3.5rem] font-bold uppercase text-white mb-2 leading-[0.92] transition-all duration-[350ms] group-hover/leon:text-white" data-testid="leadership-name">
                  Leon Schoeman
                </h2>
                <p className="font-['Barlow_Condensed'] text-base lg:text-lg font-semibold uppercase text-white/40 tracking-[0.08em] mb-8 group-hover/leon:text-white/50 transition-colors duration-[350ms]">
                  Director & Owner
                </p>

                {/* Bio */}
                <p className="text-white/55 text-[15px] lg:text-base leading-[2] max-w-lg mb-10">
                  More than four decades of hands-on experience across the electrical, mining and mine-winder industries. From apprentice electrician in 1982 to Director-Owner and Senior Winder Inspector today.
                </p>

                {/* CTA */}
                <Link
                  to="/about#leadership"
                  className="group/cta inline-flex items-center gap-4"
                  data-testid="leadership-cta-link"
                >
                  <span className="relative">
                    <span className="font-bold uppercase text-sm tracking-[0.2em] text-white group-hover/cta:text-[#8B2332] transition-colors duration-500">Meet Our Director</span>
                    <span className="absolute -bottom-2 left-0 w-full h-[1px] bg-white/15 group-hover/cta:bg-[#8B2332]/50 transition-colors duration-500" />
                    <span className="absolute -bottom-2 left-0 h-[1px] bg-[#8B2332] w-0 group-hover/cta:w-full transition-all duration-[400ms] ease-[cubic-bezier(0.22,0.61,0.36,1)]" />
                  </span>
                  <div className="w-10 h-10 border border-white/15 group-hover/cta:border-[#8B2332]/50 group-hover/cta:bg-[#8B2332]/10 flex items-center justify-center transition-all duration-500">
                    <ArrowRight size={16} className="text-white/50 group-hover/cta:text-[#8B2332] group-hover/cta:translate-x-[3px] transition-all duration-500" />
                  </div>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0A0B0D] to-transparent pointer-events-none" />
    </section>
  );
};

// =============================================
// HOME PAGE MAP / LOCATION SECTION
// =============================================
const HomeLocation = () => {
  return (
    <section className="relative py-16 lg:py-20 overflow-hidden bg-[#050506]" data-testid="home-location">
      <div className="absolute inset-0 blueprint-texture opacity-30" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <motion.div
          className="flex items-center gap-4 mb-3"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <MapPin size={14} className="text-[#8B2332]" />
          <span className="text-[#8B2332] text-[10px] font-bold uppercase tracking-[0.35em]">Welkom Headquarters</span>
          <div className="flex-1 h-px bg-gradient-to-r from-[#8B2332]/20 to-transparent" />
        </motion.div>
        <motion.p
          className="font-['Barlow_Condensed'] text-lg font-semibold uppercase text-white/70 tracking-[0.05em] mb-1"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1, duration: 0.5 }}
        >
          Welkom Winder Services (Pty) Ltd
        </motion.p>
        <motion.p
          className="text-white/40 text-sm mb-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15, duration: 0.5 }}
        >
          161 Power Rd, Voorspoed, Welkom, 9459
        </motion.p>

        <motion.div
          className="group/map relative overflow-hidden rounded-sm"
          style={{ height: '280px', boxShadow: '0 15px 50px rgba(0,0,0,0.6)' }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="absolute -inset-1 z-0" style={{
            background: 'linear-gradient(145deg, rgba(18,19,24,0.8) 0%, rgba(12,13,17,0.9) 100%)',
            border: '1px solid rgba(255,255,255,0.05)',
          }} />
          <div className="absolute top-2 right-2 w-8 h-8 border-t border-r border-white/[0.06] group-hover/map:border-white/[0.14] transition-all duration-500 pointer-events-none z-20" />
          <div className="absolute bottom-2 left-2 w-6 h-6 border-b border-l border-white/[0.04] group-hover/map:border-[#8B2332]/20 transition-all duration-500 pointer-events-none z-20" />
          <div className="absolute inset-0 border border-white/[0.05] group-hover/map:border-white/[0.1] transition-colors duration-500 z-10 pointer-events-none" />
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#8B2332]/25 to-transparent z-10 pointer-events-none" />

          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3530.5!2d26.735!3d-27.985!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1e9347c0a3ed00bb%3A0x6e8e5c8b68c90b0!2s161+Power+Rd%2C+Voorspoed%2C+Welkom%2C+9459%2C+South+Africa!5e0!3m2!1sen!2sza!4v1700000000000!5m2!1sen!2sza"
            className="w-full h-full border-0 relative z-[1]"
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Welkom Winder Services Headquarters Location"
            style={{ filter: 'invert(0.92) hue-rotate(180deg) saturate(0.3) contrast(1.1) brightness(0.65)' }}
          />

          <div className="absolute bottom-3 left-3 z-20 pointer-events-auto">
            <div className="relative overflow-hidden" style={{
              background: 'linear-gradient(160deg, rgba(10,11,13,0.92) 0%, rgba(14,15,18,0.95) 100%)',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)',
            }}>
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#8B2332]/60 via-[#8B2332]/20 to-transparent" />
              <div className="p-3 lg:p-4">
                <div className="flex gap-2">
                  <a href="tel:+27573554929" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#8B2332]/10 border border-[#8B2332]/20 text-[#8B2332] text-[10px] font-bold uppercase tracking-wider hover:bg-[#8B2332]/20 transition-all duration-300"><Phone size={10} />Call</a>
                  <a href="https://maps.google.com/?q=161+Power+Rd+Welkom" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.03] border border-white/[0.08] text-white/50 text-[10px] font-bold uppercase tracking-wider hover:text-white/80 hover:border-white/[0.15] transition-all duration-300"><MapPin size={10} />Get Directions</a>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#050506] to-transparent pointer-events-none z-10" />
          <div className="absolute top-0 left-0 bottom-0 w-20 bg-gradient-to-r from-[#050506] to-transparent pointer-events-none z-10" />
          <div className="absolute top-0 right-0 bottom-0 w-20 bg-gradient-to-l from-[#050506] to-transparent pointer-events-none z-10" />
        </motion.div>
      </div>
    </section>
  );
};

// CTA Section - Premium Pre-Footer Closing Experience
const CTASection = () => {
  return (
    <section className="relative py-24 lg:py-32 overflow-hidden" data-testid="cta-section">
      {/* Background image */}
      <div className="absolute inset-0">
        <OptimizedImage src={IMAGES.mining} alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.12) contrast(1.3) saturate(0.4)' }} />
      </div>
      
      {/* Deep cinematic overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0B0D] via-[#0A0B0D]/70 to-[#050506]" />
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 60% 50% at 50% 45%, rgba(139,35,50,0.15) 0%, transparent 65%)'
      }} />
      
      {/* Slow light sweep */}
      <motion.div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(110deg, transparent 0%, transparent 35%, rgba(255,255,255,0.015) 45%, rgba(255,255,255,0.03) 50%, rgba(255,255,255,0.015) 55%, transparent 65%, transparent 100%)',
          backgroundSize: '250% 100%'
        }}
        animate={{ backgroundPosition: ['-100% 0%', '200% 0%'] }}
        transition={{ duration: 10, repeat: Infinity, repeatDelay: 4, ease: "easeInOut" }}
      />
      
      {/* Top decorative line */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#8B2332]/30 to-transparent" />
      
      <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Overline */}
          <motion.div 
            className="flex items-center justify-center gap-5 mb-10"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <motion.span 
              className="h-[1px] bg-gradient-to-r from-transparent to-[#8B2332]/60"
              initial={{ width: 0 }}
              whileInView={{ width: 50 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            />
            <span className="text-[#8B2332] text-[11px] font-bold uppercase tracking-[0.4em]">Let&apos;s Build Together</span>
            <motion.span 
              className="h-[1px] bg-gradient-to-l from-transparent to-[#8B2332]/60"
              initial={{ width: 0 }}
              whileInView={{ width: 50 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            />
          </motion.div>
          
          {/* Bold headline */}
          <motion.h2 
            className="font-['Barlow_Condensed'] text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black uppercase text-white mb-8 leading-[0.9]"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            style={{ textShadow: '0 4px 40px rgba(0,0,0,0.5)' }}
          >
            Ready to Power Your<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B2332] via-[#d45567] to-[#8B2332]" style={{ filter: 'drop-shadow(0 0 30px rgba(139,35,50,0.4))' }}>
              Next Project?
            </span>
          </motion.h2>
          
          <motion.p 
            className="text-white/55 text-lg md:text-xl max-w-2xl mx-auto mb-14 leading-[1.9]"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.7 }}
          >
            Partner with an experienced specialist in mine winder, electrical, mechanical and industrial engineering solutions.
          </motion.p>
          
          <motion.div
            className="flex flex-col sm:flex-row gap-5 justify-center"
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Primary CTA */}
            <Link 
              to="/contact" 
              className="group relative inline-flex items-center justify-center gap-4 bg-gradient-to-b from-[#9a2a3b] to-[#7a1f2c] text-white font-bold px-12 py-6 overflow-hidden transition-all duration-500 hover:-translate-y-2"
              style={{ boxShadow: '0 8px 30px rgba(139,35,50,0.35), inset 0 1px 0 rgba(255,255,255,0.1)' }}
              data-testid="cta-contact-button"
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 25px 60px rgba(139,35,50,0.5), inset 0 1px 0 rgba(255,255,255,0.15)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 8px 30px rgba(139,35,50,0.35), inset 0 1px 0 rgba(255,255,255,0.1)'; }}
            >
              <span className="relative z-10 tracking-[0.2em] uppercase text-sm font-bold">Request a Quotation</span>
              <ArrowRight size={18} className="relative z-10 transform group-hover:translate-x-1.5 transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#a8485a] via-[#8B2332] to-[#6e1c28] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[900ms] ease-out bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            </Link>
            
            {/* Secondary CTA */}
            <Link 
              to="/contact" 
              className="group relative inline-flex items-center justify-center gap-4 bg-white/[0.03] backdrop-blur-sm border border-white/15 text-white font-bold px-12 py-6 overflow-hidden transition-all duration-500 hover:border-white/30 hover:bg-white/[0.07] hover:-translate-y-2"
              style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)' }}
            >
              <Phone size={16} className="relative z-10" />
              <span className="relative z-10 tracking-[0.2em] uppercase text-sm font-bold">Contact Our Team</span>
            </Link>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Bottom fade into footer */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#050506] to-transparent pointer-events-none" />
    </section>
  );
};

// Footer Component - Premium Enterprise Closing Experience
const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isAboutPage = location.pathname === '/about';
  const isServicesPage = location.pathname === '/services';
  const isContactPage = location.pathname === '/contact';
  const hideFooterExtras = isHomePage || isAboutPage || isServicesPage || isContactPage;
  
  const companyLinks = [
    { label: 'About Us', path: '/about' },
    { label: 'Our Team', path: '/team' },
    { label: 'Products', path: '/products' },
    { label: 'Industries', path: '/industries/mining' },
    { label: 'Contact', path: '/contact' }
  ];
  
  const serviceLinks = [
    { label: 'Mine Winders', path: '/services#mine-winder-services' },
    { label: 'Electrical', path: '/services#electrical-solutions' },
    { label: 'Mechanical', path: '/services#mechanical-services' },
    { label: 'Engineering', path: '/services#engineering-refurbishments' },
    { label: 'Renewable Energy', path: '/services#renewable-energy' }
  ];


  
  return (
    <footer className="relative overflow-hidden bg-[#050506]" data-testid="footer">
      
      {/* ═══ PREMIUM FOOTER DIVIDER ═══ */}
      <div className="relative" data-testid="footer-divider">
        {/* Atmospheric transition glow */}
        <div className="h-16 lg:h-20 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent 0%, rgba(139,35,50,0.02) 40%, rgba(139,35,50,0.04) 70%, rgba(10,11,13,0.6) 100%)' }} />
        
        {/* Main divider structure */}
        <motion.div className="relative h-[3px]" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
          {/* Metallic gradient line */}
          <motion.div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, transparent 2%, rgba(255,255,255,0.02) 10%, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.1) 45%, rgba(139,35,50,0.35) 50%, rgba(255,255,255,0.1) 55%, rgba(255,255,255,0.06) 75%, rgba(255,255,255,0.02) 90%, transparent 98%)' }} initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} />
          
          {/* Central crimson node */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-[#8B2332]/60 rounded-full" style={{ boxShadow: '0 0 8px rgba(139,35,50,0.4), 0 0 20px rgba(139,35,50,0.15)' }} />
          
          {/* Left edge bracket */}
          <div className="absolute left-[5%] top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-l border-b border-[#8B2332]/25" />
          </div>
          <div className="absolute left-[5%] -top-1 w-[1px] h-2.5 bg-gradient-to-b from-white/[0.06] to-transparent" />
          
          {/* Right edge bracket */}
          <div className="absolute right-[5%] top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-r border-b border-[#8B2332]/25" />
          </div>
          <div className="absolute right-[5%] -top-1 w-[1px] h-2.5 bg-gradient-to-b from-white/[0.06] to-transparent" />
          
          {/* Crimson accent segments near edges */}
          <div className="absolute top-0 left-[8%] w-12 h-full bg-gradient-to-r from-[#8B2332]/20 to-transparent" />
          <div className="absolute top-0 right-[8%] w-12 h-full bg-gradient-to-l from-[#8B2332]/20 to-transparent" />
        </motion.div>
        
        {/* Post-divider spacing */}
        <div className="h-14 lg:h-16" />
      </div>
      
      {/* ═══════════════════════════════════════
          EXECUTIVE ACHIEVEMENT DASHBOARD — hidden on homepage (rendered in page body)
      ═══════════════════════════════════════ */}
      {!hideFooterExtras && <ExecutiveAchievementDashboard />}

            {/* ═══════════════════════════════════════
          SECTION 2: MAP — hidden on homepage (rendered in page body)
      ═══════════════════════════════════════ */}
      {!hideFooterExtras && (
      <div className="relative bg-[#050506]">
        <div className="absolute inset-0 blueprint-texture opacity-30" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-12 lg:pt-16">
          {/* Premium map label */}
          <motion.div
            className="flex items-center gap-4 mb-6"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <MapPin size={14} className="text-[#8B2332]" />
            <span className="text-[#8B2332] text-[10px] font-bold uppercase tracking-[0.35em]">Welkom Headquarters</span>
            <div className="flex-1 h-px bg-gradient-to-r from-[#8B2332]/20 to-transparent" />
          </motion.div>

          <motion.div 
            className="group/map relative overflow-hidden rounded-sm"
            style={{ height: '280px', boxShadow: '0 15px 50px rgba(0,0,0,0.6)' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Glass backing */}
            <div className="absolute -inset-1 z-0" style={{
              background: 'linear-gradient(145deg, rgba(18,19,24,0.8) 0%, rgba(12,13,17,0.9) 100%)',
              border: '1px solid rgba(255,255,255,0.05)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
            }} />

            {/* Corner brackets */}
            <div className="absolute top-2 right-2 w-8 h-8 border-t border-r border-white/[0.06] group-hover/map:border-white/[0.14] group-hover/map:w-12 group-hover/map:h-12 transition-all duration-500 pointer-events-none z-20" />
            <div className="absolute bottom-2 left-2 w-6 h-6 border-b border-l border-white/[0.04] group-hover/map:border-[#8B2332]/20 group-hover/map:w-10 group-hover/map:h-10 transition-all duration-500 pointer-events-none z-20" />
            <div className="absolute top-2 left-2 w-5 h-5 border-t border-l border-white/[0.03] group-hover/map:border-white/[0.08] group-hover/map:w-7 group-hover/map:h-7 transition-all duration-500 pointer-events-none z-20" />
            <div className="absolute bottom-2 right-2 w-5 h-5 border-b border-r border-white/[0.03] group-hover/map:border-white/[0.08] group-hover/map:w-7 group-hover/map:h-7 transition-all duration-500 pointer-events-none z-20" />

            <div className="absolute inset-0 border border-white/[0.05] group-hover/map:border-white/[0.1] transition-colors duration-500 z-10 pointer-events-none" />
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#8B2332]/25 to-transparent z-10 pointer-events-none" />
            
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3530.5!2d26.735!3d-27.985!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1e9347c0a3ed00bb%3A0x6e8e5c8b68c90b0!2s161+Power+Rd%2C+Voorspoed%2C+Welkom%2C+9459%2C+South+Africa!5e0!3m2!1sen!2sza!4v1700000000000!5m2!1sen!2sza"
              className="w-full h-full border-0 relative z-[1]"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Welkom Winder Services Location"
              style={{ filter: 'invert(0.92) hue-rotate(180deg) saturate(0.3) contrast(1.1) brightness(0.65)' }}
            />
            
            {/* Floating info panel */}
            <div className="absolute bottom-3 left-3 z-20 pointer-events-auto">
              <div className="relative overflow-hidden" style={{
                background: 'linear-gradient(160deg, rgba(10,11,13,0.92) 0%, rgba(14,15,18,0.95) 100%)',
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)',
              }}>
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#8B2332]/60 via-[#8B2332]/20 to-transparent" />
                <div className="p-3 lg:p-4">
                  <p className="font-['Barlow_Condensed'] text-xs font-bold uppercase text-white/75 tracking-wide mb-0.5">Welkom Winder Services</p>
                  <p className="text-white/40 text-[10px] mb-2">161 Power Rd, Voorspoed, Welkom</p>
                  <div className="flex gap-2">
                    <a href="tel:+27573554929" className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#8B2332]/10 border border-[#8B2332]/20 text-[#8B2332] text-[9px] font-bold uppercase tracking-wider hover:bg-[#8B2332]/15 transition-all duration-300"><Phone size={9} />Call</a>
                    <a href="https://maps.google.com/?q=161+Power+Rd+Welkom" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/[0.03] border border-white/[0.08] text-white/45 text-[9px] font-bold uppercase tracking-wider hover:text-white/75 hover:border-white/[0.15] transition-all duration-300"><MapPin size={9} />Directions</a>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#050506] to-transparent pointer-events-none z-10" />
            <div className="absolute top-0 left-0 bottom-0 w-20 bg-gradient-to-r from-[#050506] to-transparent pointer-events-none z-10" />
            <div className="absolute top-0 right-0 bottom-0 w-20 bg-gradient-to-l from-[#050506] to-transparent pointer-events-none z-10" />
          </motion.div>
        </div>
      </div>
      )}

      {/* ═══════════════════════════════════════
          SECTION 3: MAIN CONTENT
      ═══════════════════════════════════════ */}
      <div className="relative bg-[#050506] pt-16 pb-12 lg:pt-20 lg:pb-16">
        {/* Atmospheric lighting */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none" style={{
          background: 'radial-gradient(ellipse 35% 45% at 12% 25%, rgba(139,35,50,0.06) 0%, transparent 70%)'
        }} />
        <div className="absolute bottom-0 right-0 w-1/2 h-1/2 pointer-events-none" style={{
          background: 'radial-gradient(ellipse 50% 50% at 85% 75%, rgba(139,35,50,0.04) 0%, transparent 70%)'
        }} />
        
        {/* Engineering grid */}
        <div className="absolute inset-0 opacity-[0.008]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }} />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          
          {/* Main grid: Brand | Company | Services | Contact */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 md:gap-14 lg:gap-8 mb-16">
            
            {/* ── Brand Column (wider) ── */}
            <motion.div
              className="lg:col-span-4"
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              {/* Logo with soft glow behind */}
              <div className="relative inline-block mb-8">
                <div className="absolute inset-0 scale-150 opacity-30 pointer-events-none" style={{
                  background: 'radial-gradient(ellipse at center, rgba(139,35,50,0.15) 0%, transparent 70%)'
                }} />
                <img src={LOGO_URL} alt="Welkom Winder Services" className="h-28 lg:h-36 w-auto relative z-10" />
              </div>
              
              {/* Company statement - premium typography hierarchy */}
              <div className="mb-8">
                <p className="font-['Barlow_Condensed'] text-lg font-semibold uppercase text-white/80 tracking-[0.08em] mb-3">
                  Welkom Winder Services (Pty) Ltd
                </p>
                <p className="text-white/50 text-[14px] leading-[1.9] max-w-xs mb-1">
                  Engineering reliability since 1999.
                </p>
                <p className="text-white/40 text-[15px] leading-[2.1] max-w-xs">
                  Electrical &bull; Mechanical &bull; Mining<br />
                  Industrial &bull; Renewable Energy
                </p>
                <div className="w-12 h-[1px] bg-gradient-to-r from-[#8B2332]/50 to-transparent mt-5 mb-4" />
                <p className="text-[#8B2332]/70 text-xs font-bold uppercase tracking-[0.2em]">
                  Trusted Since 1999
                </p>
              </div>
              
              {/* Social icons — fully linked */}
              <div className="flex gap-3">
                {[
                  { icon: Facebook, label: 'Facebook', href: 'https://www.facebook.com/profile.php?id=100063594697028' },
                  { icon: MessageCircle, label: 'WhatsApp', href: 'https://wa.me/27832620424' },
                  { icon: Linkedin, label: 'LinkedIn', href: 'https://www.linkedin.com/in/welkom-winder-service-pty-lrd-welkom-01a043403/' }
                ].map(({ icon: Icon, label, href }) => (
                  <a 
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Follow us on ${label}`}
                    className="group relative w-11 h-11 rounded-full bg-white/[0.05] border border-white/[0.1] flex items-center justify-center transition-all duration-500 hover:bg-[#8B2332]/15 hover:border-[#8B2332]/40 hover:-translate-y-1.5 hover:shadow-[0_12px_25px_rgba(139,35,50,0.2)] focus:outline-none focus:ring-2 focus:ring-[#8B2332]/30 focus:ring-offset-2 focus:ring-offset-[#0A0B0D]" 
                    data-testid={`social-${label.toLowerCase()}`}
                  >
                    <Icon size={15} className="text-white/45 group-hover:text-[#d45567] group-hover:scale-110 transition-all duration-500" />
                    <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ boxShadow: '0 0 15px rgba(139,35,50,0.15)' }} />
                  </a>
                ))}
              </div>
            </motion.div>
            
            {/* ── Company Links ── */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
              <h4 className="font-['Barlow_Condensed'] text-sm font-bold uppercase text-white/90 tracking-[0.2em] mb-7 relative inline-block">
                Company
                <span className="absolute -bottom-2 left-0 w-8 h-[2px] bg-gradient-to-r from-[#8B2332] to-transparent" />
              </h4>
              <ul className="space-y-4">
                {companyLinks.map((link) => (
                  <li key={link.label}>
                    <Link 
                      to={link.path} 
                      className="group inline-flex items-center gap-3 text-white/50 text-[13px] hover:text-white/80 transition-all duration-500"
                      data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <ArrowRight size={10} className="text-[#8B2332]/0 group-hover:text-[#8B2332] transition-all duration-400 -ml-4 group-hover:ml-0" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
            
            {/* ── Services Links ── */}
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            >
              <h4 className="font-['Barlow_Condensed'] text-sm font-bold uppercase text-white/90 tracking-[0.2em] mb-7 relative inline-block">
                Services
                <span className="absolute -bottom-2 left-0 w-8 h-[2px] bg-gradient-to-r from-[#8B2332] to-transparent" />
              </h4>
              <ul className="space-y-4">
                {serviceLinks.map((link) => (
                  <li key={link.label}>
                    <Link 
                      to={link.path}
                      className="group inline-flex items-center gap-3 text-white/50 text-[13px] hover:text-white/80 transition-all duration-500"
                    >
                      <ArrowRight size={10} className="text-[#8B2332]/0 group-hover:text-[#8B2332] transition-all duration-400 -ml-4 group-hover:ml-0" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
            
            {/* ── Contact Column ── */}
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              <h4 className="font-['Barlow_Condensed'] text-sm font-bold uppercase text-white/90 tracking-[0.2em] mb-7 relative inline-block">
                Get in Touch
                <span className="absolute -bottom-2 left-0 w-8 h-[2px] bg-gradient-to-r from-[#8B2332] to-transparent" />
              </h4>
              
              <div className="space-y-5">
                <a href="https://www.google.com/maps/search/?api=1&query=161+Power+Rd+Voorspoed+Welkom+9459" target="_blank" rel="noopener noreferrer" className="group flex items-start gap-4 hover:bg-white/[0.02] -mx-3 px-3 py-2 transition-colors duration-300 rounded">
                  <div className="w-10 h-10 bg-[#8B2332]/10 border border-[#8B2332]/20 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#8B2332]/15 group-hover:border-[#8B2332]/35 transition-all duration-500">
                    <MapPin size={15} className="text-[#8B2332]" />
                  </div>
                  <div>
                    <span className="text-white/55 text-[13px] leading-[1.8] block group-hover:text-white/75 transition-colors duration-500" data-testid="footer-address">
                      161 Power Rd, Voorspoed,<br />Welkom, 9459
                    </span>
                  </div>
                </a>
                
                <a href="tel:+27573554929" className="group flex items-center gap-4 hover:bg-white/[0.02] -mx-3 px-3 py-2 transition-colors duration-300 rounded">
                  <div className="w-10 h-10 bg-[#8B2332]/10 border border-[#8B2332]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#8B2332]/15 group-hover:border-[#8B2332]/35 transition-all duration-500">
                    <Phone size={15} className="text-[#8B2332]" />
                  </div>
                  <span className="text-white/55 text-[13px] group-hover:text-white/75 transition-colors duration-500" data-testid="footer-phone">
                    057 355 4929
                  </span>
                </a>
                
                <a href="mailto:sales@winderservices.com" className="group flex items-center gap-4 hover:bg-white/[0.02] -mx-3 px-3 py-2 transition-colors duration-300 rounded">
                  <div className="w-10 h-10 bg-[#8B2332]/10 border border-[#8B2332]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#8B2332]/15 group-hover:border-[#8B2332]/35 transition-all duration-500">
                    <Mail size={15} className="text-[#8B2332]" />
                  </div>
                  <span className="text-white/55 text-[13px] group-hover:text-white/75 transition-colors duration-500" data-testid="footer-email">
                    sales@winderservices.com
                  </span>
                </a>

                <div className="group flex items-center gap-4 -mx-3 px-3 py-2">
                  <div className="w-10 h-10 bg-[#8B2332]/10 border border-[#8B2332]/20 flex items-center justify-center flex-shrink-0">
                    <Clock size={15} className="text-[#8B2332]" />
                  </div>
                  <div>
                    <span className="text-white/55 text-[13px] block">Mon — Fri: 07:00 — 17:00</span>
                    <span className="text-[#8B2332]/70 text-[11px] font-semibold uppercase tracking-[0.1em]">Emergency Technical Support</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════
          SECTION 4: COPYRIGHT BAR
      ═══════════════════════════════════════ */}
      <div className="relative bg-[#040405]">
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/45 text-xs tracking-[0.03em]">
              &copy; {currentYear} Welkom Winder Services (Pty) Ltd. All Rights Reserved.
            </p>
            <div className="flex items-center gap-6 flex-wrap justify-center">
              <span className="text-white/35 text-xs tracking-wide hover:text-white/55 transition-colors duration-300 cursor-pointer">Privacy Policy</span>
              <span className="w-[3px] h-[3px] rounded-full bg-white/15 hidden md:block" />
              <span className="text-white/35 text-xs tracking-wide hover:text-white/55 transition-colors duration-300 cursor-pointer">Terms & Conditions</span>
              <span className="w-[3px] h-[3px] rounded-full bg-white/15 hidden md:block" />
              <span className="text-white/40 text-xs tracking-wide">Built with Engineering Excellence</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const HomePage = () => {
  return (
    <main data-testid="home-page">
      <SEO description="Specialist mine winder, electrical, mechanical and industrial engineering solutions built on hands-on experience since 1999. 200+ projects across Africa." path="/" />
      <HeroSection />
      <TrustStrip />
      <AboutPreview />
      <MineWinderExpertise />
      <ServicesPreview />
      <IndustriesSection />
      <WhyChooseUs />
      <SelectedProjects />
      <LeadershipTeaser />
      <ExecutiveAchievementDashboard />
      <HomeLocation />
      <CTASection />
    </main>
  );
};

// About Page — Executive Cinematic Presentation
const AboutPage = () => {
  const capabilities = [
    { title: "Mine Winder Expertise", desc: "Lifecycle support for all winder types including Blair, Koepe, and friction winders serviced to OEM-standard specifications", icon: Cog, image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/oo6gfwn5_Our%20Capabulities%20-%20Mine%20Winder%20Expertise.png", imageAlt: "Mine winder engineering and maintenance expertise", imagePosition: "center 40%" },
    { title: "Electrical Systems", desc: "Design, installation, and maintenance of electrical distribution, control systems, switchgear, and automation", icon: Zap, image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/8xmvl4oi_Our%20Capabulities%20-%20Electrical%20Systems.png", imageAlt: "Industrial electrical systems and engineering equipment", imagePosition: "center 30%" },
    { title: "Mechanical Engineering", desc: "Heavy machining, fabrication, precision component manufacturing, and hydraulic systems", icon: Wrench, image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/8jem70u1_Our%20Capabulities%20-%20Mechanical%20Engineering.png", imageAlt: "Mechanical engineering, machining and fabrication", imagePosition: "center" },
    { title: "Testing & Audits", desc: "Comprehensive Level 1 audits, NDT, EMT testing, laser alignment, and safety certifications", icon: Shield, image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/w7vdh65i_Our%20Capabilities%20-%20Testing%20%26%20Audits.png", imageAlt: "Mining engineering testing, inspection and technical audit services", imagePosition: "center 35%" },
    { title: "Renewable Energy", desc: "Solar installations, hybrid power systems, and sustainable energy solutions for industrial applications", icon: Sun, image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/0jq1a02r_Our%20capabilities%20-%20Renewable%20Energy.png", imageAlt: "Industrial renewable energy and solar engineering solutions", imagePosition: "center 40%" },
    { title: "Emergency Response", desc: "24/7 rapid deployment support for critical equipment failures and operational emergencies", icon: Clock, image: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/vxzn773c_Our%20Capabilities%20-%20Emergency%20Response.png", imageAlt: "Emergency mining engineering repair and technical response services", imagePosition: "center" }
  ];

  const authorityBrands = [
    { name: "ABB", logo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/6n23twan_8.png" },
    { name: "Schneider Electric", logo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/gehsup9m_9.png" },
    { name: "WEG", logo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/ajcr3158_11.png" },
    { name: "Allen-Bradley", logo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/e0pr2y9p_10.png" },
    { name: "Eaton", logo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/bgsj0luw_7.png" },
    { name: "CBI Electric", logo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/q37lhk7r_6.png" },
    { name: "Omron", logo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/0o56caqm_4.png" },
    { name: "Toshiba", logo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/vv1dnb2l_1.png" },
    { name: "Fluke", logo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/wt21g9bg_2.png" },
    { name: "ACDC Dynamics", logo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/sk6zt863_5.png" },
    { name: "Johannes Hübner Giessen", logo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/ie8s4ugj_3.png" },
  ];

  // Timeline scroll progress
  const timelineRef = React.useRef(null);
  const progressRef = React.useRef(null);
  React.useEffect(() => {
    const handleScroll = () => {
      if (!timelineRef.current || !progressRef.current) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const viewH = window.innerHeight;
      const start = rect.top - viewH * 0.6;
      const end = rect.bottom - viewH * 0.4;
      const range = end - start;
      if (range <= 0) return;
      const progress = Math.max(0, Math.min(1, -start / range));
      progressRef.current.style.height = `${progress * 100}%`;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <main className="pt-24" data-testid="about-page">
      <SEO title="About Us" description="Welkom Winder Services - specialist electrical, mechanical, and mining engineering since 1999. 200+ projects delivered across Africa." path="/about" />

      {/* ═══════════ PREMIUM ABOUT HERO — Full-Bleed Cinematic ═══════════ */}
      <section className="relative overflow-hidden bg-[#0A0B0D]" style={{ minHeight: 'clamp(400px, 85vh, 900px)' }} data-testid="about-hero">
        <h1 className="sr-only">Our Story - Welkom Winder Services</h1>

        {/* Full-bleed background artwork */}
        <motion.div className="absolute inset-0" initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}>
          <img
            src="https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/29jkdv4x_WWS%20-%20Our%20Story.png"
            alt="Welkom Winder Services facility with mine winder and electrical engineering equipment"
            width="1693" height="929"
            fetchPriority="high"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 35%' }}
            data-testid="about-hero-artwork"
          />
        </motion.div>

        {/* Readability gradients — lighter since artwork has built-in text */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0B0D]/40 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D]/70 via-transparent to-[#0A0B0D]/20" />

        {/* Technical grid */}
        <motion.div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.006) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.006) 1px, transparent 1px)', backgroundSize: '60px 60px' }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.8 }} />

        {/* Ambient lighting */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 35% 50% at 10% 50%, rgba(51,80,163,0.05) 0%, transparent 70%)' }} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 35% 50% at 90% 50%, rgba(139,35,50,0.04) 0%, transparent 70%)' }} />

        {/* Corner brackets */}
        <motion.div className="absolute top-4 left-4 w-6 h-6 border-t border-l border-[#8B2332]/25 z-20 pointer-events-none" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.5 }} />
        <motion.div className="absolute top-4 right-4 w-5 h-5 border-t border-r border-[#1a3a5c]/25 z-20 pointer-events-none" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.5 }} />
        <motion.div className="absolute bottom-16 left-4 w-5 h-5 border-b border-l border-[#1a3a5c]/20 z-20 pointer-events-none" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.5 }} />
        <motion.div className="absolute bottom-16 right-4 w-6 h-6 border-b border-r border-[#8B2332]/20 z-20 pointer-events-none" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.5 }} />

        {/* Hero content — artwork contains baked-in text; mobile fallback for cropped artwork */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 flex items-end" style={{ minHeight: 'clamp(400px, 85vh, 900px)', paddingTop: '7rem', paddingBottom: '3rem' }}>
          {/* Mobile-only text (artwork text is cropped by object-cover on small screens) */}
          <div className="sm:hidden">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-[2px] w-10 bg-gradient-to-r from-[#8B2332] to-transparent" />
              <span className="text-[#8B2332] text-[10px] font-bold uppercase tracking-[0.3em]">About Us</span>
            </div>
            <h2 className="font-['Barlow_Condensed'] text-4xl font-black uppercase text-white leading-[0.9] mb-4">Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B2332] to-[#c44b5e]">Story</span></h2>
            <p className="text-white/55 text-[14px] leading-[1.8] mb-4">Specialist electrical, mechanical, and mining engineering since 1999. 200+ projects delivered across Africa.</p>
          </div>
        </div>

        {/* Bottom transition */}
        <div className="absolute bottom-0 left-0 right-0 z-20">
          <div className="h-px bg-gradient-to-r from-transparent via-[#8B2332]/20 to-transparent" />
        </div>
      </section>

      {/* ═══════════ OUR STORY — Editorial Content ═══════════ */}
      <section className="relative py-16 lg:py-20 overflow-hidden" data-testid="about-story-content">
        <div className="absolute inset-0 bg-[#0e0f12]" />
        <div className="absolute inset-0 blueprint-texture opacity-50" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
            {/* LEFT: Editorial Panel */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="group/story relative overflow-hidden cursor-default transition-transform duration-[300ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] hover:-translate-y-[2px]" data-testid="executive-editorial-panel">
                <div className="absolute inset-0 transition-all duration-[300ms]" style={{ background: 'linear-gradient(160deg, rgba(18,19,24,0.92) 0%, rgba(12,13,17,0.96) 100%)', border: '1px solid rgba(255,255,255,0.07)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05), 0 25px 70px rgba(0,0,0,0.4)' }} />
                <div className="absolute inset-0 border border-transparent group-hover/story:border-white/[0.03] transition-colors duration-[300ms] pointer-events-none" />
                <div className="absolute inset-0 blueprint-texture opacity-50 group-hover/story:opacity-[0.65] transition-opacity duration-[400ms]" />
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.1] to-transparent group-hover/story:via-white/[0.15] transition-all duration-[300ms]" />
                <div className="absolute top-8 bottom-8 left-0 w-[2px] bg-gradient-to-b from-[#8B2332] via-[#8B2332]/40 to-transparent group-hover/story:from-[#a63d50] group-hover/story:via-[#8B2332]/60 transition-all duration-[350ms]" />
                <div className="absolute top-3 right-3 w-8 h-8 border-t border-r border-white/[0.07] group-hover/story:border-white/[0.14] group-hover/story:w-10 group-hover/story:h-10 transition-all duration-[350ms] pointer-events-none" />
                <div className="absolute bottom-3 left-3 w-6 h-6 border-b border-l border-[#8B2332]/20 group-hover/story:border-[#8B2332]/35 group-hover/story:w-8 group-hover/story:h-8 transition-all duration-[350ms] pointer-events-none" />
                <div className="relative z-10 p-9 lg:p-11">
                  <div className="space-y-7">
                    <p className="text-white/72 text-[15px] lg:text-[16px] leading-[2.15]">
                      <span className="text-white/95 font-semibold">Welkom Winder Services</span> (Pty) Ltd is a specialist in the electrical, mechanical, and mining industries, proudly serving <span className="text-white/95 font-semibold">Welkom</span> and clients across <span className="text-white/95 font-semibold">southern Africa since 1999</span>. With decades of hands-on experience, we specialize in the supply, maintenance, repair, and upgrading of all types of mine winders, ensuring safe and efficient operations through strict <span className="text-white/95 font-semibold">OEM standards</span>.
                    </p>
                    <div className="flex items-center gap-4"><div className="w-16 h-px bg-gradient-to-r from-[#8B2332]/60 to-transparent" /><div className="w-1 h-1 border border-[#8B2332]/30 rotate-45" /></div>
                    <p className="text-white/65 text-[15px] lg:text-[16px] leading-[2.15]">
                      Our expertise covers a wide spectrum including <span className="text-white/90 font-medium">Level 1 Audits</span>, dynamic brake testing, locked bell audits, <span className="text-white/90 font-medium">NDT and EMT testing</span>, laser alignment, and electrical and mechanical refurbishments. From heavy engineering to precision component manufacturing, we deliver solutions tailored to the unique demands of the mining sector.
                    </p>
                    <p className="text-white/68 text-[15px] lg:text-[16px] leading-[2.15]">
                      Beyond mine winder services, we provide comprehensive electrical solutions including <span className="text-white/90 font-medium">switchgear</span>, motor control centers, <span className="text-white/90 font-medium">automation systems</span>, transformers, generators, and <span className="text-white/90 font-medium">renewable energy</span> installations across industrial and agricultural sectors.
                    </p>
                  </div>
                </div>
              </div>
              <motion.div className="wws-commitment-strip group/commit relative mt-10 p-7 lg:p-8 cursor-default transition-transform duration-[300ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] hover:-translate-y-[2px]" initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3, duration: 0.6 }}>
                <span className="text-[#8B2332]/50 text-[10px] font-bold uppercase tracking-[0.3em] block mb-3 group-hover/commit:text-[#8B2332]/70 transition-colors duration-[300ms]">Our Commitment</span>
                <p className="text-white/70 text-[15px] leading-[2] italic max-w-lg">
                  At Welkom Winder Services, we are committed to delivering outstanding results with{' '}
                  <span className="text-white/90 font-semibold not-italic">integrity</span>,{' '}
                  <span className="text-white/90 font-semibold not-italic">safety</span>, and{' '}
                  <span className="text-white/90 font-semibold not-italic">professionalism</span>{' '}
                  at the core of everything we do.
                </p>
                <div className="flex items-center gap-4 mt-4">
                  {["Integrity", "Safety", "Precision", "Professionalism"].map((v, vi) => (
                    <React.Fragment key={v}>
                      {vi > 0 && <div className="w-px h-3 bg-white/[0.08]" />}
                      <span className="text-white/35 text-[10px] font-bold uppercase tracking-[0.15em]">{v}</span>
                    </React.Fragment>
                  ))}
                </div>
              </motion.div>
            </motion.div>

            {/* RIGHT: KPI Cards */}
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="grid grid-cols-2 gap-5">
                <ExecutiveKPICard value={1999} suffix="" label="Established" index={0} icon={Award} />
                <ExecutiveKPICard value={200} suffix="+" label="Projects Completed" index={1} icon={Target} />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Engineering Section Divider */}
      <div className="relative bg-[#0E0F12]">
        <EngineeringDivider />
      </div>


      {/* ═══════════ LEADERSHIP & EXPERIENCE ═══════════ */}
      <section className="relative py-16 lg:py-20 overflow-hidden bg-[#0A0B0D]" data-testid="leadership-section">
        <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.005) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.005) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 40% 40% at 20% 30%, rgba(139,35,50,0.03) 0%, transparent 60%)' }} />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8B2332]/15 to-transparent" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          {/* Section intro */}
          <div className="text-center mb-16 lg:mb-20 group/lead cursor-default">
            <motion.div className="flex items-center justify-center gap-4 mb-5" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
              <motion.span className="h-[2px] bg-gradient-to-r from-transparent to-[#8B2332] transition-all duration-[350ms] group-hover/lead:to-[#a63d50]" initial={{ width: 0 }} whileInView={{ width: 45 }} viewport={{ once: true }} transition={{ duration: 0.6 }} style={{ transition: 'width 350ms cubic-bezier(0.22,0.61,0.36,1)' }} />
              <span className="text-[#8B2332] text-[13px] lg:text-[14px] font-bold uppercase tracking-[0.35em] transition-all duration-[300ms] group-hover/lead:text-[#a63d50] group-hover/lead:tracking-[0.38em]">Leadership & Experience</span>
              <motion.span className="h-[2px] bg-gradient-to-l from-transparent to-[#8B2332] transition-all duration-[350ms] group-hover/lead:to-[#a63d50]" initial={{ width: 0 }} whileInView={{ width: 45 }} viewport={{ once: true }} transition={{ duration: 0.6 }} style={{ transition: 'width 350ms cubic-bezier(0.22,0.61,0.36,1)' }} />
            </motion.div>
            <motion.h2 className="font-['Barlow_Condensed'] text-2xl lg:text-3xl font-bold uppercase text-white tracking-wide mb-5 transition-all duration-[350ms]" initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              Built on Decades of Hands-On <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B2332] to-[#c44b5e] transition-all duration-[350ms] group-hover/lead:from-[#a63d50] group-hover/lead:to-[#d46b7c]">Industry Experience</span>
            </motion.h2>
            <motion.p className="text-white/50 text-[15px] lg:text-[16px] leading-[2] max-w-3xl mx-auto" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              Behind Welkom Winder Services (Pty) Ltd is decades of practical experience in electrical engineering, mine winders, inspection, testing, maintenance and specialised mining services.
            </motion.p>
          </div>

          {/* ── Executive Profile ── */}
          <motion.div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-start mb-16 lg:mb-20" initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            {/* Photo — 2 cols */}
            <div className="lg:col-span-2">
              <div className="relative group/portrait" data-testid="director-photo">
                <div className="relative overflow-hidden" style={{ aspectRatio: '5/6' }}>
                  {/* Outer technical frame */}
                  <div className="absolute inset-0 border-2 border-white/[0.06] group-hover/portrait:border-white/[0.10] transition-colors duration-[400ms] z-10 pointer-events-none" />
                  {/* Inner frame */}
                  <div className="absolute inset-[5px] border border-white/[0.03] group-hover/portrait:border-white/[0.06] transition-colors duration-[400ms] z-10 pointer-events-none" />
                  {/* Top red accent — sweeps on hover */}
                  <div className="absolute top-0 left-0 right-0 h-[3px] z-20 overflow-hidden pointer-events-none">
                    <div className="h-full bg-gradient-to-r from-[#8B2332] via-[#8B2332]/60 to-transparent w-[35%] group-hover/portrait:w-full transition-all duration-[500ms] ease-[cubic-bezier(0.22,0.61,0.36,1)]" />
                  </div>
                  {/* Left red accent */}
                  <div className="absolute top-0 left-0 w-[3px] z-20 overflow-hidden pointer-events-none">
                    <div className="w-full bg-gradient-to-b from-[#8B2332] to-transparent h-[70px] group-hover/portrait:h-[120px] transition-all duration-[500ms]" />
                  </div>
                  {/* Corner brackets */}
                  <div className="absolute top-3 right-3 w-8 h-8 border-t-2 border-r-2 border-white/[0.06] group-hover/portrait:border-white/[0.14] group-hover/portrait:w-10 group-hover/portrait:h-10 transition-all duration-[400ms] z-20 pointer-events-none" />
                  <div className="absolute bottom-3 left-3 w-7 h-7 border-b-2 border-l-2 border-[#8B2332]/20 group-hover/portrait:border-[#8B2332]/40 group-hover/portrait:w-9 group-hover/portrait:h-9 transition-all duration-[400ms] z-20 pointer-events-none" />
                  <div className="absolute bottom-3 right-3 w-5 h-5 border-b border-r border-white/[0.04] group-hover/portrait:border-white/[0.08] transition-all duration-[400ms] z-20 pointer-events-none" />
                  <div className="absolute top-3 left-3 w-4 h-4 border-t border-l border-[#8B2332]/10 group-hover/portrait:border-[#8B2332]/25 transition-all duration-[400ms] z-20 pointer-events-none" />
                  {/* Leon Schoeman portrait */}
                  <img
                    src="https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/x9lcwy5l_Welkom%20Winder%20Service.png"
                    alt="Leon Schoeman – Director and Owner of Welkom Winder Services (Pty) Ltd"
                    width="600" height="720"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-[600ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/portrait:scale-[1.012]"
                    style={{ objectPosition: 'center 20%' }}
                    data-testid="director-photo-img"
                  />
                  {/* Bottom vignette */}
                  <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#0A0B0D]/30 to-transparent pointer-events-none" />
                </div>
                {/* Outer shadow/depth */}
                <div className="absolute -inset-1 -z-10 opacity-60 group-hover/portrait:opacity-80 transition-opacity duration-[400ms]" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} />
              </div>
            </div>

            {/* Profile text — 3 cols */}
            <div className="lg:col-span-3">
              {/* Premium executive content panel */}
              <div className="group/bio relative overflow-hidden cursor-default" style={{ background: 'linear-gradient(155deg, rgba(16,17,22,0.6) 0%, rgba(12,13,17,0.4) 100%)', border: '1px solid rgba(255,255,255,0.04)', padding: '2rem 2rem 2rem 2.25rem' }}>
                <div className="absolute top-8 bottom-8 left-0 w-[2px] bg-gradient-to-b from-[#8B2332]/30 via-[#8B2332]/10 to-transparent group-hover/bio:from-[#8B2332]/50 group-hover/bio:via-[#8B2332]/25 transition-all duration-[400ms]" />
                <div className="absolute top-3 right-3 w-5 h-5 border-t border-r border-white/[0.03] group-hover/bio:border-white/[0.08] transition-all duration-[400ms] pointer-events-none" />

                <div className="mb-2">
                  <span className="text-white/30 text-[12px] font-bold uppercase tracking-[0.25em] group-hover/bio:text-white/40 transition-colors duration-[300ms]">Director & Owner</span>
                </div>
                <h3 className="font-['Barlow_Condensed'] text-3xl lg:text-4xl font-bold uppercase text-white tracking-wide mb-2 transition-all duration-[350ms] group-hover/bio:text-white" data-testid="director-name">Leon Schoeman</h3>
                <div className="h-[2px] w-14 bg-gradient-to-r from-[#8B2332] to-[#8B2332]/30 mb-7 group-hover/bio:w-20 transition-all duration-[400ms]" />

                <div className="space-y-5 text-white/60 text-[16px] lg:text-[17px] leading-[1.95] max-w-xl">
                  <p>Leon Schoeman brings more than four decades of hands-on experience in the electrical and mining industries to Welkom Winder Services (Pty) Ltd. His career has progressed from Apprentice Electrician and Mine Electrician to specialist Winder Electrician, Senior Winder Inspector and ultimately Director and Owner of Welkom Winder Services.</p>
                  <p>His extensive practical experience encompasses mine winders and related equipment, electrical systems, specialised inspections, third-party audits, testing, servicing, repairs and technical support across mining operations in several regions of South Africa.</p>
                  <p>Today, Leon remains actively involved in the technical and operational side of the business, overseeing sales, services, inspections, third-party audits and testing while continuing to serve clients as a Senior Winder Inspector.</p>
                </div>
              </div>

              {/* Experience indicators */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
                {[
                  { value: "1982", label: "Industry Career Began" },
                  { value: "2005", label: "Director & Owner Since" },
                  { value: "40+", label: "Years Experience" },
                  { value: "Winder", label: "Senior Inspector" },
                ].map((stat, i) => (
                  <motion.div key={stat.label} className="group/stat relative overflow-hidden p-4" style={{ background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(107,122,141,0.08)' }} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 + i * 0.06, duration: 0.5 }} whileHover={{ y: -2, borderColor: 'rgba(139,35,50,0.15)' }} data-testid={`director-stat-${i}`}>
                    <div className="absolute top-0 left-0 right-0 h-[1px] overflow-hidden"><div className="h-full w-0 group-hover/stat:w-1/3 bg-gradient-to-r from-[#8B2332]/50 to-transparent transition-all duration-300" /></div>
                    <div className="font-['Barlow_Condensed'] text-xl font-black text-white/80 tracking-tight">{stat.value}</div>
                    <div className="text-white/30 text-[10px] font-bold uppercase tracking-[0.1em] mt-0.5">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* ── Career Journey Timeline ── */}
          <div className="mb-16 lg:mb-20" data-testid="career-timeline">
            <div className="text-center mb-10 group/timeline cursor-default">
              <motion.div className="flex items-center justify-center gap-4 mb-3" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
                <motion.span className="h-[2px] bg-gradient-to-r from-transparent to-[#8B2332]/50 transition-all duration-[350ms] group-hover/timeline:to-[#a63d50]" initial={{ width: 0 }} whileInView={{ width: 35 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ transition: 'width 350ms cubic-bezier(0.22,0.61,0.36,1)' }} />
                <span className="text-[#8B2332]/60 text-[13px] lg:text-[14px] font-bold uppercase tracking-[0.3em] transition-all duration-[300ms] group-hover/timeline:text-[#a63d50]/80 group-hover/timeline:tracking-[0.33em]">Four Decades of Technical Experience</span>
                <motion.span className="h-[2px] bg-gradient-to-l from-transparent to-[#8B2332]/50 transition-all duration-[350ms] group-hover/timeline:to-[#a63d50]" initial={{ width: 0 }} whileInView={{ width: 35 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ transition: 'width 350ms cubic-bezier(0.22,0.61,0.36,1)' }} />
              </motion.div>
              <motion.h3 className="font-['Barlow_Condensed'] text-2xl lg:text-3xl font-bold uppercase text-white tracking-wide mb-3 transition-all duration-[350ms]" initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                A Career Built on <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B2332] to-[#c44b5e] transition-all duration-[350ms] group-hover/timeline:from-[#a63d50] group-hover/timeline:to-[#d46b7c]">Experience</span>
              </motion.h3>
              <p className="text-white/40 text-[14px]">From apprentice electrician in 1982 to Director-Owner and Senior Winder Inspector today.</p>
            </div>

            {/* Desktop: alternating 2-column | Mobile: single-column left */}
            <div className="relative max-w-4xl mx-auto" ref={timelineRef}>
              {/* Base line — desktop */}
              <div className="hidden lg:block absolute left-1/2 -translate-x-px top-0 bottom-0 w-px bg-white/[0.04]" />
              {/* Progress line — desktop */}
              <div className="hidden lg:block wws-timeline-progress" ref={progressRef} />
              {/* Base line — mobile */}
              <div className="lg:hidden absolute left-4 top-0 bottom-0 w-px bg-white/[0.04]" />

              {[
                { date: "30 November 1982", title: "Career Begins at Free State Saaiplaas Mine", desc: "Leon started his career as an Apprentice Electrician at Free State Saaiplaas Mine, gaining practical experience in the demanding electrical environment of the mining industry." },
                { date: "26 August 1987", title: "Qualified Electrician", desc: "Qualified as an Electrician and subsequently appointed as Acting Electrical Foreman at Free State Saaiplaas Mine." },
                { date: "20 February 1999", title: "Harmony Acquisition", desc: "Following Harmony\u2019s acquisition of Free State Saaiplaas Mine, Leon continued working as an Electrician, with particular involvement and experience as a Winder Electrician." },
                { date: "29 July 1999", title: "Joining Welkom Winder Services", desc: "Leon resigned from the mine and joined Welkom Winder Services (Pty) Ltd. Through the company he became involved in specialised services and repairs for mining operations across the Goldfields, Gauteng, North West and Northern Cape." },
                { date: "4 December 2002", title: "Appointed Co-Director", desc: "Appointed as Co-Director of Welkom Winder Services (Pty) Ltd." },
                { date: "1 September 2005", title: "Director & Owner", desc: "Leon acquired Welkom Winder Services (Pty) Ltd and became the Director and Owner. Since acquiring the business, he has been responsible for overseeing sales, services, mine winder inspections, third-party audits, testing and related technical services." },
                { date: "13 July 2012", title: "Additional Specialist Qualification", desc: "Completed an Earthing, Bonding and Lightning course and qualified as an Inspector and Installer." },
                { date: "Today", title: "Continuing Hands-On Leadership", desc: "Leon continues to serve as a Senior Winder Inspector for Welkom Winder Services. He has also conducted various Dynamic Brake Tests in the presence of DMI inspectors." },
              ].map((milestone, i) => {
                const isLeft = i % 2 === 0;
                return (
                  <motion.div key={i} className={`relative pb-6 last:pb-0 ${/* Mobile: always left-aligned */'pl-12 lg:pl-0'} ${/* Desktop: alternating */''}`} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04, duration: 0.5 }} data-testid={`milestone-${i}`}>
                    {/* Mobile node */}
                    <div className="lg:hidden absolute left-[11px] top-1 w-2.5 h-2.5 rounded-full border border-[#8B2332]/50" style={{ background: milestone.date === "Today" ? '#8B2332' : 'rgba(139,35,50,0.15)', boxShadow: milestone.date === "Today" ? '0 0 8px rgba(139,35,50,0.4)' : 'none' }} />
                    {/* Desktop node — centre */}
                    <div className="hidden lg:block absolute left-1/2 -translate-x-1/2 top-1 w-2.5 h-2.5 rounded-full border border-[#8B2332]/50" style={{ background: milestone.date === "Today" ? '#8B2332' : 'rgba(139,35,50,0.15)', boxShadow: milestone.date === "Today" ? '0 0 8px rgba(139,35,50,0.4)' : 'none' }} />
                    {/* Desktop content — alternating sides with panel */}
                    <div className={`hidden lg:block ${isLeft ? 'pr-[calc(50%+2rem)] text-right' : 'pl-[calc(50%+2rem)]'}`}>
                      <div className="wws-milestone-panel relative group/ms cursor-default" style={{ transition: 'transform 300ms cubic-bezier(0.22,0.61,0.36,1)' }}>
                        <span className="text-[#8B2332]/70 text-[13px] font-bold uppercase tracking-[0.15em] block mb-1.5">{milestone.date}</span>
                        <h4 className="font-['Barlow_Condensed'] text-lg lg:text-xl font-bold uppercase text-white/90 group-hover/ms:text-white tracking-wide mb-2 transition-colors duration-[300ms]">{milestone.title}</h4>
                        <p className="text-white/55 text-[15px] lg:text-[16px] leading-[1.75]">{milestone.desc}</p>
                      </div>
                    </div>
                    {/* Mobile content — single column with panel */}
                    <div className="lg:hidden">
                      <div className="wws-milestone-panel relative">
                        <span className="text-[#8B2332]/70 text-[13px] font-bold uppercase tracking-[0.15em] block mb-1.5">{milestone.date}</span>
                        <h4 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-white/90 tracking-wide mb-2">{milestone.title}</h4>
                        <p className="text-white/55 text-[15px] leading-[1.75] max-w-lg">{milestone.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* ── Professional Credentials ── */}
          <div data-testid="credentials-section">
            <div className="text-center mb-10 group/cred cursor-default">
              <motion.div className="flex items-center justify-center gap-4 mb-4" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
                <motion.span className="h-[2px] bg-gradient-to-r from-transparent to-[#8B2332]/50 transition-all duration-[350ms] group-hover/cred:to-[#a63d50]" initial={{ width: 0 }} whileInView={{ width: 35 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ transition: 'width 350ms cubic-bezier(0.22,0.61,0.36,1)' }} />
                <span className="text-[#8B2332] text-[13px] lg:text-[14px] font-bold uppercase tracking-[0.3em] transition-all duration-[300ms] group-hover/cred:text-[#a63d50] group-hover/cred:tracking-[0.33em]">Professional Qualifications</span>
                <motion.span className="h-[2px] bg-gradient-to-l from-transparent to-[#8B2332]/50 transition-all duration-[350ms] group-hover/cred:to-[#a63d50]" initial={{ width: 0 }} whileInView={{ width: 35 }} viewport={{ once: true }} transition={{ duration: 0.5 }} style={{ transition: 'width 350ms cubic-bezier(0.22,0.61,0.36,1)' }} />
              </motion.div>
              <motion.h3 className="font-['Barlow_Condensed'] text-2xl lg:text-3xl font-bold uppercase text-white tracking-wide transition-all duration-[350ms]" initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                Professional <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B2332] to-[#c44b5e] transition-all duration-[350ms] group-hover/cred:from-[#a63d50] group-hover/cred:to-[#d46b7c]">Credentials</span> & Industry Affiliations
              </motion.h3>
            </div>
            <div className="flex flex-wrap justify-center gap-4 lg:gap-5 max-w-5xl mx-auto">
              {/* 3+2 grid with last row centred */}
              {[
                { title: "Registered Electrical Contractor", desc: "Registered as an Electrical Contractor with the Department of Employment and Labour.", icon: Shield },
                { title: "Earthing, Bonding & Lightning", desc: "Qualified as an Inspector and Installer following completion of the relevant Earthing, Bonding and Lightning course.", icon: Zap },
                { title: "Senior Winder Inspector", desc: "Continues to provide specialist mine winder inspection services through Welkom Winder Services (Pty) Ltd.", icon: Target },
                { title: "Dynamic Brake Testing", desc: "Experienced in conducting Dynamic Brake Testing, including testing performed in the presence of DMI inspectors.", icon: Gauge },
                { title: "Industry Affiliations", desc: "Member of the Electrical Contractors Association, National Bargaining Council for the Electrical Industry of South Africa and Lightning Protection Practitioners.", icon: Award },
              ].map((cred, i) => {
                const CredIcon = cred.icon;
                return (
                  <motion.div key={cred.title} className="group relative overflow-hidden p-6 w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.84rem)]" style={{ background: 'linear-gradient(155deg, rgba(14,15,18,0.95) 0%, rgba(10,11,13,0.98) 100%)', border: '1px solid rgba(107,122,141,0.08)', boxShadow: '0 6px 24px rgba(0,0,0,0.25)' }} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.5 }} whileHover={{ y: -3, borderColor: 'rgba(139,35,50,0.15)', boxShadow: '0 15px 40px rgba(0,0,0,0.35), 0 0 20px rgba(139,35,50,0.04)' }} data-testid={`credential-${i}`}>
                    <div className="absolute top-0 left-0 right-0 h-[1.5px] overflow-hidden"><div className="h-full w-0 group-hover:w-full bg-gradient-to-r from-[#8B2332] via-[#8B2332]/40 to-transparent transition-all duration-500 ease-out" /></div>
                    <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(107,122,141,0.01) 1px, transparent 1px), linear-gradient(90deg, rgba(107,122,141,0.01) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                    <div className="absolute top-2 right-2 w-3.5 h-3.5 border-t border-r border-white/[0.04] group-hover:border-[#8B2332]/20 transition-all duration-400 pointer-events-none" />
                    <div className="w-9 h-9 flex items-center justify-center mb-4" style={{ background: 'rgba(139,35,50,0.06)', border: '1px solid rgba(139,35,50,0.15)' }}>
                      <CredIcon size={16} className="text-[#8B2332]/70" />
                    </div>
                    <h4 className="font-['Barlow_Condensed'] text-base lg:text-lg font-bold uppercase text-white/80 group-hover:text-white tracking-wide mb-2.5 transition-colors duration-300">{cred.title}</h4>
                    <p className="text-white/50 text-[14px] lg:text-[15px] leading-[1.75]">{cred.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.04] to-transparent" />
      </section>

      {/* ═══════════ CHAPTER 2: OUR CAPABILITIES ═══════════ */}
      <section className="relative py-20 lg:py-24 overflow-hidden" data-testid="capabilities-section">
        {/* Background */}
        <div className="absolute inset-0 bg-[#121318]" />
        <div className="absolute inset-0 blueprint-texture" />

        {/* Atmospheric lighting */}
        <div className="absolute top-0 left-0 w-full h-2/3 pointer-events-none" style={{
          background: 'radial-gradient(ellipse 50% 50% at 50% 0%, rgba(139,35,50,0.03) 0%, transparent 70%)'
        }} />
        <div className="absolute bottom-0 right-0 w-1/2 h-1/2 pointer-events-none" style={{
          background: 'radial-gradient(ellipse 60% 60% at 100% 100%, rgba(51,51,163,0.02) 0%, transparent 60%)'
        }} />

        {/* Engineering grid */}
        <div className="absolute top-0 left-[33%] w-px h-full bg-gradient-to-b from-transparent via-white/[0.02] to-transparent pointer-events-none" />
        <div className="absolute top-0 left-[66%] w-px h-full bg-gradient-to-b from-transparent via-white/[0.02] to-transparent pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <PremiumSectionHeader
            overline="What We Do"
            title="Our"
            titleAccent="Capabilities"
            description="Comprehensive engineering solutions delivered with precision, safety, and OEM-standard excellence across every sector we serve."
          />

          {/* Capability Cards Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
            {capabilities.map((cap, index) => (
              <motion.div
                key={cap.title}
                className="group relative overflow-hidden"
                style={{
                  background: 'linear-gradient(150deg, rgba(18,19,24,0.95) 0%, rgba(10,11,13,0.98) 100%)',
                  border: '1px solid rgba(255,255,255,0.05)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03), inset 0 0 0 1px rgba(255,255,255,0.02), 0 8px 32px rgba(0,0,0,0.3)',
                  transition: 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.6s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.8, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{
                  y: -8,
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), inset 0 0 0 1px rgba(139,35,50,0.06), 0 35px 70px rgba(0,0,0,0.45), 0 0 50px rgba(139,35,50,0.06)',
                  borderColor: 'rgba(139,35,50,0.22)',
                  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
                }}
                data-testid={`capability-card-${index}`}
              >
                {/* Blueprint texture */}
                <div className="absolute inset-0 blueprint-texture opacity-30" />

                {/* Top accent line */}
                <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden z-20">
                  <div className="h-full w-0 group-hover:w-full bg-gradient-to-r from-[#8B2332] via-[#d45567]/50 to-transparent transition-all duration-700 ease-out" />
                </div>

                {/* Left accent line */}
                <div className="absolute top-0 left-0 w-[2px] overflow-hidden z-20">
                  <div className="w-full h-0 group-hover:h-20 bg-gradient-to-b from-[#8B2332] to-transparent transition-all duration-700 ease-out" />
                </div>

                {/* ═══ Featured Image Area (cards with images only) ═══ */}
                {cap.image && (
                  <div className="relative w-full overflow-hidden" style={{ height: '220px' }}>
                    <img
                      src={cap.image}
                      alt={cap.imageAlt}
                      loading="lazy"
                      width="540" height="540"
                      className="absolute inset-0 w-full h-full transition-transform duration-700 ease-out group-hover:scale-[1.035]"
                      style={{ objectFit: 'cover', objectPosition: cap.imagePosition || 'center', filter: 'brightness(0.6) contrast(1.1) saturate(0.9)' }}
                    />
                    {/* Bottom gradient blend into content area */}
                    <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0c0d11] via-[#0c0d11]/70 to-transparent" />
                    {/* Subtle crimson atmospheric glow */}
                    <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-[#8B2332]/[0.06]" />
                    {/* Vignette */}
                    <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 80% at 50% 40%, transparent 40%, rgba(10,11,14,0.4) 100%)' }} />
                    {/* Number overlay on image */}
                    <span className="absolute top-4 right-5 font-['Barlow_Condensed'] text-5xl font-black text-white/[0.08] group-hover:text-white/[0.12] select-none transition-colors duration-700 z-10" style={{ lineHeight: 1 }}>
                      0{index + 1}
                    </span>
                  </div>
                )}

                {/* Card content */}
                <div className={`relative ${cap.image ? 'p-7 lg:p-8' : 'p-8 lg:p-9'} z-10`}>
                  {/* Icon + Index row */}
                  <div className="flex items-start justify-between mb-6">
                    <div className="relative w-14 h-14 flex items-center justify-center" style={cap.image ? { marginTop: '-2rem', zIndex: 20 } : {}}>
                      <div className="absolute inset-0 bg-[#8B2332]/[0.08] border border-[#8B2332]/20 group-hover:bg-[#8B2332]/[0.12] group-hover:border-[#8B2332]/35 transition-all duration-500" style={cap.image ? { background: 'rgba(12,13,17,0.85)', backdropFilter: 'blur(8px)' } : {}} />
                      <div className="absolute -inset-1.5 border border-[#8B2332]/[0.06] opacity-0 group-hover:opacity-100 transition-all duration-500" />
                      <div className="absolute -inset-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{ boxShadow: '0 0 25px rgba(139,35,50,0.15)' }} />
                      <cap.icon className="w-6 h-6 text-[#8B2332] group-hover:text-[#d45567] transition-colors duration-500 relative z-10" strokeWidth={1.5} />
                    </div>
                    {!cap.image && (
                      <span className="font-['Barlow_Condensed'] text-4xl font-black text-white/[0.04] group-hover:text-white/[0.07] select-none transition-colors duration-700" style={{ lineHeight: 1 }}>
                        0{index + 1}
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="font-['Barlow_Condensed'] text-xl lg:text-[1.35rem] font-bold uppercase text-white mb-3 group-hover:text-[#d45567] transition-colors duration-500" style={{ letterSpacing: '0.03em' }}>
                    {cap.title}
                  </h3>

                  {/* Accent divider */}
                  <div className="w-8 h-[1.5px] bg-[#8B2332]/30 group-hover:w-14 group-hover:bg-[#8B2332]/60 transition-all duration-500 mb-4" />

                  {/* Description */}
                  <p className="text-white/55 text-[15px] leading-[2.1] group-hover:text-white/75 transition-colors duration-500" style={{ letterSpacing: '0.01em' }}>
                    {cap.desc}
                  </p>
                </div>

                {/* Bottom ambient glow */}
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#8B2332]/[0.06] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                {/* Bottom engineering indicator */}
                <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/[0.03] to-transparent group-hover:via-[#8B2332]/20 transition-all duration-500" />

                {/* Bottom-right corner bracket */}
                <div className="absolute bottom-3 right-3 w-6 h-6 border-b border-r border-white/[0.05] group-hover:border-[#8B2332]/25 group-hover:w-10 group-hover:h-10 transition-all duration-500 pointer-events-none" />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Section transition to CTA */}
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/60 to-transparent pointer-events-none" />
      </section>

      {/* ══ BRANDS & SYSTEMS WE WORK WITH — Logo Showcase ══ */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-[#0A0B0D]" data-testid="brands-section">
        <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.004) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.004) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#8B2332]/15 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <PremiumSectionHeader overline="Trusted Industry Brands" title="Brands We" titleAccent="Work With" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5">
            {authorityBrands.map((brand, i) => (
                <motion.div key={brand.name} className="group relative overflow-hidden flex items-center justify-center" style={{ background: 'linear-gradient(155deg, rgba(16,17,22,0.98) 0%, rgba(12,13,16,0.99) 100%)', border: '1px solid rgba(107,122,141,0.07)', boxShadow: '0 6px 24px rgba(0,0,0,0.25)', height: '140px' }} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04, duration: 0.6, ease: [0.16, 1, 0.3, 1] }} whileHover={{ y: -3, boxShadow: '0 18px 45px rgba(0,0,0,0.35), 0 0 25px rgba(139,35,50,0.04)', borderColor: 'rgba(139,35,50,0.15)' }} data-testid={`brand-card-${i}`}>
                  {/* Subtle grid */}
                  <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(107,122,141,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(107,122,141,0.015) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                  {/* Top accent on hover */}
                  <div className="absolute top-0 left-0 right-0 h-[1.5px] overflow-hidden z-10"><div className="h-full w-0 group-hover:w-full bg-gradient-to-r from-[#8B2332] via-[#8B2332]/40 to-transparent transition-all duration-500 ease-out" /></div>
                  {/* Corner brackets */}
                  <div className="absolute top-2 right-2 w-3.5 h-3.5 border-t border-r border-white/[0.04] group-hover:border-[#8B2332]/20 transition-all duration-400 pointer-events-none z-10" />
                  <div className="absolute bottom-2 left-2 w-3.5 h-3.5 border-b border-l border-white/[0.04] group-hover:border-[#8B2332]/15 transition-all duration-400 pointer-events-none z-10" />
                  {/* Hover glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(139,35,50,0.04) 0%, transparent 60%)' }} />
                  {/* Logo */}
                  <div className="relative z-20 px-7 py-5 flex items-center justify-center w-full h-full">
                    <img
                      src={brand.logo}
                      alt={brand.name}
                      loading="lazy"
                      decoding="async"
                      className="max-w-[85%] max-h-[80px] object-contain opacity-75 group-hover:opacity-100 transition-all duration-400 group-hover:scale-[1.02]"
                    />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.03] to-transparent" />
                </motion.div>
            ))}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/[0.04] to-transparent" />
      </section>

      {/* ═══════════ CHAPTER 3: LOCATION + CTA FINALE ═══════════ */}
      <HomeLocation />
      <CTASection />
    </main>
  );
};

const ServicesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const serviceSections = [
    {
      title: "Mine Winder Services",
      slug: "mine-winder-services",
      icon: Cog,
      intro: "As specialists in mine winder maintenance, repair, and upgrading, we ensure every system is serviced to OEM-standard specifications — keeping your operations running safely underground.",
      services: [
        "Level 1 & Level 2 Audits",
        "Dynamic Brake Testing",
        "Locked Bell Audits",
        "NDT & EMT Testing",
        "Laser Alignment",
        "Complete Winder Refurbishments",
        "Emergency Repair Support",
        "Spare Parts Supply & Management"
      ],
      highlights: ["OEM Standard", "Emergency Support", "Specialist Testing"],
      image: IMAGES.mining,
      accent: 'rgba(139,35,50,0.05)',
    },
    {
      title: "Electrical Solutions",
      slug: "electrical-solutions",
      icon: Zap,
      intro: "From power distribution to advanced automation, our electrical engineering team delivers robust, safe, and efficient systems engineered for the most demanding industrial and mining environments.",
      services: [
        "Electrical Distribution Equipment",
        "Switchgear & Motor Control Centres",
        "Automation & PLC Systems",
        "Transformers & UPS Systems",
        "Generators & Standby Power",
        "Industrial Lighting Systems",
        "Cabling, Wiring & Terminations",
        "Preventive Electrical Maintenance"
      ],
      highlights: ["Experienced Technical Team", "End-to-End Solutions", "Preventive Programs"],
      image: IMAGES.electrical,
      accent: 'rgba(51,100,200,0.04)',
    },
    {
      title: "Mechanical Services",
      slug: "mechanical-services",
      icon: Wrench,
      intro: "Our mechanical division handles heavy-duty fabrication, precision machining, and complex repairs — delivering components and assemblies built to withstand the toughest operating conditions.",
      services: [
        "Heavy Engineering & Fabrication",
        "Precision CNC Machining",
        "Hydraulic System Repairs",
        "Component Fabrication & Assembly",
        "Shaft Alignment & Balancing",
        "Gearbox Overhaul & Repairs",
        "Pump Maintenance & Rebuilds",
        "Structural Steel Work"
      ],
      highlights: ["Precision Tolerances", "In-House Machining", "Responsive Support"],
      image: IMAGES.mechanical,
      accent: 'rgba(150,150,170,0.03)',
    },
    {
      title: "Engineering & Refurbishments",
      slug: "engineering-refurbishments",
      icon: Settings,
      intro: "We support key stages of the equipment lifecycle — from engineering assessment and refurbishment through to commissioning and performance optimisation for extended asset life.",
      services: [
        "Complete Equipment Refurbishments",
        "Modernization & Upgrade Projects",
        "Design & Process Engineering",
        "Project Management & Coordination",
        "Installation & Integration Services",
        "Commissioning & Handover",
        "Performance Optimization & Tuning",
        "Technical Consulting & Advisory"
      ],
      highlights: ["Lifecycle Support", "Project Managed", "OEM Standard"],
      image: IMAGES.hero,
      accent: 'rgba(139,35,50,0.04)',
    },
    {
      title: "Renewable Energy Solutions",
      slug: "renewable-energy",
      icon: Sun,
      intro: "Our renewable energy division delivers sustainable power solutions — from solar PV installations and hybrid systems to battery storage — helping industrial clients reduce costs and carbon footprint.",
      services: [
        "Solar PV Design & Installation",
        "Hybrid Power Systems",
        "Comprehensive Energy Audits",
        "Battery Storage Solutions",
        "Grid-Tie & Net Metering Systems",
        "Off-Grid & Remote Power Solutions",
        "Maintenance & Performance Monitoring",
        "Energy Efficiency Consulting"
      ],
      highlights: ["Cost Reduction", "Sustainable Solutions", "Performance Support"],
      image: IMAGES.renewable,
      accent: 'rgba(80,160,80,0.035)',
    }
  ];

  const majorClients = [
    { name: "Harmony Gold", image: "https://images.unsplash.com/photo-1530424257364-cd52a3e9bd40?w=800&q=80&auto=format", imageAlt: "Representative underground mining shaft environment", focal: "center 40%" },
    { name: "Sibanye-Stillwater", image: "https://images.unsplash.com/photo-1660748255246-80f24d317070?w=800&q=80&auto=format", imageAlt: "Representative underground mining tunnel infrastructure", focal: "center 50%" },
    { name: "Gold Fields", image: "https://images.unsplash.com/photo-1720036236632-fdb6211cf317?w=800&q=80&auto=format", imageAlt: "Representative industrial processing plant interior", focal: "center 40%" },
    { name: "AngloGold Ashanti", image: "https://images.unsplash.com/photo-1544531697-0f624508b0d4?w=800&q=80&auto=format", imageAlt: "Representative underground mining engineering environment", focal: "center 55%" },
    { name: "African Rainbow Minerals", image: "https://images.unsplash.com/photo-1759148414485-5f624fe9d1ea?w=800&q=80&auto=format", imageAlt: "Representative heavy industrial mechanical equipment", focal: "center 45%" },
    { name: "Pan African Resources", image: "https://images.unsplash.com/photo-1788119044376-935c05f1759b?w=800&q=80&auto=format", imageAlt: "Representative mining hoisting and industrial gear infrastructure", focal: "center 50%" }
  ];

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    }
  }, [location.hash]);

  return (
    <main className="pt-24 bg-[#0A0B0D]" data-testid="services-page">
      <SEO title="Our Services" description="Specialist mine winder, electrical, mechanical, refurbishment and renewable-energy engineering solutions by Welkom Winder Services since 1999." path="/services" />

      {/* ══ PREMIUM SERVICES HERO — Full-Bleed Cinematic ══ */}
      <section className="relative overflow-hidden bg-[#0A0B0D]" style={{ minHeight: 'clamp(380px, 60vh, 650px)' }} data-testid="services-hero">
        {/* Full-bleed background artwork — cropped to show only industrial equipment, hiding baked-in text/icons */}
        <motion.div className="absolute inset-0" initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}>
          <img
            src="https://customer-assets-gfyr7b9c.emergentagent.net/job_f370c49b-77c6-42a3-81d6-f8b8fd2e1fcc/artifacts/a4wlyvw2_WWS%20-%20Our%20Services%20Hero%20Picture.png"
            alt="Welkom Winder Services engineering equipment and industrial service capabilities"
            width="1920" height="960"
            fetchPriority="high"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'right 15%' }}
            data-testid="services-hero-artwork"
          />
        </motion.div>

        {/* Heavy gradient overlays — left side opaque for text, right shows equipment imagery */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0B0D] via-[#0A0B0D]/95 to-[#0A0B0D]/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/30 to-[#0A0B0D]/40" />
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-[#0A0B0D]" />

        {/* Technical grid */}
        <motion.div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.004) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.004) 1px, transparent 1px)', backgroundSize: '60px 60px' }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.8 }} />

        {/* Corner brackets */}
        <motion.div className="absolute top-4 left-4 w-6 h-6 border-t border-l border-[#8B2332]/25 z-20 pointer-events-none" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.5 }} />
        <motion.div className="absolute top-4 right-4 w-5 h-5 border-t border-r border-[#1a3a5c]/25 z-20 pointer-events-none" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.5 }} />
        <motion.div className="absolute bottom-16 left-4 w-5 h-5 border-b border-l border-[#1a3a5c]/20 z-20 pointer-events-none" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.5 }} />
        <motion.div className="absolute bottom-16 right-4 w-6 h-6 border-b border-r border-[#8B2332]/20 z-20 pointer-events-none" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.5 }} />

        {/* Hero content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 flex items-center" style={{ minHeight: 'clamp(380px, 60vh, 650px)', paddingTop: '6rem', paddingBottom: '3rem' }}>
          <div>
            {/* Eyebrow */}
            <motion.div className="flex items-center gap-3 mb-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.6 }}>
              <span className="h-[2px] w-12 bg-gradient-to-r from-[#8B2332] to-transparent" />
              <span className="text-[#8B2332] text-[13px] lg:text-[14px] font-bold uppercase tracking-[0.35em]">What We Offer</span>
            </motion.div>
            {/* Headline */}
            <motion.h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl lg:text-6xl font-black uppercase text-white leading-[0.9] mb-5" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B2332] to-[#c44b5e]">Services</span>
            </motion.h1>
            {/* Supporting copy */}
            <motion.p className="text-white/60 text-[15px] lg:text-[16px] leading-[1.85] max-w-xl mb-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55, duration: 0.6 }}>
              Specialist mine-winder, electrical, mechanical, refurbishment and renewable-energy solutions engineered for reliability, safety and long-term performance.
            </motion.p>
            {/* CTA always visible */}
            <a
              href="#services-content"
              onClick={(e) => { e.preventDefault(); document.getElementById('services-content')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
              className="inline-flex items-center gap-3 px-7 py-3.5 bg-[#8B2332] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#a02a3d] transition-all duration-300 hover:-translate-y-0.5"
              style={{ boxShadow: '0 4px 20px rgba(139,35,50,0.25)' }}
              data-testid="services-hero-cta"
            >
              Explore Our Services <ArrowRight size={14} />
            </a>
          </div>
        </div>

        {/* Bottom transition */}
        <div className="absolute bottom-0 left-0 right-0 z-20">
          <div className="h-px bg-gradient-to-r from-transparent via-[#8B2332]/20 to-transparent" />
        </div>
      </section>

      {/* ══ FIVE-MODULE SERVICE NAVIGATION ══ */}
      <section id="services-content" className="relative py-12 lg:py-14 overflow-hidden" data-testid="service-nav-strip">
        <div className="absolute inset-0 bg-[#0e0f12]" />
        <div className="absolute inset-0 blueprint-texture opacity-50" />
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#8B2332]/25 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5">
            {serviceSections.map((svc, i) => {
              const SvcIcon = svc.icon;
              return (
                <motion.a
                  key={svc.slug}
                  href={`#${svc.slug}`}
                  onClick={(e) => { e.preventDefault(); document.getElementById(svc.slug)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                  className="group/nav relative overflow-hidden cursor-pointer block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8B2332]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0e0f12]"
                  style={{ minHeight: '180px' }}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06, duration: 0.5 }}
                  whileHover={{ y: -3, transition: { duration: 0.32, ease: [0.16, 1, 0.3, 1] } }}
                  data-testid={`service-nav-${i}`}
                >
                  {/* Background image */}
                  <div className="absolute inset-0 z-0">
                    <img
                      src={svc.image}
                      alt=""
                      className="w-full h-full object-cover transition-all duration-[600ms] ease-out group-hover/nav:scale-[1.02]"
                      loading="lazy"
                      style={{ filter: 'brightness(0.45) saturate(0.6) contrast(1.1)' }}
                    />
                  </div>
                  {/* Directional gradient — heavier at bottom where text sits */}
                  <div className="absolute inset-0 z-[1]" style={{ background: 'linear-gradient(to bottom, rgba(10,11,13,0.3) 0%, rgba(10,11,13,0.55) 40%, rgba(10,11,13,0.82) 75%, rgba(10,11,13,0.92) 100%)' }} />
                  {/* Hover overlay — reveal image more */}
                  <div className="absolute inset-0 z-[1] opacity-0 group-hover/nav:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ background: 'linear-gradient(to bottom, rgba(10,11,13,0.15) 0%, rgba(10,11,13,0.35) 35%, rgba(10,11,13,0.72) 70%, rgba(10,11,13,0.88) 100%)' }} />
                  {/* Technical grid */}
                  <div className="absolute inset-0 z-[2] pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.008) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.008) 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                  {/* Border system */}
                  <div className="absolute inset-0 z-[3] border border-white/[0.06] group-hover/nav:border-[#8B2332]/25 transition-colors duration-500 pointer-events-none" />
                  {/* Top red accent line */}
                  <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden z-[4] pointer-events-none"><div className="h-full w-0 group-hover/nav:w-full bg-gradient-to-r from-[#8B2332] via-[#8B2332]/40 to-transparent transition-all duration-500" /></div>
                  {/* Corner brackets */}
                  <div className="absolute top-2 right-2 w-4 h-4 border-t border-r border-white/[0.05] group-hover/nav:border-[#8B2332]/30 group-hover/nav:w-6 group-hover/nav:h-6 transition-all duration-500 pointer-events-none z-[4]" />
                  <div className="absolute bottom-2 left-2 w-3 h-3 border-b border-l border-white/[0.03] group-hover/nav:border-[#8B2332]/20 group-hover/nav:w-5 group-hover/nav:h-5 transition-all duration-500 pointer-events-none z-[4]" />
                  {/* Red atmospheric glow on hover */}
                  <div className="absolute inset-0 z-[2] pointer-events-none opacity-0 group-hover/nav:opacity-100 transition-opacity duration-500" style={{ background: 'radial-gradient(ellipse 60% 60% at 50% 80%, rgba(139,35,50,0.08) 0%, transparent 70%)' }} />

                  {/* Content */}
                  <div className="relative z-[5] flex flex-col items-center justify-end h-full p-5 text-center">
                    {/* Icon */}
                    <div className="w-11 h-11 mb-3 flex items-center justify-center backdrop-blur-sm transition-all duration-500" style={{ background: 'rgba(139,35,50,0.1)', border: '1px solid rgba(139,35,50,0.2)' }}>
                      <SvcIcon size={18} className="text-[#8B2332] group-hover/nav:text-[#d45567] transition-colors duration-400" strokeWidth={1.5} />
                    </div>
                    {/* Title */}
                    <span className="font-['Barlow_Condensed'] text-[14px] lg:text-[15px] font-bold uppercase text-white/90 group-hover/nav:text-white tracking-[0.08em] transition-colors duration-300 block leading-tight mb-3" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>{svc.title}</span>
                    {/* Explore cue */}
                    <span className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.2em] text-white/30 group-hover/nav:text-[#d45567] transition-all duration-400">
                      Explore <ArrowRight size={10} className="group-hover/nav:translate-x-1 transition-transform duration-300" />
                    </span>
                  </div>
                </motion.a>
              );
            })}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/[0.05] to-transparent" />
      </section>

      {/* ══ SERVICE SECTIONS (ALTERNATING) ══ */}
      {serviceSections.map((section, index) => {
        const isReversed = index % 2 !== 0;
        return (
          <section
            key={section.slug}
            id={section.slug}
            className="relative py-20 lg:py-24 overflow-hidden scroll-mt-24"
            style={{ background: index % 2 === 0 ? '#0A0B0D' : '#0e0f12' }}
            data-testid={`service-section-${section.slug}`}
          >
            {/* Blueprint texture */}
            <div className="absolute inset-0 blueprint-texture" />

            {/* Section-specific atmospheric lighting */}
            <div className="absolute top-0 pointer-events-none" style={{
              [isReversed ? 'left' : 'right']: 0,
              width: '50%', height: '100%',
              background: `radial-gradient(ellipse at ${isReversed ? '0%' : '100%'} 50%, ${section.accent} 0%, transparent 70%)`
            }} />

            {/* Engineering grid lines */}
            <div className="absolute top-0 left-[25%] w-px h-full bg-gradient-to-b from-transparent via-white/[0.015] to-transparent pointer-events-none" />
            <div className="absolute top-0 left-[75%] w-px h-full bg-gradient-to-b from-transparent via-white/[0.015] to-transparent pointer-events-none" />

            {/* Oversized architectural numeral */}
            <div className="absolute pointer-events-none select-none" style={{
              top: '10%',
              [isReversed ? 'right' : 'left']: '5%',
              fontSize: 'clamp(120px, 15vw, 220px)',
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 900,
              color: 'rgba(255,255,255,0.025)',
              lineHeight: 1,
            }}>
              0{index + 1}
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
              <div className={`grid lg:grid-cols-2 gap-14 lg:gap-20 items-start ${isReversed ? '' : ''}`}>
                {/* TEXT COLUMN — Executive Editorial Panel */}
                <motion.div
                  className={`group/svc cursor-default ${isReversed ? 'lg:order-2' : 'lg:order-1'}`}
                  initial={{ opacity: 0, x: isReversed ? 40 : -40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                >
                  {/* Icon + Section number */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="relative w-14 h-14 flex items-center justify-center">
                      <div className="absolute inset-0 bg-[#8B2332]/[0.08] border border-[#8B2332]/20 group-hover/svc:bg-[#8B2332]/[0.12] group-hover/svc:border-[#8B2332]/35 transition-all duration-500" />
                      <section.icon className="w-6 h-6 text-[#8B2332] group-hover/svc:text-[#d45567] transition-colors duration-500 relative z-10" strokeWidth={1.5} />
                    </div>
                    <span className="font-['Barlow_Condensed'] text-white/[0.08] text-6xl font-black leading-none select-none">0{index + 1}</span>
                  </div>

                  {/* Title */}
                  <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl lg:text-5xl font-bold uppercase text-white mb-6 leading-[0.95] transition-all duration-[350ms] group-hover/svc:text-white" style={{ letterSpacing: '0.01em' }}>
                    {section.title}
                  </h2>

                  {/* Editorial Panel with intro */}
                  <div className="relative overflow-hidden mb-8 transition-transform duration-[300ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] group-hover/svc:-translate-y-[2px]">
                    <div className="absolute inset-0 transition-all duration-[300ms]" style={{
                      background: 'linear-gradient(160deg, rgba(18,19,24,0.88) 0%, rgba(12,13,17,0.92) 100%)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), inset 0 0 0 1px rgba(255,255,255,0.02), 0 16px 50px rgba(0,0,0,0.3)',
                    }} />
                    <div className="absolute inset-0 border border-transparent group-hover/svc:border-white/[0.03] transition-colors duration-[300ms] pointer-events-none" />
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.07] to-transparent group-hover/svc:via-white/[0.12] transition-all duration-[300ms]" />
                    <div className="absolute top-5 bottom-5 left-0 w-[2px] bg-gradient-to-b from-[#8B2332] via-[#8B2332]/30 to-transparent group-hover/svc:from-[#a63d50] group-hover/svc:via-[#8B2332]/50 transition-all duration-[350ms]" />
                    <div className="absolute top-0 left-0 w-8 h-full bg-gradient-to-r from-[#8B2332]/[0.03] to-transparent pointer-events-none" />
                    <div className="absolute top-2.5 right-2.5 w-6 h-6 border-t border-r border-white/[0.06] group-hover/svc:border-white/[0.12] group-hover/svc:w-8 group-hover/svc:h-8 transition-all duration-[350ms] pointer-events-none" />

                    <div className="relative z-10 p-7 lg:p-8">
                      <p className="text-white/68 text-[15px] lg:text-[16px] leading-[2.1]" style={{ letterSpacing: '0.015em' }}>
                        {section.intro}
                      </p>
                    </div>
                  </div>

                  {/* Premium badges */}
                  <div className="flex flex-wrap gap-3 mb-8">
                    {section.highlights.map((h) => (
                      <span key={h} className="group/badge inline-flex items-center gap-2 px-4 py-2.5 relative overflow-hidden" style={{
                        background: 'linear-gradient(135deg, rgba(139,35,50,0.08) 0%, rgba(139,35,50,0.03) 100%)',
                        border: '1px solid rgba(139,35,50,0.18)',
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.02)',
                        transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                      }}>
                        <Check size={11} strokeWidth={3} className="text-[#8B2332]" />
                        <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#8B2332]/80">{h}</span>
                        <div className="absolute inset-0 bg-[#8B2332]/5 opacity-0 group-hover/badge:opacity-100 transition-opacity duration-300" />
                      </span>
                    ))}
                  </div>

                  {/* Service list — interactive */}
                  <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mb-10">
                    {section.services.map((svc, si) => (
                      <motion.li
                        key={svc}
                        className="group/item flex items-start gap-3 text-white/55 text-[15px] py-1.5 border-b border-white/[0.03] hover:text-white/80 hover:border-[#8B2332]/15 transition-all duration-300 cursor-default"
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: si * 0.04, duration: 0.5 }}
                      >
                        <Check size={14} className="text-[#8B2332] mt-0.5 flex-shrink-0 group-hover/item:text-[#d45567] transition-colors duration-300" strokeWidth={2.5} />
                        <span style={{ letterSpacing: '0.01em' }}>{svc}</span>
                      </motion.li>
                    ))}
                  </ul>

                  {/* CTA Button with light sweep */}
                  <Link
                    to="/contact"
                    className="group inline-flex items-center gap-3 bg-gradient-to-b from-[#9a2a3b] to-[#7a1f2c] text-white font-bold px-8 py-4 text-sm overflow-hidden relative transition-all duration-500 hover:shadow-[0_20px_50px_rgba(139,35,50,0.4)] hover:-translate-y-1"
                    style={{ boxShadow: '0 6px 25px rgba(139,35,50,0.2), inset 0 1px 0 rgba(255,255,255,0.08)' }}
                    data-testid={`cta-${section.slug}`}
                  >
                    <span className="relative z-10 tracking-[0.15em] uppercase">Enquire Now</span>
                    <ArrowRight size={16} className="relative z-10 group-hover:translate-x-1.5 transition-transform duration-300" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#a8485a] via-[#8B2332] to-[#6e1c28] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[900ms] ease-out bg-gradient-to-r from-transparent via-white/15 to-transparent" />
                  </Link>
                </motion.div>

                {/* IMAGE COLUMN — Engineered Frame */}
                <motion.div
                  className={`relative ${isReversed ? 'lg:order-1' : 'lg:order-2'}`}
                  initial={{ opacity: 0, x: isReversed ? -60 : 60 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                >
                  <EngineeredImageFrame
                    src={section.image}
                    alt={`${section.title} - Welkom Winder Services`}
                    aspectRatio="4/3"
                  />
                </motion.div>
              </div>
            </div>

            {/* Section transition */}
            <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/[0.04] to-transparent" />
          </section>
        );
      })}


      {/* ══ CLIENT TRUST — Premium Cinematic Industrial Showcase ══ */}
      <section className="relative py-24 lg:py-28 overflow-hidden bg-[#0A0B0D]" data-testid="clients-section">
        {/* Network background */}
        <div className="absolute inset-0 blueprint-texture" />
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.008) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.008) 1px, transparent 1px)', backgroundSize: '80px 80px' }} />
        <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 1200 800">
          <line x1="100" y1="120" x2="350" y2="200" stroke="rgba(107,122,141,0.04)" strokeWidth="0.5" />
          <line x1="350" y1="200" x2="600" y2="150" stroke="rgba(107,122,141,0.04)" strokeWidth="0.5" />
          <line x1="600" y1="150" x2="850" y2="250" stroke="rgba(107,122,141,0.04)" strokeWidth="0.5" />
          <line x1="850" y1="250" x2="1100" y2="180" stroke="rgba(107,122,141,0.04)" strokeWidth="0.5" />
          <line x1="200" y1="500" x2="500" y2="580" stroke="rgba(107,122,141,0.03)" strokeWidth="0.5" />
          <line x1="500" y1="580" x2="750" y2="520" stroke="rgba(107,122,141,0.03)" strokeWidth="0.5" />
          <line x1="750" y1="520" x2="1000" y2="620" stroke="rgba(107,122,141,0.03)" strokeWidth="0.5" />
          <circle cx="100" cy="120" r="2.5" fill="rgba(139,35,50,0.12)" />
          <circle cx="350" cy="200" r="2" fill="rgba(26,58,92,0.10)" />
          <circle cx="600" cy="150" r="2.5" fill="rgba(139,35,50,0.10)" />
          <circle cx="850" cy="250" r="2" fill="rgba(26,58,92,0.12)" />
          <circle cx="1100" cy="180" r="2.5" fill="rgba(139,35,50,0.08)" />
          <circle cx="200" cy="500" r="2" fill="rgba(26,58,92,0.08)" />
          <circle cx="500" cy="580" r="2.5" fill="rgba(139,35,50,0.10)" />
          <circle cx="750" cy="520" r="2" fill="rgba(107,122,141,0.10)" />
          <circle cx="1000" cy="620" r="2.5" fill="rgba(26,58,92,0.10)" />
        </svg>
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(139,35,50,0.02) 0%, transparent 60%)' }} />
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#8B2332]/15 to-transparent" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <PremiumSectionHeader
            overline="Companies We Have Worked With"
            title="Our"
            titleAccent="Clients"
            description="We are proud to serve some of Africa's leading mining and industrial operations, delivering solutions they can rely on."
          />

          {/* Engineering divider */}
          <div className="flex items-center justify-center gap-4 -mt-10 mb-14 lg:-mt-12 lg:mb-16">
            <div className="h-px flex-1 max-w-[120px] bg-gradient-to-r from-transparent to-white/[0.06]" />
            <div className="w-1.5 h-1.5 rounded-full bg-[#8B2332]/30" />
            <div className="h-px w-20 bg-white/[0.06]" />
            <div className="w-1.5 h-1.5 rounded-full bg-[#1a3a5c]/30" />
            <div className="h-px w-20 bg-white/[0.06]" />
            <div className="w-1.5 h-1.5 rounded-full bg-[#8B2332]/30" />
            <div className="h-px flex-1 max-w-[120px] bg-gradient-to-l from-transparent to-white/[0.06]" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {majorClients.map((client, i) => (
              <motion.div
                key={client.name}
                className="group relative overflow-hidden"
                style={{
                  aspectRatio: '3 / 2',
                  background: 'linear-gradient(155deg, rgba(14,15,18,0.98) 0%, rgba(10,11,13,0.99) 100%)',
                  border: '1px solid rgba(107,122,141,0.08)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
                }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{
                  y: -4,
                  boxShadow: '0 30px 65px rgba(0,0,0,0.45), 0 0 45px rgba(139,35,50,0.06), 0 0 30px rgba(26,58,92,0.04)',
                  borderColor: 'rgba(139,35,50,0.20)',
                }}
                data-testid={`client-card-${i}`}
              >
                {/* Background image with dark cinematic treatment */}
                <div className="absolute inset-0 overflow-hidden">
                  <img
                    src={client.image}
                    alt=""
                    role="presentation"
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover scale-100 group-hover:scale-[1.04] transition-transform duration-[600ms] ease-out"
                    style={{ objectPosition: client.focal, filter: 'brightness(0.40) contrast(1.1) saturate(0.4)' }}
                  />
                  {/* Dark cinematic grade overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/55 to-[#0A0B0D]/25" />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#0A0B0D]/50 to-transparent" />
                  {/* Hover brightness reveal */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(139,35,50,0.06) 0%, transparent 60%)' }} />
                  {/* Subtle crimson edge glow on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: 'linear-gradient(90deg, rgba(139,35,50,0.08) 0%, transparent 30%)' }} />
                </div>

                {/* Engineering grid overlay */}
                <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(107,122,141,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(107,122,141,0.02) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

                {/* Technical circuit paths */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.15] group-hover:opacity-[0.25] transition-opacity duration-500" viewBox="0 0 300 200" preserveAspectRatio="none">
                  <path d={i % 2 === 0 ? "M0,30 H60 V70 H120 V40 H180" : "M300,40 H240 V80 H180 V50 H120"} stroke="rgba(26,58,92,0.5)" strokeWidth="0.3" fill="none" />
                  <path d={i % 3 === 0 ? "M280,160 H220 V130 H160" : "M20,170 H80 V140 H140 V160 H200"} stroke="rgba(139,35,50,0.4)" strokeWidth="0.3" fill="none" />
                  <circle cx={40 + i * 30} cy="35" r="1.5" fill="rgba(26,58,92,0.4)" />
                  <circle cx={260 - i * 20} cy="165" r="1.5" fill="rgba(139,35,50,0.3)" />
                </svg>

                {/* Ghost number */}
                <div className="absolute top-3 right-4 pointer-events-none select-none" aria-hidden="true">
                  <span className="font-['Barlow_Condensed'] text-[4.5rem] lg:text-[5.5rem] font-black leading-none text-white/[0.03] group-hover:text-white/[0.06] transition-all duration-500" style={{ letterSpacing: '-0.02em' }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>

                {/* Top accent trace */}
                <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden z-20">
                  <div className="h-full w-0 group-hover:w-full bg-gradient-to-r from-[#8B2332] via-[#8B2332]/50 to-transparent transition-all duration-700 ease-out" />
                </div>
                {/* Left accent trace */}
                <div className="absolute top-0 left-0 w-[1.5px] overflow-hidden z-20">
                  <div className="w-full h-0 group-hover:h-20 bg-gradient-to-b from-[#1a3a5c]/60 to-transparent transition-all duration-500 ease-out" />
                </div>

                {/* Corner brackets */}
                <div className="absolute top-2.5 right-2.5 w-5 h-5 border-t border-r border-white/[0.05] group-hover:border-[#8B2332]/30 group-hover:w-7 group-hover:h-7 transition-all duration-500 pointer-events-none z-20" />
                <div className="absolute top-2.5 left-2.5 w-4 h-4 border-t border-l border-white/[0.04] group-hover:border-[#1a3a5c]/25 group-hover:w-6 group-hover:h-6 transition-all duration-500 pointer-events-none z-20" />
                <div className="absolute bottom-2.5 left-2.5 w-5 h-5 border-b border-l border-white/[0.05] group-hover:border-[#8B2332]/25 group-hover:w-7 group-hover:h-7 transition-all duration-500 pointer-events-none z-20" />
                <div className="absolute bottom-2.5 right-2.5 w-4 h-4 border-b border-r border-white/[0.04] group-hover:border-[#1a3a5c]/20 group-hover:w-6 group-hover:h-6 transition-all duration-500 pointer-events-none z-20" />

                {/* Inner metallic border */}
                <div className="absolute inset-0 pointer-events-none z-20" style={{ border: '1px solid rgba(107,122,141,0.04)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)' }} />

                {/* Content overlay */}
                <div className="absolute bottom-0 left-0 right-0 z-30 p-5 lg:p-6">
                  {/* Classification label */}
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className="font-['Barlow_Condensed'] text-[10px] lg:text-[11px] uppercase tracking-[0.2em] text-white/30 group-hover:text-[#8B2332]/70 transition-colors duration-500">
                      WWS Client / {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  {/* Client name */}
                  <h3 className="font-['Barlow_Condensed'] text-xl lg:text-2xl font-bold uppercase text-white/75 group-hover:text-white/95 tracking-[0.06em] transition-all duration-400 leading-tight">
                    {client.name}
                  </h3>
                  {/* Crimson underline */}
                  <div className="mt-2.5 h-[1.5px] w-10 group-hover:w-16 bg-gradient-to-r from-[#8B2332] to-[#8B2332]/30 transition-all duration-500 ease-out" />
                </div>

                {/* Bottom metallic edge */}
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.04] to-transparent z-20" />

                {/* Diagonal light sweep on hover */}
                <div className="absolute inset-0 pointer-events-none z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{ background: 'linear-gradient(135deg, transparent 40%, rgba(255,255,255,0.015) 50%, transparent 60%)' }} />
              </motion.div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/[0.04] to-transparent" />
      </section>

      {/* ══ CTA FINALE — Location then CTA ══ */}
      <ExecutiveAchievementDashboard />
      <HomeLocation />
      <CTASection />
    </main>
  );
};

// Team Page
const TeamPage = () => {
  const teamMembers = [
    { name: "John Smith", position: "Managing Director", experience: "30+ years in mining industry" },
    { name: "Sarah Johnson", position: "Operations Manager", experience: "20+ years in industrial operations" },
    { name: "Michael Brown", position: "Chief Engineer", experience: "25+ years in electrical engineering" },
    { name: "David Wilson", position: "Technical Director", experience: "22+ years in mechanical engineering" },
    { name: "Emily Davis", position: "Project Manager", experience: "15+ years in project management" },
    { name: "Robert Taylor", position: "Safety Officer", experience: "18+ years in mining safety" }
  ];

  return (
    <main className="pt-24" data-testid="team-page">
      <SEO title="Our Team" description="Meet the experienced engineers and technicians at Welkom Winder Services. Skilled professionals serving Africa's mining and industrial sectors." path="/team" />
      <section className="py-24 lg:py-32 bg-[#0A0B0D]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            className="max-w-3xl mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="overline mb-4">The People</p>
            <h1 className="heading-xl text-5xl md:text-7xl text-white mb-8">Our Team</h1>
            <p className="body-text text-xl">
              Experienced professionals dedicated to delivering excellence in every project.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.name}
                className="team-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`team-member-${index}`}
              >
                <div className="team-card-image bg-[#1A1C23]">
                  <div className="w-full h-full flex items-center justify-content-center bg-gradient-to-br from-[#1A1C23] to-[#121318]">
                    <Users size={80} className="text-[#8B2332]/30 mx-auto my-auto" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="heading-md text-xl text-white mb-1">{member.name}</h3>
                  <p className="text-[#8B2332] text-sm font-medium uppercase tracking-wider mb-3">{member.position}</p>
                  <p className="body-text text-sm">{member.experience}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      <CTASection />
    </main>
  );
};

// Contact Page

// Enquiry type options matching WWS service architecture
const ENQUIRY_TYPES = [
  { value: '', label: 'Select enquiry type...' },
  { value: 'General Enquiry', label: 'General Enquiry' },
  { value: 'Mine Winder Services', label: 'Mine Winder Services' },
  { value: 'Electrical Solutions', label: 'Electrical Solutions' },
  { value: 'Mechanical Services', label: 'Mechanical Services' },
  { value: 'Engineering & Refurbishments', label: 'Engineering & Refurbishments' },
  { value: 'Product Enquiry', label: 'Product Enquiry' },
  { value: 'Renewable Energy', label: 'Renewable Energy' },
  { value: 'Mining Sector', label: 'Mining Sector' },
  { value: 'Other', label: 'Other' },
];

// Map URL type params to enquiry type values
const TYPE_MAP = {
  'general': 'General Enquiry',
  'mine-winder': 'Mine Winder Services',
  'electrical': 'Electrical Solutions',
  'mechanical': 'Mechanical Services',
  'engineering': 'Engineering & Refurbishments',
  'product': 'Product Enquiry',
  'renewable': 'Renewable Energy',
  'mining': 'Mining Sector',
  'other': 'Other',
};

// Premium Contact Method Card with cursor-tracking radial light
const ContactMethodCard = ({ method, index }) => {
  const cardRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHov, setIsHov] = useState(false);
  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, []);
  return (
    <motion.div
      ref={cardRef}
      className="group relative overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 + index * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -6, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHov(true)}
      onMouseLeave={() => setIsHov(false)}
      data-testid={`contact-method-${method.title.toLowerCase()}`}
    >
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(155deg, rgba(18,19,24,0.94) 0%, rgba(10,11,13,0.97) 100%)',
        border: isHov ? '1px solid rgba(139,35,50,0.22)' : '1px solid rgba(255,255,255,0.06)',
        boxShadow: isHov ? 'inset 0 1px 0 rgba(255,255,255,0.04), 0 25px 55px rgba(0,0,0,0.45), 0 0 35px rgba(139,35,50,0.05)' : 'inset 0 1px 0 rgba(255,255,255,0.03), 0 6px 25px rgba(0,0,0,0.3)',
        transition: 'border-color 0.5s ease, box-shadow 0.5s ease',
      }} />
      {isHov && <div className="absolute inset-0 pointer-events-none z-[1]" style={{ background: `radial-gradient(circle 160px at ${mousePos.x}px ${mousePos.y}px, rgba(139,35,50,0.08) 0%, transparent 70%)` }} />}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.04] to-transparent" />
      <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden pointer-events-none">
        <div style={{ height: '100%', width: isHov ? '100%' : '0%', background: 'linear-gradient(to right, #8B2332, rgba(212,85,103,0.4), transparent)', transition: 'width 0.7s cubic-bezier(0.16, 1, 0.3, 1)' }} />
      </div>
      <div className="absolute top-3 bottom-3 left-0 w-[2px] pointer-events-none" style={{ background: isHov ? 'linear-gradient(to bottom, rgba(139,35,50,0.7), rgba(139,35,50,0.3), transparent)' : 'linear-gradient(to bottom, rgba(139,35,50,0.3), transparent)', transition: 'background 0.5s ease' }} />
      <div className="absolute top-2 right-2 pointer-events-none z-10" style={{ width: isHov ? '24px' : '14px', height: isHov ? '24px' : '14px', borderTop: isHov ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(255,255,255,0.04)', borderRight: isHov ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(255,255,255,0.04)', transition: 'all 0.5s cubic-bezier(0.16,1,0.3,1)' }} />
      <div className="absolute bottom-2 left-2 pointer-events-none z-10" style={{ width: isHov ? '24px' : '14px', height: isHov ? '24px' : '14px', borderBottom: isHov ? '1px solid rgba(139,35,50,0.25)' : '1px solid rgba(255,255,255,0.03)', borderLeft: isHov ? '1px solid rgba(139,35,50,0.25)' : '1px solid rgba(255,255,255,0.03)', transition: 'all 0.5s cubic-bezier(0.16,1,0.3,1)' }} />
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(135deg, rgba(139,35,50,0.05) 0%, transparent 50%)', opacity: isHov ? 1 : 0, transition: 'opacity 0.5s ease' }} />
      <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1000ms] ease-out bg-gradient-to-r from-transparent via-white/[0.02] to-transparent pointer-events-none" />
      <a href={method.href} target={method.external ? '_blank' : undefined} rel={method.external ? 'noopener noreferrer' : undefined} className="relative z-10 flex items-center gap-5 p-6 lg:p-7 cursor-pointer" style={{ textDecoration: 'none' }}>
        <div className="relative w-14 h-14 flex items-center justify-center flex-shrink-0">
          <div className="absolute inset-0 border transition-all duration-500" style={{ background: isHov ? 'rgba(139,35,50,0.12)' : 'rgba(139,35,50,0.08)', borderColor: isHov ? 'rgba(139,35,50,0.35)' : 'rgba(139,35,50,0.2)' }} />
          <div className="absolute -inset-1 border transition-all duration-500" style={{ borderColor: isHov ? 'rgba(139,35,50,0.15)' : 'rgba(139,35,50,0.06)', opacity: isHov ? 1 : 0 }} />
          <div className="absolute -inset-1 transition-opacity duration-700" style={{ boxShadow: '0 0 22px rgba(139,35,50,0.15)', opacity: isHov ? 1 : 0 }} />
          <method.icon className="w-5 h-5 relative z-10 transition-all duration-500" style={{ color: isHov ? '#d45567' : '#8B2332', transform: isHov ? 'scale(1.1)' : 'scale(1)' }} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-['Barlow_Condensed'] text-sm font-bold uppercase tracking-[0.15em] mb-1.5 transition-colors duration-500" style={{ color: isHov ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.5)' }}>{method.title}</h3>
          <p className="text-[15px] transition-colors duration-500" style={{ color: isHov ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.7)', letterSpacing: '0.01em' }}>{method.value}</p>
        </div>
        <div className="flex-shrink-0 transition-all duration-500 hidden sm:block" style={{ opacity: isHov ? 1 : 0.4, transform: isHov ? 'translateX(0)' : 'translateX(-4px)' }}>
          <span className="text-[9px] font-bold uppercase tracking-[0.15em] whitespace-nowrap" style={{ color: isHov ? '#d45567' : 'rgba(255,255,255,0.3)' }}>{method.cta}</span>
          <ArrowRight size={12} className="ml-1 inline" style={{ color: isHov ? '#d45567' : 'rgba(255,255,255,0.3)' }} />
        </div>
      </a>
    </motion.div>
  );
};

const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const typeFromUrl = searchParams.get('type') || '';
  const subjectFromUrl = searchParams.get('subject') || '';
  const resolvedType = TYPE_MAP[typeFromUrl] || '';

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    company: '',
    enquiry_type: resolvedType,
    subject: subjectFromUrl ? decodeURIComponent(subjectFromUrl.replace(/\+/g, ' ')) : '',
    message: '',
    website_url: '', // honeypot
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'success' | 'error'
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.full_name.trim() || formData.full_name.trim().length < 2) errs.full_name = 'Please enter your full name.';
    if (!formData.email.trim()) errs.email = 'Email address is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Please enter a valid email address.';
    if (!formData.enquiry_type) errs.enquiry_type = 'Please select an enquiry type.';
    if (!formData.subject.trim() || formData.subject.trim().length < 2) errs.subject = 'Please enter a subject.';
    if (!formData.message.trim()) errs.message = 'Please enter your message.';
    else if (formData.message.trim().length < 10) errs.message = 'Please provide more detail (minimum 10 characters).';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const firstErr = Object.keys(errs)[0];
      const el = document.querySelector(`[name="${firstErr}"]`);
      if (el) el.focus();
      return;
    }
    setIsSubmitting(true);
    setErrors({});
    try {
      const payload = {
        full_name: formData.full_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        company: formData.company.trim() || null,
        enquiry_type: formData.enquiry_type || null,
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        website_url: formData.website_url, // honeypot
      };
      await axios.post(`${API}/contact`, payload);
      setSubmitStatus('success');
    } catch (error) {
      if (error.response?.status === 429) {
        setSubmitStatus('error');
      } else {
        setSubmitStatus('error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ full_name: '', email: '', phone: '', company: '', enquiry_type: '', subject: '', message: '', website_url: '' });
    setErrors({});
    setSubmitStatus(null);
  };

  const contactMethods = [
    { icon: MapPin, title: 'Address', value: '161 Power Rd, Voorspoed, Welkom, 9459', href: 'https://www.google.com/maps/search/?api=1&query=161+Power+Rd+Voorspoed+Welkom+9459', cta: 'Get Directions', external: true },
    { icon: Phone, title: 'Phone', value: '057 355 4929', href: 'tel:+27573554929', cta: 'Call WWS' },
    { icon: Mail, title: 'Email', value: 'sales@winderservices.com', href: 'mailto:sales@winderservices.com', cta: 'Send Email' },
  ];

  const trustItems = [
    { value: '1999', label: 'Established', sublabel: 'Year Founded', icon: Clock },
    { value: '200+', label: 'Projects Completed', sublabel: 'Engineering Track Record', icon: Target },
    { value: 'OEM', label: 'Standard Workmanship', sublabel: 'Specification-Led', icon: Shield },
    { value: 'Multi-Sector', label: 'Engineering Capability', sublabel: 'Mining · Industrial · Energy', icon: Globe },
  ];

  const inputBaseClass = "w-full bg-white/[0.02] border border-white/[0.08] text-white/90 text-[15px] px-5 py-4 outline-none placeholder:text-white/25 transition-all duration-500 focus:border-[#8B2332]/40 focus:bg-white/[0.035] hover:border-white/[0.12] hover:bg-white/[0.03]";

  const renderField = (name, label, type = 'text', placeholder = '', required = false, autoComplete = '') => (
    <div className="relative" key={name}>
      <label htmlFor={`contact-${name}`} className={`block text-xs font-bold uppercase tracking-[0.15em] mb-2.5 transition-colors duration-300 ${focusedField === name ? 'text-[#8B2332]' : errors[name] ? 'text-[#d45567]' : 'text-white/50'}`}>
        {label} {required && <span className="text-[#8B2332]/60">*</span>}
      </label>
      <div className="relative group/input">
        <input
          id={`contact-${name}`}
          type={type}
          name={name}
          value={formData[name]}
          onChange={handleChange}
          onFocus={() => setFocusedField(name)}
          onBlur={() => setFocusedField(null)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-required={required}
          aria-invalid={!!errors[name]}
          aria-describedby={errors[name] ? `${name}-error` : undefined}
          className={`${inputBaseClass} ${errors[name] ? 'border-[#d45567]/50 focus:border-[#d45567]/70' : ''}`}
          style={{
            boxShadow: focusedField === name ? '0 0 0 1px rgba(139,35,50,0.15), 0 8px 25px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.03)' : '0 2px 10px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.02)',
          }}
          data-testid={`contact-input-${name}`}
        />
        <div className="absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden">
          <div style={{ height: '100%', width: focusedField === name ? '100%' : '0%', background: errors[name] ? 'linear-gradient(to right, #d45567, rgba(212,85,103,0.4), transparent)' : 'linear-gradient(to right, #8B2332, rgba(212,85,103,0.4), transparent)', transition: 'width 0.5s cubic-bezier(0.16, 1, 0.3, 1)' }} />
        </div>
      </div>
      {errors[name] && <p id={`${name}-error`} role="alert" className="text-[#d45567] text-[12px] mt-2 flex items-center gap-1.5"><X size={11} />{errors[name]}</p>}
    </div>
  );

  return (
    <main className="pt-24 bg-[#0A0B0D]" data-testid="contact-page">
      <SEO title="Contact & Engineering Enquiries" description="Contact Welkom Winder Services for engineering quotes, technical enquiries and specialist support. 161 Power Rd, Welkom, 9459. 057 355 4929." path="/contact" />

      {/* ═══ SECTION 1: CONTACT HERO + FORM ═══ */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[#0A0B0D]" />
        <div className="absolute inset-0 blueprint-texture" />
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.006) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.006) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none" style={{ background: 'radial-gradient(ellipse 45% 40% at 25% 30%, rgba(139,35,50,0.05) 0%, transparent 60%)' }} />
        <div className="absolute bottom-0 right-0 w-full h-full pointer-events-none" style={{ background: 'radial-gradient(ellipse 40% 40% at 75% 70%, rgba(51,80,163,0.025) 0%, transparent 60%)' }} />
        <div className="absolute top-0 left-[25%] w-px h-full bg-gradient-to-b from-transparent via-white/[0.015] to-transparent pointer-events-none" />
        <div className="absolute top-0 left-[50%] w-px h-full bg-gradient-to-b from-transparent via-white/[0.01] to-transparent pointer-events-none" />
        <div className="absolute top-0 left-[75%] w-px h-full bg-gradient-to-b from-transparent via-white/[0.015] to-transparent pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20">
            {/* LEFT COLUMN — Contact Info */}
            <motion.div initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
              {/* Eyebrow */}
              <motion.div className="mb-7 group/eyebrow cursor-default" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
                <span className="inline-flex items-center gap-4">
                  <motion.span className="h-[2px] bg-gradient-to-r from-[#8B2332] to-transparent group-hover/eyebrow:to-[#8B2332]/50 transition-all duration-500" initial={{ width: 0 }} animate={{ width: 56 }} transition={{ duration: 0.7, delay: 0.3 }} />
                  <span className="text-[#8B2332] text-[11px] font-bold uppercase tracking-[0.4em] group-hover/eyebrow:text-[#d45567] transition-colors duration-500">Get in Touch</span>
                </span>
              </motion.div>

              {/* Heading */}
              <motion.h1
                className="font-['Barlow_Condensed'] text-5xl md:text-6xl lg:text-7xl font-bold uppercase text-white mb-8 leading-[0.92] group/heading cursor-default"
                style={{ letterSpacing: '-0.01em', textShadow: '0 4px 30px rgba(0,0,0,0.4)' }}
                initial={{ opacity: 0, y: 35 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              >
                Contact{' '}<span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B2332] to-[#c44b5e]" style={{ filter: 'drop-shadow(0 0 20px rgba(139,35,50,0.2))' }}>Us</span>
              </motion.h1>

              {/* Intro panel */}
              <motion.div
                className="relative overflow-hidden mb-10 group/intro cursor-default"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.7 }}
                whileHover={{ y: -3, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } }}
              >
                <div className="absolute inset-0 transition-all duration-500" style={{
                  background: 'linear-gradient(160deg, rgba(18,19,24,0.8) 0%, rgba(12,13,17,0.88) 100%)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 16px 50px rgba(0,0,0,0.25)',
                }} />
                <div className="absolute inset-0 border border-transparent group-hover/intro:border-[#8B2332]/15 transition-colors duration-500 pointer-events-none" />
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.07] to-transparent" />
                <div className="absolute top-4 bottom-4 left-0 w-[2px] bg-gradient-to-b from-[#8B2332]/60 via-[#8B2332]/20 to-transparent group-hover/intro:from-[#8B2332]/90 group-hover/intro:via-[#8B2332]/40 transition-all duration-500" />
                <div className="relative z-10 p-7 lg:p-8">
                  <p className="text-white/65 text-[15px] lg:text-base leading-[2.1] group-hover/intro:text-white/78 transition-colors duration-500" style={{ letterSpacing: '0.012em' }}>
                    Tell us about your project, equipment, product requirement or technical challenge and our engineering team will direct your enquiry to the appropriate specialist. We typically respond within <span className="text-white/90 font-semibold">24–48 hours</span>.
                  </p>
                </div>
              </motion.div>

              {/* Contact Method Cards — Fully Actionable */}
              <div className="space-y-4 mb-10">
                {contactMethods.map((method, i) => (
                  <ContactMethodCard key={method.title} method={method} index={i} />
                ))}
              </div>

              {/* Response & Support Panel */}
              <motion.div
                className="relative overflow-hidden group/support"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.7 }}
              >
                <div className="absolute inset-0" style={{
                  background: 'linear-gradient(155deg, rgba(18,19,24,0.9) 0%, rgba(10,11,13,0.94) 100%)',
                  border: '1px solid rgba(255,255,255,0.05)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03), 0 10px 35px rgba(0,0,0,0.3)',
                }} />
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#8B2332]/50 via-[#8B2332]/15 to-transparent" />
                <div className="relative z-10 p-6 lg:p-7">
                  <h3 className="font-['Barlow_Condensed'] text-sm font-bold uppercase tracking-[0.2em] text-white/60 mb-5">Response & Support</h3>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8B2332]/70 mb-1">General Enquiries</p>
                      <p className="text-white/60 text-[13px]">Response within 24–48 hours</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8B2332]/70 mb-1">Office Hours</p>
                      <p className="text-white/60 text-[13px]">Mon–Fri: 07:00–17:00</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8B2332]/70 mb-1">Technical Assistance</p>
                      <p className="text-white/60 text-[13px]">Call directly for urgent matters</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8B2332]/70 mb-1">Headquarters</p>
                      <p className="text-white/60 text-[13px]">Welkom, Free State, SA</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* RIGHT COLUMN — Premium Enquiry Form */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="relative overflow-hidden" data-testid="form-panel">
                <div className="absolute inset-0" style={{
                  background: 'linear-gradient(160deg, rgba(18,19,24,0.92) 0%, rgba(12,13,17,0.96) 100%)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05), inset 0 0 0 1px rgba(255,255,255,0.02), 0 25px 70px rgba(0,0,0,0.4)',
                }} />
                <div className="absolute inset-0 blueprint-texture opacity-30" />
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />
                <div className="absolute top-3 right-3 w-8 h-8 border-t border-r border-white/[0.06] pointer-events-none" />
                <div className="absolute bottom-3 left-3 w-6 h-6 border-b border-l border-[#8B2332]/15 pointer-events-none" />

                <div className="relative z-10 p-8 lg:p-10">
                  {/* Form Header */}
                  <div className="mb-8">
                    <span className="inline-flex items-center gap-3 mb-4">
                      <div className="w-1.5 h-1.5 border border-[#8B2332]/40 rotate-45" />
                      <span className="text-[#8B2332] text-[10px] font-bold uppercase tracking-[0.35em]">Send Enquiry</span>
                    </span>
                    <h2 className="font-['Barlow_Condensed'] text-2xl lg:text-3xl font-bold uppercase text-white mb-2" style={{ letterSpacing: '0.02em' }}>Tell Us About Your Requirement</h2>
                    <p className="text-white/45 text-[13px] leading-[1.8]">Describe your project, equipment or technical challenge and our team will respond with the right expertise.</p>
                  </div>

                  {submitStatus === 'success' ? (
                    /* ═══ SUCCESS STATE ═══ */
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5 }}
                      className="text-center py-12"
                      data-testid="contact-success"
                    >
                      <div className="w-16 h-16 mx-auto mb-6 border-2 border-[#8B2332]/40 flex items-center justify-center">
                        <Check size={28} className="text-[#8B2332]" />
                      </div>
                      <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white mb-3">Enquiry Received</h3>
                      <p className="text-white/55 text-[14px] leading-[1.9] max-w-sm mx-auto mb-8">
                        Thank you for contacting Welkom Winder Services. Your enquiry has been received and a member of the appropriate team will respond using the contact details provided.
                      </p>
                      <button
                        onClick={resetForm}
                        className="group inline-flex items-center gap-3 bg-white/[0.04] border border-white/[0.1] text-white font-bold px-8 py-4 transition-all duration-500 hover:border-[#8B2332]/30 hover:bg-white/[0.06] hover:-translate-y-1"
                        data-testid="send-another-btn"
                      >
                        <span className="tracking-[0.15em] uppercase text-[12px]">Send Another Enquiry</span>
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                      </button>
                    </motion.div>
                  ) : submitStatus === 'error' ? (
                    /* ═══ ERROR STATE ═══ */
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center py-12"
                      data-testid="contact-error"
                    >
                      <div className="w-16 h-16 mx-auto mb-6 border-2 border-[#d45567]/40 flex items-center justify-center">
                        <X size={28} className="text-[#d45567]" />
                      </div>
                      <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white mb-3">We Couldn't Send Your Enquiry</h3>
                      <p className="text-white/55 text-[14px] leading-[1.9] max-w-sm mx-auto mb-4">
                        Please try again, or contact WWS directly by phone or email.
                      </p>
                      <div className="flex justify-center gap-3 mb-6">
                        <a href="tel:+27573554929" className="inline-flex items-center gap-2 px-4 py-2 bg-[#8B2332]/10 border border-[#8B2332]/25 text-[#d45567] text-[11px] font-bold uppercase tracking-wider hover:bg-[#8B2332]/15 transition-all duration-300"><Phone size={12} />Call</a>
                        <a href="mailto:sales@winderservices.com" className="inline-flex items-center gap-2 px-4 py-2 bg-white/[0.03] border border-white/[0.08] text-white/60 text-[11px] font-bold uppercase tracking-wider hover:text-white/80 transition-all duration-300"><Mail size={12} />Email</a>
                      </div>
                      <button
                        onClick={() => setSubmitStatus(null)}
                        className="text-white/40 text-[12px] uppercase tracking-wider hover:text-white/60 transition-colors duration-300"
                        data-testid="try-again-btn"
                      >
                        Try Again
                      </button>
                    </motion.div>
                  ) : (
                    /* ═══ FORM ═══ */
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form" noValidate>
                      {/* Honeypot — hidden from users, visible to bots */}
                      <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                        <input type="text" name="website_url" value={formData.website_url} onChange={handleChange} tabIndex={-1} autoComplete="off" />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {renderField('full_name', 'Full Name', 'text', 'Your full name', true, 'name')}
                        {renderField('email', 'Email Address', 'email', 'your@email.com', true, 'email')}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {renderField('phone', 'Phone Number', 'tel', '+27 00 000 0000', false, 'tel')}
                        {renderField('company', 'Company Name', 'text', 'Your company name', false, 'organization')}
                      </div>

                      {/* Enquiry Type Select */}
                      <div className="relative">
                        <label htmlFor="contact-enquiry_type" className={`block text-xs font-bold uppercase tracking-[0.15em] mb-2.5 transition-colors duration-300 ${focusedField === 'enquiry_type' ? 'text-[#8B2332]' : errors.enquiry_type ? 'text-[#d45567]' : 'text-white/50'}`}>
                          Enquiry Type <span className="text-[#8B2332]/60">*</span>
                        </label>
                        <div className="relative group/input">
                          <select
                            id="contact-enquiry_type"
                            name="enquiry_type"
                            value={formData.enquiry_type}
                            onChange={handleChange}
                            onFocus={() => setFocusedField('enquiry_type')}
                            onBlur={() => setFocusedField(null)}
                            aria-required="true"
                            aria-invalid={!!errors.enquiry_type}
                            className={`${inputBaseClass} appearance-none cursor-pointer ${errors.enquiry_type ? 'border-[#d45567]/50' : ''} ${!formData.enquiry_type ? 'text-white/25' : ''}`}
                            style={{
                              boxShadow: focusedField === 'enquiry_type' ? '0 0 0 1px rgba(139,35,50,0.15), 0 8px 25px rgba(0,0,0,0.2)' : '0 2px 10px rgba(0,0,0,0.15)',
                            }}
                            data-testid="contact-input-enquiry_type"
                          >
                            {ENQUIRY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                          <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
                          <div className="absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden">
                            <div style={{ height: '100%', width: focusedField === 'enquiry_type' ? '100%' : '0%', background: 'linear-gradient(to right, #8B2332, rgba(212,85,103,0.4), transparent)', transition: 'width 0.5s cubic-bezier(0.16, 1, 0.3, 1)' }} />
                          </div>
                        </div>
                        {errors.enquiry_type && <p role="alert" className="text-[#d45567] text-[12px] mt-2 flex items-center gap-1.5"><X size={11} />{errors.enquiry_type}</p>}
                      </div>

                      {renderField('subject', 'Subject', 'text', 'What is this regarding?', true)}

                      {/* Message textarea */}
                      <div className="relative">
                        <label htmlFor="contact-message" className={`block text-xs font-bold uppercase tracking-[0.15em] mb-2.5 transition-colors duration-300 ${focusedField === 'message' ? 'text-[#8B2332]' : errors.message ? 'text-[#d45567]' : 'text-white/50'}`}>
                          Message <span className="text-[#8B2332]/60">*</span>
                        </label>
                        <div className="relative">
                          <textarea
                            id="contact-message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            onFocus={() => setFocusedField('message')}
                            onBlur={() => setFocusedField(null)}
                            rows={5}
                            placeholder="Describe your project, equipment or technical requirement..."
                            aria-required="true"
                            aria-invalid={!!errors.message}
                            className={`${inputBaseClass} resize-none ${errors.message ? 'border-[#d45567]/50' : ''}`}
                            style={{
                              boxShadow: focusedField === 'message' ? '0 0 0 1px rgba(139,35,50,0.15), 0 8px 25px rgba(0,0,0,0.2)' : '0 2px 10px rgba(0,0,0,0.15)',
                            }}
                            data-testid="contact-input-message"
                          />
                          <div className="absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden">
                            <div style={{ height: '100%', width: focusedField === 'message' ? '100%' : '0%', background: 'linear-gradient(to right, #8B2332, rgba(212,85,103,0.4), transparent)', transition: 'width 0.5s cubic-bezier(0.16, 1, 0.3, 1)' }} />
                          </div>
                        </div>
                        {errors.message && <p role="alert" className="text-[#d45567] text-[12px] mt-2 flex items-center gap-1.5"><X size={11} />{errors.message}</p>}
                      </div>

                      {/* POPIA Notice */}
                      <p className="text-white/30 text-[11px] leading-[1.8]">
                        By submitting this form, you consent to Welkom Winder Services (Pty) Ltd using the information provided to respond to your enquiry. <span className="text-white/40 hover:text-white/55 cursor-pointer transition-colors duration-300">Privacy Policy</span>
                      </p>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="group relative w-full inline-flex items-center justify-center gap-3 bg-gradient-to-b from-[#9a2a3b] to-[#7a1f2c] text-white font-bold py-5 overflow-hidden transition-all duration-500 hover:-translate-y-1 disabled:opacity-50 disabled:hover:translate-y-0 disabled:cursor-not-allowed"
                        style={{ boxShadow: '0 8px 30px rgba(139,35,50,0.3), inset 0 1px 0 rgba(255,255,255,0.08)' }}
                        data-testid="contact-submit-button"
                      >
                        <span className="relative z-10 tracking-[0.2em] uppercase text-sm">{isSubmitting ? 'Sending...' : 'Send Enquiry'}</span>
                        {!isSubmitting && <ArrowRight size={16} className="relative z-10 group-hover:translate-x-1.5 transition-transform duration-300" />}
                        {isSubmitting && <div className="relative z-10 w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                        <div className="absolute inset-0 bg-gradient-to-r from-[#a8485a] via-[#8B2332] to-[#6e1c28] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[900ms] ease-out bg-gradient-to-r from-transparent via-white/15 to-transparent" />
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 2: COMPACT TRUST STRIP ═══ */}
      <section className="relative py-14 lg:py-16 overflow-hidden bg-[#08090B]" data-testid="contact-trust-strip">
        <div className="absolute inset-0 blueprint-texture opacity-40" />
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.006) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.006) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.04] to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 lg:gap-6">
            {trustItems.map((item, idx) => (
              <motion.div
                key={item.label}
                className="group relative overflow-hidden cursor-default text-center py-6 px-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -2, transition: { duration: 0.3 } }}
              >
                <div className="absolute inset-0 transition-all duration-500" style={{ background: 'linear-gradient(155deg, rgba(18,19,24,0.7) 0%, rgba(10,11,13,0.8) 100%)', border: '1px solid rgba(255,255,255,0.04)' }} />
                <div className="absolute inset-0 border border-transparent group-hover:border-[#8B2332]/15 transition-colors duration-500 pointer-events-none" />
                <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden pointer-events-none">
                  <div className="h-full w-0 group-hover:w-full bg-gradient-to-r from-[#8B2332] to-transparent transition-all duration-700" />
                </div>
                <div className="relative z-10">
                  <item.icon size={18} className="mx-auto mb-3 text-[#8B2332]/60 group-hover:text-[#d45567] transition-colors duration-500" strokeWidth={1.5} />
                  <motion.p
                    className="font-['Barlow_Condensed'] text-2xl lg:text-3xl font-bold text-white group-hover:text-white transition-colors duration-500"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + idx * 0.1, duration: 0.6 }}
                  >
                    {item.value}
                  </motion.p>
                  <p className="text-white/50 text-[11px] font-bold uppercase tracking-[0.15em] mt-1 group-hover:text-white/65 transition-colors duration-500">{item.label}</p>
                  <p className="text-white/25 text-[10px] uppercase tracking-[0.1em] mt-0.5">{item.sublabel}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 3: WELKOM HEADQUARTERS ═══ */}
      <section className="relative py-16 lg:py-20 overflow-hidden bg-[#0A0B0D]" data-testid="contact-headquarters">
        <div className="absolute inset-0 blueprint-texture opacity-20" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          {/* Section label */}
          <motion.div
            className="flex items-center gap-4 mb-10"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <MapPin size={14} className="text-[#8B2332]" />
            <span className="text-[#8B2332] text-[11px] font-bold uppercase tracking-[0.35em]">Welkom Headquarters</span>
            <div className="flex-1 h-px bg-gradient-to-r from-[#8B2332]/20 to-transparent" />
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Map */}
            <motion.div
              className="lg:col-span-2 group/map relative overflow-hidden"
              style={{ height: 'clamp(280px, 40vh, 420px)', boxShadow: '0 15px 50px rgba(0,0,0,0.5)' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              data-testid="contact-map"
            >
              <div className="absolute -inset-1 z-0" style={{ background: 'linear-gradient(145deg, rgba(18,19,24,0.8) 0%, rgba(12,13,17,0.9) 100%)', border: '1px solid rgba(255,255,255,0.05)' }} />
              <div className="absolute top-2 right-2 w-8 h-8 border-t border-r border-white/[0.06] group-hover/map:border-white/[0.14] group-hover/map:w-12 group-hover/map:h-12 transition-all duration-500 pointer-events-none z-20" />
              <div className="absolute bottom-2 left-2 w-6 h-6 border-b border-l border-white/[0.04] group-hover/map:border-[#8B2332]/20 group-hover/map:w-10 group-hover/map:h-10 transition-all duration-500 pointer-events-none z-20" />
              <div className="absolute inset-0 border border-white/[0.05] group-hover/map:border-white/[0.1] transition-colors duration-500 z-10 pointer-events-none" />
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#8B2332]/25 to-transparent z-10 pointer-events-none" />
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3530.5!2d26.735!3d-27.985!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1e9347c0a3ed00bb%3A0x6e8e5c8b68c90b0!2s161+Power+Rd%2C+Voorspoed%2C+Welkom%2C+9459%2C+South+Africa!5e0!3m2!1sen!2sza!4v1700000000000!5m2!1sen!2sza"
                className="w-full h-full border-0 relative z-[1]"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Welkom Winder Services Headquarters Location"
                style={{ filter: 'invert(0.92) hue-rotate(180deg) saturate(0.3) contrast(1.1) brightness(0.65)' }}
              />
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#0A0B0D] to-transparent pointer-events-none z-10" />
              <div className="absolute top-0 left-0 bottom-0 w-16 bg-gradient-to-r from-[#0A0B0D] to-transparent pointer-events-none z-10" />
              <div className="absolute top-0 right-0 bottom-0 w-16 bg-gradient-to-l from-[#0A0B0D] to-transparent pointer-events-none z-10" />
            </motion.div>

            {/* Office Info */}
            <motion.div
              className="relative overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.7 }}
            >
              <div className="absolute inset-0" style={{
                background: 'linear-gradient(160deg, rgba(18,19,24,0.85) 0%, rgba(10,11,13,0.92) 100%)',
                border: '1px solid rgba(255,255,255,0.05)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03), 0 10px 35px rgba(0,0,0,0.3)',
              }} />
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#8B2332]/50 via-[#8B2332]/15 to-transparent" />
              <div className="relative z-10 p-7 lg:p-8 space-y-6" data-testid="hq-info">
                <div>
                  <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-white tracking-wide mb-1">Welkom Winder Services</h3>
                  <p className="text-white/35 text-[11px] uppercase tracking-[0.15em]">(Pty) Ltd</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8B2332]/70 mb-1.5">Address</p>
                  <p className="text-white/65 text-[14px] leading-[1.8]">161 Power Rd, Voorspoed,<br />Welkom, 9459</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8B2332]/70 mb-1.5">Office Hours</p>
                  <p className="text-white/65 text-[14px]">Mon–Fri: 07:00–17:00</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8B2332]/70 mb-1.5">Phone</p>
                  <a href="tel:+27573554929" className="text-white/65 text-[14px] hover:text-white/90 transition-colors duration-300">057 355 4929</a>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8B2332]/70 mb-1.5">Email</p>
                  <a href="mailto:sales@winderservices.com" className="text-white/65 text-[14px] hover:text-white/90 transition-colors duration-300">sales@winderservices.com</a>
                </div>

                <div className="flex gap-3 pt-2">
                  <a href="https://www.google.com/maps/search/?api=1&query=161+Power+Rd+Voorspoed+Welkom+9459" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-[#8B2332]/10 border border-[#8B2332]/20 text-[#8B2332] text-[10px] font-bold uppercase tracking-wider hover:bg-[#8B2332]/15 hover:border-[#8B2332]/30 transition-all duration-300" data-testid="hq-directions"><MapPin size={11} />Get Directions</a>
                  <a href="tel:+27573554929" className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-white/[0.03] border border-white/[0.08] text-white/50 text-[10px] font-bold uppercase tracking-wider hover:text-white/80 hover:border-white/[0.15] transition-all duration-300" data-testid="hq-call"><Phone size={11} />Call WWS</a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

// Premium Navigation Controls Component (Back & Home buttons)
const NavigationControls = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Don't show on any page - redundant with main navigation
  return null;
  
  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };
  
  const handleHome = () => {
    navigate('/');
  };
  
  return (
    <motion.div
      className="fixed top-28 left-6 lg:left-8 z-40 flex flex-col sm:flex-row gap-2"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
      data-testid="navigation-controls"
    >
      {/* Back Button */}
      <motion.button
        onClick={handleBack}
        className="group flex items-center gap-2 px-4 py-2.5 bg-white/5 backdrop-blur-xl border border-white/10 rounded-lg text-white/80 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all duration-300 shadow-lg shadow-black/20"
        whileHover={{ y: -2, boxShadow: "0 8px 30px rgba(0,0,0,0.3)" }}
        whileTap={{ scale: 0.97 }}
        data-testid="back-button"
      >
        <motion.span
          className="flex items-center justify-center"
          initial={{ x: 0 }}
          whileHover={{ x: -3 }}
          transition={{ duration: 0.2 }}
        >
          <ArrowLeft size={16} strokeWidth={2} />
        </motion.span>
        <span className="text-sm font-medium tracking-wide hidden sm:inline">Back</span>
      </motion.button>
      
      {/* Home Button */}
      <motion.button
        onClick={handleHome}
        className="group flex items-center gap-2 px-4 py-2.5 bg-white/5 backdrop-blur-xl border border-white/10 rounded-lg text-white/80 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all duration-300 shadow-lg shadow-black/20"
        whileHover={{ y: -2, boxShadow: "0 8px 30px rgba(0,0,0,0.3)" }}
        whileTap={{ scale: 0.97 }}
        data-testid="home-button"
      >
        <motion.span
          className="flex items-center justify-center"
          whileHover={{ scale: 1.1 }}
          transition={{ duration: 0.2 }}
        >
          <Home size={16} strokeWidth={2} />
        </motion.span>
        <span className="text-sm font-medium tracking-wide hidden sm:inline">Home</span>
      </motion.button>
    </motion.div>
  );
};

// Layout wrapper with smooth scroll to top and page transitions
const Layout = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  return (
    <>
      <Navigation />
      <NavigationControls />
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
      >
        {children}
      </motion.div>
      <Footer />
    </>
  );
};

// Main App
function App() {
  return (
    <HelmetProvider>
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#121318',
            color: '#F9FAFB',
            border: '1px solid rgba(255,255,255,0.1)'
          }
        }}
      />
      
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/:slug" element={<ProductDepartmentPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/projects/:industrySlug/:projectSlug" element={<ProjectPage />} />
            <Route path="/projects" element={<Navigate to="/industries/mining#projects" replace />} />
            <Route path="/projects/*" element={<Navigate to="/industries/mining#projects" replace />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/industries/:slug" element={<IndustryTemplate />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
